import React, { useState, useEffect, useCallback, useRef } from 'react'
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
    AreaChart,
    Area,
    PieChart,
    Pie,
    Cell,
    BarChart,
    Bar,
    ReferenceLine,
    ComposedChart,
} from 'recharts'
import { useAuth, LoginScreen } from '@bstock/auth/client'
import ReleaseTracking from './pulse/ReleaseTracking.jsx'
import TicketScrubbing from './pulse/TicketScrubbing.jsx'
import EpicClassification from './pulse/EpicClassification.jsx'
import SprintSummary from './pulse/SprintSummary.jsx'
import AiEfficiency from './pulse/AiEfficiency.jsx'
import QualityHub from './pulse/QualityHub.jsx'
import AgentInsights from './pulse/AgentInsights.jsx'
import TeamAdmin from './pulse/TeamAdmin.jsx'
import Frameworks from './pulse/Frameworks.jsx'
import { shouldForceFullSync } from './cacheFreshness.js'

// Color palette
const COLORS = {
    primary: '#3b82f6',
    secondary: '#8b5cf6',
    success: '#10b981',
    warning: '#f59e0b',
    danger: '#ef4444',
    ai: '#06b6d4',
    nonAi: '#64748b',
    background: '#0f172a',
    cardBg: '#1e293b',
    text: '#f1f5f9',
    textMuted: '#94a3b8',
    border: '#334155',
    leadTime: '#f472b6',
    cycleTime: '#a78bfa',
    throughput: '#34d399',
}

const CHART_COLORS = ['#3b82f6', '#8b5cf6', '#06b6d4', '#10b981', '#f59e0b']

// Extended palette for developer progress lines (12 distinct colors)
const DEV_COLORS = [
    '#3b82f6',
    '#8b5cf6',
    '#06b6d4',
    '#10b981',
    '#f59e0b',
    '#ef4444',
    '#ec4899',
    '#f97316',
    '#14b8a6',
    '#a78bfa',
    '#84cc16',
    '#e879f9',
]

const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID || ''
const JIRA_BASE_URL = 'https://bstock.atlassian.net'

// Format ISO date to short readable form (e.g. "Feb 11")
const fmtDate = (iso) => {
    if (!iso) return ''
    const d = new Date(iso)
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

// Format sprint date range (e.g. "Feb 11 – Feb 25")
const fmtDateRange = (start, end) => {
    if (!start && !end) return ''
    return `${fmtDate(start)} – ${fmtDate(end)}`
}

// Gamification Configuration
const GAMIFICATION_CONFIG = {
    velocityBadges: [
        {
            id: 'velocity_starter',
            name: 'Velocity Starter',
            emoji: '🌱',
            threshold: 20,
            description: 'Delivered 20+ story points in a single sprint',
        },
        {
            id: 'velocity_contributor',
            name: 'Solid Contributor',
            emoji: '⚡',
            threshold: 30,
            description: 'Delivered 30+ story points in a single sprint',
        },
        {
            id: 'velocity_powerhouse',
            name: 'Powerhouse',
            emoji: '💪',
            threshold: 40,
            description: 'Delivered 40+ story points in a single sprint',
        },
        {
            id: 'velocity_superstar',
            name: 'Superstar',
            emoji: '⭐',
            threshold: 50,
            description: 'Delivered 50+ story points in a single sprint',
        },
        {
            id: 'velocity_legend',
            name: 'Legend',
            emoji: '👑',
            threshold: 60,
            description: 'Delivered 60+ story points in a single sprint — top tier output',
        },
    ],
    aiAdoptionBadges: [
        {
            id: 'ai_curious',
            name: 'AI Curious',
            emoji: '🔍',
            threshold: 25,
            description: 'At least 25% of completed work was AI-assisted',
        },
        {
            id: 'ai_adopter',
            name: 'AI Adopter',
            emoji: '🤖',
            threshold: 50,
            description: 'At least 50% of completed work was AI-assisted',
        },
        {
            id: 'ai_champion',
            name: 'AI Champion',
            emoji: '🚀',
            threshold: 70,
            description: 'At least 70% of completed work was AI-assisted',
        },
        {
            id: 'ai_pioneer',
            name: 'AI Pioneer',
            emoji: '🧠',
            threshold: 85,
            description: 'At least 85% of completed work was AI-assisted — leading the way',
        },
    ],
}

// Calculate badges for a contributor
const calculateBadges = (contributor) => {
    const badges = []

    GAMIFICATION_CONFIG.velocityBadges.forEach((badge) => {
        if (contributor.currentVelocity >= badge.threshold) {
            badges.push(badge)
        }
    })

    GAMIFICATION_CONFIG.aiAdoptionBadges.forEach((badge) => {
        if (contributor.aiUsage >= badge.threshold) {
            badges.push(badge)
        }
    })

    return badges
}

// Data Staleness Banner — loud warning when the loaded snapshot is old.
// < 48h: renders nothing (no layout shift). 48h–7d: amber. > 7d (or bad/missing
// timestamp): red. Reflects the active workspace via the `lastSync` prop, which
// fetchData resets on every workspace switch.
const DataStalenessBanner = ({ lastSync, onRefresh, syncing }) => {
    const parsed = lastSync ? new Date(lastSync) : null
    const valid = parsed && !isNaN(parsed.getTime())
    const ageMs = valid ? Date.now() - parsed.getTime() : Infinity
    const HOUR = 1000 * 60 * 60

    // Fresh enough — render nothing at all (no layout shift).
    if (ageMs < 48 * HOUR) return null

    const danger = !valid || ageMs > 7 * 24 * HOUR
    const color = danger ? COLORS.danger : COLORS.warning
    const days = valid ? Math.floor(ageMs / (24 * HOUR)) : null
    const ageLabel = valid ? `${days} day${days === 1 ? '' : 's'} old` : 'of unknown age'
    const syncedLabel = valid ? `last synced ${parsed.toLocaleString()}` : 'last sync unknown'

    return (
        <div
            style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '16px',
                flexWrap: 'wrap',
                background: `${color}20`,
                border: `1px solid ${color}`,
                borderLeft: `4px solid ${color}`,
                borderRadius: '8px',
                padding: '12px 16px',
                marginBottom: '24px',
                color,
            }}
        >
            <span style={{ fontSize: '14px', fontWeight: '600' }}>
                ⚠ Data is {ageLabel} ({syncedLabel}). Numbers may be stale — run a full refresh.
            </span>
            <button
                onClick={onRefresh}
                disabled={syncing}
                style={{
                    background: syncing ? 'transparent' : color,
                    border: `1px solid ${color}`,
                    color: syncing ? color : '#fff',
                    padding: '8px 16px',
                    borderRadius: '8px',
                    fontSize: '13px',
                    fontWeight: '700',
                    cursor: syncing ? 'not-allowed' : 'pointer',
                    whiteSpace: 'nowrap',
                }}
            >
                {syncing ? '⏳ Syncing...' : '🔃 Refresh All Sprints'}
            </button>
        </div>
    )
}

// Stat Card Component
const StatCard = ({ title, value, subtitle, trend, icon, color = COLORS.primary, progressMax = 1600 }) => (
    <div
        style={{
            background: COLORS.cardBg,
            borderRadius: '16px',
            padding: '24px',
            border: `1px solid ${COLORS.border}`,
        }}
    >
        <div
            style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}
        >
            <span style={{ color: COLORS.textMuted, fontSize: '14px', fontWeight: '500' }}>{title}</span>
            {icon && <span style={{ fontSize: '24px' }}>{icon}</span>}
        </div>
        <div style={{ fontSize: '36px', fontWeight: '700', color: COLORS.text, marginBottom: '8px' }}>
            {typeof value === 'number' ? value.toLocaleString() : value}
        </div>
        {subtitle && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                {trend && (
                    <span
                        style={{
                            color: trend > 0 ? COLORS.success : trend < 0 ? COLORS.danger : COLORS.textMuted,
                            fontSize: '14px',
                            fontWeight: '600',
                        }}
                    >
                        {trend > 0 ? '↑' : trend < 0 ? '↓' : '→'} {Math.abs(trend)}%
                    </span>
                )}
                <span style={{ color: COLORS.textMuted, fontSize: '14px' }}>{subtitle}</span>
            </div>
        )}
        <div
            style={{
                height: '4px',
                background: `${color}30`,
                borderRadius: '2px',
                marginTop: '16px',
                overflow: 'hidden',
            }}
        >
            <div
                style={{
                    height: '100%',
                    width: `${Math.min(((typeof value === 'number' ? value : parseFloat(value) || 0) / progressMax) * 100, 100)}%`,
                    background: color,
                    borderRadius: '2px',
                    transition: 'width 0.5s ease',
                }}
            />
        </div>
    </div>
)

// Progress Ring Component
const ProgressRing = ({ progress, size = 120, strokeWidth = 10, color = COLORS.ai }) => {
    const radius = (size - strokeWidth) / 2
    const circumference = radius * 2 * Math.PI
    const offset = circumference - (progress / 100) * circumference

    return (
        <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
            <circle
                stroke={`${color}20`}
                strokeWidth={strokeWidth}
                fill="transparent"
                r={radius}
                cx={size / 2}
                cy={size / 2}
            />
            <circle
                stroke={color}
                strokeWidth={strokeWidth}
                strokeLinecap="round"
                fill="transparent"
                r={radius}
                cx={size / 2}
                cy={size / 2}
                style={{
                    strokeDasharray: circumference,
                    strokeDashoffset: offset,
                    transition: 'stroke-dashoffset 0.5s ease',
                }}
            />
        </svg>
    )
}

// Custom XAxis tick that shows sprint number + start date on two lines
const SprintTick = ({ x, y, payload, data: chartData }) => {
    const entry = chartData?.find((d) => d.sprint === payload.value)
    const dateStr = entry?.startDate ? fmtDate(entry.startDate) : ''
    return (
        <g transform={`translate(${x},${y})`}>
            <text x={0} y={0} dy={12} textAnchor="middle" fill={COLORS.textMuted} fontSize={12}>
                {payload.value}
            </text>
            {dateStr && (
                <text x={0} y={0} dy={25} textAnchor="middle" fill={COLORS.textMuted} fontSize={10} opacity={0.7}>
                    {dateStr}
                </text>
            )}
        </g>
    )
}

// Velocity Chart Component
const VelocityChart = ({ data, selectedSprintNum }) => {
    // Baseline FTE = mean activeDevs across sprints with valid capacity data;
    // used to renormalize per-dev-sprint throughput back to a velocity scale
    // so it can share the left axis with raw velocity.
    const validForBaseline = (data || []).filter(
        (d) => d.activeDevs > 0 && d.avgAvailability != null && d.avgAvailability > 0
    )
    const baselineFTE =
        validForBaseline.length > 0
            ? validForBaseline.reduce((s, d) => s + d.activeDevs, 0) / validForBaseline.length
            : null
    // Pre-compute availability as a 0-100 number — function dataKey on <Line>
    // silently fails to render in some recharts versions, so use a string dataKey.
    const chartData = (data || []).map((d) => {
        const canAdjust = d.activeDevs > 0 && d.avgAvailability != null && d.avgAvailability > 0 && baselineFTE
        return {
            ...d,
            availabilityPct: d.avgAvailability != null ? Math.round(d.avgAvailability * 100) : null,
            capacityAdjusted: canAdjust
                ? Math.round((d.total / (d.activeDevs * d.avgAvailability)) * baselineFTE)
                : null,
        }
    })
    const baselineLabel = baselineFTE ? `${Math.round(baselineFTE)} devs @ 100%` : ''
    return (
        <div
            style={{
                background: COLORS.cardBg,
                borderRadius: '16px',
                padding: '24px',
                border: `1px solid ${COLORS.border}`,
            }}
        >
            <h3 style={{ color: COLORS.text, marginBottom: '4px', fontSize: '18px', fontWeight: '600' }}>
                Velocity Trend
            </h3>
            <p style={{ color: COLORS.textMuted, fontSize: '11px', margin: '0 0 12px 0' }}>
                Capacity-Adjusted normalizes velocity to a {baselineLabel} team — if it{"'"}s flat while raw moves, the
                swing is people-driven (PTO, headcount).
            </p>
            <ResponsiveContainer width="100%" height={320}>
                <ComposedChart data={chartData} margin={{ top: 5, right: 5, left: 0, bottom: 18 }}>
                    <defs>
                        <linearGradient id="totalGradient" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor={COLORS.primary} stopOpacity={0.4} />
                            <stop offset="95%" stopColor={COLORS.primary} stopOpacity={0.05} />
                        </linearGradient>
                        <linearGradient id="aiGradient" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor={COLORS.ai} stopOpacity={0.3} />
                            <stop offset="95%" stopColor={COLORS.ai} stopOpacity={0} />
                        </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke={COLORS.border} />
                    <XAxis dataKey="sprint" stroke={COLORS.textMuted} fontSize={12} tick={<SprintTick data={data} />} />
                    <YAxis
                        yAxisId="left"
                        stroke={COLORS.textMuted}
                        fontSize={12}
                        domain={[0, (max) => Math.max(700, Math.ceil((max + 50) / 100) * 100)]}
                    />
                    <YAxis
                        yAxisId="right"
                        orientation="right"
                        stroke="#22d3ee"
                        fontSize={12}
                        domain={[0, 100]}
                        tickFormatter={(v) => `${v}%`}
                        width={44}
                    />
                    <Tooltip
                        content={({ active, payload, label }) => {
                            if (!active || !payload?.length) return null
                            const d = payload[0]?.payload
                            return (
                                <div
                                    style={{
                                        background: COLORS.cardBg,
                                        border: `1px solid ${COLORS.border}`,
                                        borderRadius: '8px',
                                        padding: '10px 14px',
                                        color: COLORS.text,
                                    }}
                                >
                                    <div style={{ fontWeight: '600', fontSize: '13px', marginBottom: '2px' }}>
                                        {label}
                                    </div>
                                    {(d?.startDate || d?.endDate) && (
                                        <div style={{ color: COLORS.textMuted, fontSize: '11px', marginBottom: '8px' }}>
                                            {fmtDateRange(d.startDate, d.endDate)}
                                        </div>
                                    )}
                                    {payload
                                        .filter((p) => p.value != null)
                                        .map((p, i) => (
                                            <div
                                                key={i}
                                                style={{
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    gap: '16px',
                                                    fontSize: '12px',
                                                    marginBottom: '2px',
                                                }}
                                            >
                                                <span style={{ color: p.color }}>{p.name}</span>
                                                <span style={{ fontWeight: '600' }}>
                                                    {p.dataKey === 'availabilityPct' ? `${p.value}%` : p.value}
                                                </span>
                                            </div>
                                        ))}
                                </div>
                            )
                        }}
                    />
                    <Legend />
                    <Area
                        yAxisId="left"
                        type="monotone"
                        dataKey="total"
                        stroke={COLORS.primary}
                        strokeWidth={2}
                        fill="url(#totalGradient)"
                        name="Total Velocity"
                    />
                    <Area
                        yAxisId="left"
                        type="monotone"
                        dataKey="aiVelocity"
                        stroke={COLORS.ai}
                        fill="url(#aiGradient)"
                        name="AI-Assisted"
                    />
                    <Line
                        yAxisId="left"
                        type="monotone"
                        dataKey="goal"
                        stroke={COLORS.success}
                        strokeDasharray="5 5"
                        strokeWidth={2}
                        dot={false}
                        name="Goal (1600)"
                    />
                    <Line
                        yAxisId="left"
                        type="monotone"
                        dataKey="baseline"
                        stroke={COLORS.warning}
                        strokeDasharray="3 3"
                        dot={false}
                        name="Baseline (400)"
                    />
                    <Line
                        yAxisId="right"
                        type="monotone"
                        dataKey="availabilityPct"
                        name="Team Availability"
                        stroke="#22d3ee"
                        strokeWidth={2}
                        strokeDasharray="2 4"
                        dot={{ r: 3, fill: '#22d3ee' }}
                        activeDot={{ r: 5 }}
                        connectNulls
                    />
                    <Line
                        yAxisId="left"
                        type="monotone"
                        dataKey="capacityAdjusted"
                        name="Capacity-Adjusted"
                        stroke={COLORS.cycleTime}
                        strokeWidth={2}
                        dot={{ r: 3, fill: COLORS.cycleTime }}
                        activeDot={{ r: 5 }}
                        connectNulls
                    />
                    {selectedSprintNum && (
                        <ReferenceLine
                            yAxisId="left"
                            x={`S${selectedSprintNum}`}
                            stroke={COLORS.text}
                            strokeWidth={2}
                            strokeDasharray="4 2"
                            label={{
                                value: `S${selectedSprintNum}`,
                                fill: COLORS.text,
                                fontSize: 11,
                                position: 'insideTopRight',
                            }}
                        />
                    )}
                </ComposedChart>
            </ResponsiveContainer>
        </div>
    )
}

// === Sprint-over-sprint driver analysis ===
const POD_LABELS = {
    BP: { name: 'Buyer', emoji: '🛒' },
    SP: { name: 'Seller', emoji: '🏪' },
    FP: { name: 'Foundations', emoji: '🏗️' },
    DAT: { name: 'Data', emoji: '📊' },
    DET: { name: 'Data Eng', emoji: '🛠️' },
}

const analyzeSprintDrivers = (curr, prev) => {
    if (!curr || !prev) return null
    const totalDelta = (curr.totalPoints || 0) - (prev.totalPoints || 0)
    const pctDelta = prev.totalPoints > 0 ? (totalDelta / prev.totalPoints) * 100 : null

    const allPods = new Set([...Object.keys(curr.byProject || {}), ...Object.keys(prev.byProject || {})])

    const podStats = [...allPods]
        .map((pod) => {
            const podCurrV = curr.byProject?.[pod] || 0
            const podPrevV = prev.byProject?.[pod] || 0
            const podDelta = podCurrV - podPrevV

            const currDevs = (curr.contributors || []).filter((c) => c.projects?.includes(pod))
            const prevDevs = (prev.contributors || []).filter((c) => c.projects?.includes(pod))
            const currDevCount = currDevs.length
            const prevDevCount = prevDevs.length

            const avg = (arr) => (arr.length > 0 ? arr.reduce((s, v) => s + v, 0) / arr.length : null)
            const currAvail = avg(currDevs.map((c) => c.availability).filter((a) => a != null))
            const prevAvail = avg(prevDevs.map((c) => c.availability).filter((a) => a != null))

            // Per-dev throughput at full availability — pod_velocity / (devs × avail)
            const currPDT = currDevCount > 0 && currAvail ? podCurrV / (currDevCount * currAvail) : null
            const prevPDT = prevDevCount > 0 && prevAvail ? podPrevV / (prevDevCount * prevAvail) : null

            // Laspeyres-style decomposition holding earlier factors at prev value
            let dHead = null,
                dAvail = null,
                dThr = null
            if (currAvail != null && prevAvail != null && prevPDT != null && currPDT != null) {
                dHead = (currDevCount - prevDevCount) * prevAvail * prevPDT
                dAvail = currDevCount * (currAvail - prevAvail) * prevPDT
                dThr = currDevCount * currAvail * (currPDT - prevPDT)
            }

            return {
                pod,
                podDelta,
                currDevCount,
                prevDevCount,
                currAvail,
                prevAvail,
                currPDT,
                prevPDT,
                dHead,
                dAvail,
                dThr,
            }
        })
        .filter((p) => !(p.podDelta === 0 && p.currDevCount === 0 && p.prevDevCount === 0))
        .sort((a, b) => Math.abs(b.podDelta) - Math.abs(a.podDelta))

    return { totalDelta, pctDelta, podStats, sprintNum: curr.sprintNum, sprintName: curr.name }
}

// Pick the dominant factor for a pod and produce a one-line caption
const podFactorCaption = (p) => {
    // No decomposition possible — fall back to raw counts
    if (p.dHead == null) {
        const bits = []
        if (p.currDevCount !== p.prevDevCount && p.prevDevCount > 0) {
            bits.push(`${p.currDevCount} devs (was ${p.prevDevCount})`)
        }
        if (p.currAvail != null && p.prevAvail != null && Math.abs(p.currAvail - p.prevAvail) > 0.03) {
            bits.push(`avail ${Math.round(p.currAvail * 100)}% (was ${Math.round(p.prevAvail * 100)}%)`)
        }
        return bits.join(' • ') || '—'
    }
    // Pick factor with largest |contribution| matching the sign of podDelta
    const factors = [
        {
            key: 'head',
            val: p.dHead,
            text: p.currDevCount === p.prevDevCount ? null : `${p.currDevCount} devs (was ${p.prevDevCount})`,
        },
        {
            key: 'avail',
            val: p.dAvail,
            text:
                p.currAvail != null && p.prevAvail != null
                    ? `avail ${Math.round(p.currAvail * 100)}% (was ${Math.round(p.prevAvail * 100)}%)`
                    : null,
        },
        {
            key: 'thr',
            val: p.dThr,
            text:
                p.prevPDT && p.currPDT
                    ? `per-dev output ${p.currPDT >= p.prevPDT ? '+' : ''}${Math.round((p.currPDT / p.prevPDT - 1) * 100)}%`
                    : null,
        },
    ].filter((f) => f.text != null)
    if (factors.length === 0) return '—'
    factors.sort((a, b) => Math.abs(b.val) - Math.abs(a.val))
    const dom = factors[0]
    // If the second factor is also material (>40% of first), include it too
    const second = factors[1]
    if (second && Math.abs(second.val) > Math.abs(dom.val) * 0.4) {
        return `${dom.text} • ${second.text}`
    }
    return dom.text
}

// Point-in-time card for the in-flight sprint. Reads data.activeSprint (engineering
// workspace only — server only computes it for workspaces with enableActiveSprint).
// Pod filter applies via projectBreakdown so the same Buyer/Seller/Foundations
// chip in the global filter chain narrows this card too.
const ActiveSprintCard = ({ activeSprint, selectedPod, podColor }) => {
    if (!activeSprint) return null

    // Apply pod filter by collapsing onto the matching projectBreakdown entry.
    const view = (() => {
        if (!selectedPod) return activeSprint
        const proj = activeSprint.projectBreakdown?.find((p) => p.name === selectedPod)
        if (!proj) return activeSprint
        return {
            ...activeSprint,
            pointsCommitted: proj.pointsCommitted,
            pointsCompleted: proj.pointsCompleted,
            pointsInProgress: proj.pointsInProgress,
            pointsRemaining: proj.pointsCommitted - proj.pointsCompleted,
            storiesCommitted: proj.storiesCommitted,
            storiesCompleted: proj.storiesCompleted,
            aiPointsCompleted: proj.aiPointsCompleted,
            aiAdoption: proj.aiAdoption,
            percentComplete: proj.percentComplete,
            contributors: (activeSprint.contributors || []).filter((c) => c.projects?.includes(selectedPod)),
        }
    })()

    const accent = podColor || COLORS.warning
    const pctComplete = Math.min(100, view.percentComplete || 0)
    const pctTime = view.percentTimeElapsed != null ? Math.min(100, view.percentTimeElapsed) : null
    // Highlight when work-completion is more than 15 points behind time-elapsed
    const behindByTime = pctTime != null && pctTime - pctComplete > 15

    const Metric = ({ label, value, sub, color }) => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', minWidth: '120px' }}>
            <span style={{ color: COLORS.textMuted, fontSize: '11px', fontWeight: '600', textTransform: 'uppercase' }}>
                {label}
            </span>
            <span style={{ color: color || COLORS.text, fontSize: '22px', fontWeight: '700', lineHeight: 1.1 }}>
                {value}
            </span>
            {sub && <span style={{ color: COLORS.textMuted, fontSize: '12px' }}>{sub}</span>}
        </div>
    )

    return (
        <div
            style={{
                background: COLORS.cardBg,
                borderRadius: '16px',
                padding: '24px',
                border: `1px solid ${accent}55`,
                marginBottom: '24px',
                boxShadow: `0 0 24px ${accent}15`,
            }}
        >
            {/* Header */}
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    flexWrap: 'wrap',
                    gap: '12px',
                    marginBottom: '16px',
                }}
            >
                <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '4px' }}>
                        <span
                            style={{
                                background: `${accent}20`,
                                color: accent,
                                fontSize: '10px',
                                fontWeight: '700',
                                letterSpacing: '0.08em',
                                textTransform: 'uppercase',
                                padding: '3px 8px',
                                borderRadius: '999px',
                                border: `1px solid ${accent}66`,
                            }}
                        >
                            🔥 Active
                        </span>
                        <h3 style={{ color: COLORS.text, margin: 0, fontSize: '20px', fontWeight: '700' }}>
                            {view.name}
                            {selectedPod && (
                                <span style={{ color: COLORS.textMuted, fontWeight: '500' }}> · {selectedPod}</span>
                            )}
                        </h3>
                    </div>
                    <div style={{ color: COLORS.textMuted, fontSize: '13px' }}>
                        {fmtDateRange(activeSprint.startDate, activeSprint.endDate)}
                        {view.daysTotal != null && (
                            <>
                                {' · '}
                                Day {Math.min(view.daysElapsed, view.daysTotal)} of {view.daysTotal}
                                {view.daysRemaining != null && (
                                    <span style={{ color: view.daysRemaining <= 2 ? COLORS.warning : COLORS.textMuted }}>
                                        {' · '}
                                        {view.daysRemaining}d left
                                    </span>
                                )}
                            </>
                        )}
                    </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div
                        style={{
                            color: behindByTime ? COLORS.warning : COLORS.success,
                            fontSize: '32px',
                            fontWeight: '700',
                            lineHeight: 1,
                        }}
                    >
                        {pctComplete}%
                    </div>
                    <div style={{ color: COLORS.textMuted, fontSize: '12px' }}>complete</div>
                </div>
            </div>

            {/* Dual progress bar: work vs time elapsed */}
            <div style={{ marginBottom: '20px' }}>
                <div
                    style={{
                        position: 'relative',
                        height: '12px',
                        background: COLORS.background,
                        borderRadius: '6px',
                        overflow: 'hidden',
                        border: `1px solid ${COLORS.border}`,
                    }}
                >
                    <div
                        style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            height: '100%',
                            width: `${pctComplete}%`,
                            background: behindByTime ? COLORS.warning : COLORS.success,
                            borderRadius: '6px',
                            transition: 'width 0.4s ease',
                        }}
                    />
                    {pctTime != null && (
                        <div
                            style={{
                                position: 'absolute',
                                top: '-3px',
                                bottom: '-3px',
                                left: `${pctTime}%`,
                                width: '2px',
                                background: COLORS.textMuted,
                            }}
                            title={`Time elapsed: ${pctTime}%`}
                        />
                    )}
                </div>
                {pctTime != null && (
                    <div style={{ marginTop: '6px', display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ color: COLORS.textMuted, fontSize: '11px' }}>
                            ▌ time elapsed {pctTime}%
                        </span>
                        {behindByTime && (
                            <span style={{ color: COLORS.warning, fontSize: '11px', fontWeight: '600' }}>
                                Behind pace
                            </span>
                        )}
                    </div>
                )}
            </div>

            {/* Metric grid */}
            <div
                style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
                    gap: '16px',
                    marginBottom: '20px',
                    paddingBottom: '20px',
                    borderBottom: `1px solid ${COLORS.border}`,
                }}
            >
                <Metric
                    label="Committed"
                    value={view.pointsCommitted}
                    sub={`${view.storiesCommitted} stories`}
                />
                <Metric
                    label="Completed"
                    value={view.pointsCompleted}
                    sub={`${view.storiesCompleted} stories`}
                    color={COLORS.success}
                />
                <Metric
                    label="In progress"
                    value={view.pointsInProgress}
                    sub={`${activeSprint.storiesInProgress ?? 0} stories`}
                    color={COLORS.primary}
                />
                <Metric
                    label="Remaining"
                    value={view.pointsRemaining ?? view.pointsCommitted - view.pointsCompleted}
                    sub="not done"
                    color={COLORS.textMuted}
                />
                <Metric
                    label="AI-assisted"
                    value={`${view.aiAdoption || 0}%`}
                    sub={`${view.aiPointsCompleted || 0} of ${view.pointsCompleted || 0} done`}
                    color={COLORS.ai}
                />
            </div>

            {/* Per-pod breakdown (only when not pod-filtered, and only with >1 pod in data) */}
            {!selectedPod && (activeSprint.projectBreakdown || []).length > 1 && (
                <div style={{ marginBottom: '16px' }}>
                    <div
                        style={{
                            color: COLORS.textMuted,
                            fontSize: '11px',
                            fontWeight: '600',
                            textTransform: 'uppercase',
                            marginBottom: '10px',
                        }}
                    >
                        By Pod
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '12px' }}>
                        {activeSprint.projectBreakdown.map((p, idx) => {
                            const podColor = CHART_COLORS[idx % CHART_COLORS.length]
                            const pct = Math.min(100, p.percentComplete || 0)
                            return (
                                <div
                                    key={p.name}
                                    style={{
                                        background: COLORS.background,
                                        border: `1px solid ${COLORS.border}`,
                                        borderRadius: '10px',
                                        padding: '12px 14px',
                                    }}
                                >
                                    <div
                                        style={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                            marginBottom: '6px',
                                        }}
                                    >
                                        <span style={{ color: podColor, fontSize: '13px', fontWeight: '700' }}>
                                            {p.name}
                                        </span>
                                        <span style={{ color: COLORS.text, fontSize: '13px', fontWeight: '600' }}>
                                            {p.pointsCompleted} / {p.pointsCommitted} pts
                                        </span>
                                    </div>
                                    <div
                                        style={{
                                            height: '6px',
                                            background: `${podColor}25`,
                                            borderRadius: '3px',
                                            overflow: 'hidden',
                                            marginBottom: '6px',
                                        }}
                                    >
                                        <div
                                            style={{
                                                width: `${pct}%`,
                                                height: '100%',
                                                background: podColor,
                                                transition: 'width 0.4s ease',
                                            }}
                                        />
                                    </div>
                                    <div style={{ color: COLORS.textMuted, fontSize: '11px' }}>
                                        {pct}% · {p.storiesCompleted}/{p.storiesCommitted} stories · AI {p.aiAdoption}%
                                    </div>
                                </div>
                            )
                        })}
                    </div>
                </div>
            )}

            {/* Contributor list */}
            {(view.contributors || []).length > 0 && (
                <details>
                    <summary
                        style={{
                            color: COLORS.textMuted,
                            fontSize: '11px',
                            fontWeight: '600',
                            textTransform: 'uppercase',
                            cursor: 'pointer',
                            marginBottom: '10px',
                            userSelect: 'none',
                        }}
                    >
                        By Contributor ({view.contributors.length})
                    </summary>
                    <div
                        style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                            gap: '8px',
                            marginTop: '8px',
                        }}
                    >
                        {view.contributors.map((c) => {
                            const pct = c.percentComplete || 0
                            return (
                                <div
                                    key={c.id}
                                    style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        padding: '6px 10px',
                                        background: COLORS.background,
                                        borderRadius: '6px',
                                        fontSize: '12px',
                                        border: `1px solid ${COLORS.border}`,
                                    }}
                                >
                                    <span style={{ color: COLORS.text, fontWeight: '500' }}>{c.name}</span>
                                    <span style={{ color: COLORS.textMuted }}>
                                        <span style={{ color: COLORS.success, fontWeight: '600' }}>{c.pointsCompleted}</span>
                                        {' / '}
                                        {c.pointsCommitted} ({pct}%)
                                    </span>
                                </div>
                            )
                        })}
                    </div>
                </details>
            )}
        </div>
    )
}

const SprintDriversCard = ({ history }) => {
    if (!history || history.length < 2) return null
    // history is sorted latest-first; pair each sprint with the next (older) one
    const analyses = []
    for (let i = 0; i < Math.min(5, history.length - 1); i++) {
        const a = analyzeSprintDrivers(history[i], history[i + 1])
        if (a) analyses.push(a)
    }
    if (analyses.length === 0) return null

    return (
        <div
            style={{
                background: COLORS.cardBg,
                borderRadius: '16px',
                padding: '24px',
                border: `1px solid ${COLORS.border}`,
                marginBottom: '24px',
            }}
        >
            <h3 style={{ color: COLORS.text, marginBottom: '4px', fontSize: '18px', fontWeight: '600' }}>
                What moved velocity
            </h3>
            <p style={{ color: COLORS.textMuted, fontSize: '11px', margin: '0 0 16px 0' }}>
                Sprint-over-sprint Δ split by pod. The bullet under each pod is the dominant factor (headcount /
                availability / per-dev throughput).
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                {analyses.map((a) => {
                    const sign = a.totalDelta > 0 ? '▲' : a.totalDelta < 0 ? '▼' : '·'
                    const color =
                        a.totalDelta > 0 ? COLORS.success : a.totalDelta < 0 ? COLORS.danger : COLORS.textMuted
                    return (
                        <div key={a.sprintNum} style={{ borderTop: `1px solid ${COLORS.border}`, paddingTop: '12px' }}>
                            <div style={{ display: 'flex', alignItems: 'baseline', gap: '12px', marginBottom: '6px' }}>
                                <span style={{ color: COLORS.text, fontSize: '14px', fontWeight: '600' }}>
                                    S{a.sprintNum}
                                </span>
                                <span style={{ color, fontSize: '13px', fontWeight: '600' }}>
                                    {sign} {Math.abs(Math.round(a.totalDelta))} pts
                                    {a.pctDelta != null
                                        ? ` (${a.totalDelta >= 0 ? '+' : ''}${a.pctDelta.toFixed(0)}%)`
                                        : ''}
                                </span>
                                <span style={{ color: COLORS.textMuted, fontSize: '12px' }}>vs S{a.sprintNum - 1}</span>
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', paddingLeft: '12px' }}>
                                {a.podStats.map((p) => {
                                    const label = POD_LABELS[p.pod] || { name: p.pod, emoji: '•' }
                                    const podColor =
                                        p.podDelta > 0
                                            ? COLORS.success
                                            : p.podDelta < 0
                                              ? COLORS.danger
                                              : COLORS.textMuted
                                    return (
                                        <div
                                            key={p.pod}
                                            style={{
                                                display: 'flex',
                                                alignItems: 'baseline',
                                                gap: '10px',
                                                fontSize: '12px',
                                            }}
                                        >
                                            <span style={{ color: COLORS.text, minWidth: '120px' }}>
                                                {label.emoji} {label.name}
                                            </span>
                                            <span style={{ color: podColor, fontWeight: '600', minWidth: '70px' }}>
                                                {p.podDelta > 0 ? '+' : ''}
                                                {Math.round(p.podDelta)} pts
                                            </span>
                                            <span style={{ color: COLORS.textMuted }}>{podFactorCaption(p)}</span>
                                        </div>
                                    )
                                })}
                            </div>
                        </div>
                    )
                })}
            </div>
        </div>
    )
}

// Sprint Diagnostics Chart — explains why velocity went up or down
const DIAGNOSTICS_METRICS = [
    {
        key: 'activeDevs',
        label: 'Active Devs',
        emoji: '👥',
        color: '#a78bfa',
        desc: 'Devs who shipped at least one story. A drop here directly explains lower team velocity.',
        yAxis: 'right',
        higherIsBetter: true,
    },
    {
        key: 'fullyActiveDevs',
        label: 'Fully Active',
        emoji: '✅',
        color: '#4ade80',
        desc: 'Developers with ≥80% availability (based on Jira activity days + velocity vs rolling avg). These devs were present for the full sprint.',
        yAxis: 'right',
        higherIsBetter: true,
    },
    {
        key: 'partialDevs',
        label: 'Partial Devs',
        emoji: '⏸️',
        color: '#fbbf24',
        strokeDasharray: '4 2',
        desc: 'Developers with <80% availability. Likely PTO, illness, or joined/left mid-sprint. Gap between Active Devs and Fully Active = partial capacity.',
        yAxis: 'right',
        higherIsBetter: false,
    },
    {
        key: 'pointsPerDev',
        label: 'Pts / Dev',
        emoji: '⚡',
        color: '#fb923c',
        strokeDasharray: '5 3',
        desc: 'Average output per active developer. Falling here with stable headcount signals productivity or scope issues.',
        yAxis: 'left',
        higherIsBetter: true,
    },
    {
        key: 'storiesCommitted',
        label: 'Stories Committed',
        emoji: '📝',
        color: '#60a5fa',
        desc: 'Stories planned at sprint start (completed + not done). Shows how much was taken on vs delivered.',
        yAxis: 'left',
        higherIsBetter: null, // neutral — more commitment isn't inherently better
    },
    {
        key: 'storiesCompleted',
        label: 'Stories Done',
        emoji: '📋',
        color: '#34d399',
        strokeDasharray: '3 3',
        desc: 'Total stories closed. Compare against total points to spot changes in average story size.',
        yAxis: 'left',
        higherIsBetter: true,
    },
    {
        key: 'rollovers',
        label: 'Rollovers',
        emoji: '🔁',
        color: '#f87171',
        strokeDasharray: '2 4',
        desc: 'Stories carried over from the prior sprint. High rollovers indicate overcommitment or blockers.',
        yAxis: 'right',
        higherIsBetter: false, // fewer rollovers = better
    },
    {
        key: 'avgStorySize',
        label: 'Avg Story Size',
        emoji: '📐',
        color: '#fbbf24',
        desc: 'Mean story points per ticket. Rising here means the team is tackling larger, potentially riskier work.',
        yAxis: 'right',
        higherIsBetter: null, // neutral — depends on context
    },
    {
        key: 'avgAvailability',
        label: 'Avg Availability',
        emoji: '📅',
        color: '#22d3ee',
        desc: 'Team average availability (0-1). Combines Jira activity days with velocity deviation from rolling average.',
        yAxis: 'right',
        higherIsBetter: true,
    },
    {
        key: 'lowAvailCount',
        label: 'Low Avail Devs',
        emoji: '🔻',
        color: '#fb7185',
        strokeDasharray: '2 2',
        desc: 'Developers below 50% availability. May indicate PTO, illness, or blockers.',
        yAxis: 'right',
        higherIsBetter: false,
    },
]

const SprintDiagnosticsChart = ({ data }) => {
    const DiagnosticsTooltip = ({ active, payload, label }) => {
        if (!active || !payload?.length) return null

        // Full data object for the hovered sprint
        const sprintData = payload[0]?.payload

        // Find the previous sprint (data is sorted chronologically old→new)
        const sprintIdx = data.findIndex((d) => d.sprintNum === sprintData?.sprintNum)
        const prevData = sprintIdx > 0 ? data[sprintIdx - 1] : null

        return (
            <div
                style={{
                    background: COLORS.background,
                    border: `1px solid ${COLORS.border}`,
                    borderRadius: '10px',
                    padding: '12px 16px',
                    boxShadow: '0 8px 24px rgba(0,0,0,0.6)',
                    minWidth: '260px',
                }}
            >
                {/* Header: sprint label + date range + "vs previous" reference */}
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'baseline',
                        marginBottom: '2px',
                    }}
                >
                    <span style={{ color: COLORS.text, fontWeight: '700', fontSize: '13px' }}>{label}</span>
                    {prevData && (
                        <span style={{ color: COLORS.textMuted, fontSize: '10px' }}>vs S{prevData.sprintNum}</span>
                    )}
                </div>
                {(sprintData?.startDate || sprintData?.endDate) && (
                    <div style={{ color: COLORS.textMuted, fontSize: '10px', marginBottom: '10px' }}>
                        {fmtDateRange(sprintData.startDate, sprintData.endDate)}
                    </div>
                )}

                {payload.map((entry) => {
                    const metric = DIAGNOSTICS_METRICS.find((m) => m.key === entry.dataKey)
                    const currentVal = entry.value
                    const prevVal = prevData != null ? prevData[entry.dataKey] : null
                    const delta = prevVal != null ? Number((currentVal - prevVal).toFixed(1)) : null

                    // Determine colour: green = good, red = bad, muted = neutral / no prev
                    let deltaColor = COLORS.textMuted
                    if (delta !== null && delta !== 0 && metric?.higherIsBetter != null) {
                        const improved = metric.higherIsBetter ? delta > 0 : delta < 0
                        deltaColor = improved ? COLORS.success : COLORS.danger
                    }

                    // Arrow glyph
                    const arrow = delta === null ? null : delta > 0 ? '↑' : delta < 0 ? '↓' : '→'
                    const sign = delta > 0 ? '+' : ''

                    // Sub-line points notes
                    let pointsNote = null
                    let prevPointsNote = null
                    if (entry.dataKey === 'storiesCommitted') {
                        if (sprintData?.pointsCommitted != null)
                            pointsNote = `${sprintData.pointsCommitted} pts committed`
                        if (prevData?.pointsCommitted != null) prevPointsNote = `prev: ${prevData.pointsCommitted} pts`
                    } else if (entry.dataKey === 'storiesCompleted') {
                        if (sprintData?.total != null) pointsNote = `${sprintData.total} pts delivered`
                        if (prevData?.total != null) prevPointsNote = `prev: ${prevData.total} pts`
                    } else if (entry.dataKey === 'rollovers') {
                        if (sprintData?.rolloverPoints != null)
                            pointsNote = `${sprintData.rolloverPoints} pts rolled over`
                        if (prevData?.rolloverPoints != null) prevPointsNote = `prev: ${prevData.rolloverPoints} pts`
                    }

                    return (
                        <div key={entry.dataKey} style={{ marginBottom: '7px' }}>
                            <div
                                style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    gap: '16px',
                                }}
                            >
                                <span
                                    style={{
                                        color: entry.color,
                                        fontSize: '12px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '4px',
                                    }}
                                >
                                    {entry.name}
                                </span>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}>
                                    <span style={{ color: COLORS.text, fontWeight: '700', fontSize: '12px' }}>
                                        {currentVal}
                                    </span>
                                    {arrow && (
                                        <span
                                            style={{
                                                color: deltaColor,
                                                fontSize: '11px',
                                                fontWeight: '600',
                                                minWidth: '36px',
                                                textAlign: 'right',
                                            }}
                                        >
                                            {arrow}
                                            {sign}
                                            {Math.abs(delta)}
                                        </span>
                                    )}
                                </div>
                            </div>
                            {(pointsNote || prevPointsNote) && (
                                <div
                                    style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        paddingLeft: '18px',
                                        marginTop: '2px',
                                    }}
                                >
                                    {pointsNote && (
                                        <span style={{ color: COLORS.textMuted, fontSize: '11px' }}>{pointsNote}</span>
                                    )}
                                    {prevPointsNote && (
                                        <span style={{ color: COLORS.textMuted, fontSize: '10px', opacity: 0.7 }}>
                                            {prevPointsNote}
                                        </span>
                                    )}
                                </div>
                            )}
                        </div>
                    )
                })}
            </div>
        )
    }

    return (
        <div
            style={{
                background: COLORS.cardBg,
                borderRadius: '16px',
                padding: '24px',
                border: `1px solid ${COLORS.border}`,
            }}
        >
            {/* Header */}
            <div style={{ marginBottom: '16px' }}>
                <h3 style={{ color: COLORS.text, fontSize: '18px', fontWeight: '600', marginBottom: '4px' }}>
                    🔬 Sprint Diagnostics
                </h3>
                <p style={{ color: COLORS.textMuted, fontSize: '12px' }}>
                    Overlay metrics to understand why velocity changes sprint over sprint
                </p>
            </div>

            {/* Metric legend chips */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '20px' }}>
                {DIAGNOSTICS_METRICS.map((m) => (
                    <div
                        key={m.key}
                        title={m.desc}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            background: `${m.color}18`,
                            border: `1px solid ${m.color}50`,
                            borderRadius: '8px',
                            padding: '5px 12px',
                            cursor: 'default',
                        }}
                    >
                        <span style={{ fontSize: '14px' }}>{m.emoji}</span>
                        <span style={{ color: m.color, fontSize: '11px', fontWeight: '700' }}>{m.label}</span>
                        <span style={{ color: COLORS.textMuted, fontSize: '10px' }}>— {m.desc.split('.')[0]}</span>
                    </div>
                ))}
            </div>

            {/* Axis scale note */}
            <div
                style={{ display: 'flex', gap: '16px', marginBottom: '8px', fontSize: '10px', color: COLORS.textMuted }}
            >
                <span>
                    ← Left axis: <span style={{ color: '#fb923c' }}>Pts/Dev</span>,{' '}
                    <span style={{ color: '#60a5fa' }}>Committed</span>, <span style={{ color: '#34d399' }}>Done</span>
                </span>
                <span>
                    Right axis →: <span style={{ color: '#a78bfa' }}>Active Devs</span>,{' '}
                    <span style={{ color: '#f87171' }}>Rollovers</span>,{' '}
                    <span style={{ color: '#fbbf24' }}>Avg Size</span>
                </span>
            </div>

            <ResponsiveContainer width="100%" height={320}>
                <LineChart data={data} margin={{ top: 5, right: 40, left: 0, bottom: 18 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={COLORS.border} />
                    <XAxis dataKey="sprint" stroke={COLORS.textMuted} fontSize={12} tick={<SprintTick data={data} />} />
                    <YAxis yAxisId="left" stroke={COLORS.textMuted} fontSize={11} domain={[0, 'auto']} tickCount={6} />
                    <YAxis
                        yAxisId="right"
                        orientation="right"
                        stroke={COLORS.textMuted}
                        fontSize={11}
                        domain={[0, 'auto']}
                        tickCount={6}
                    />
                    <Tooltip content={<DiagnosticsTooltip />} />
                    <Legend wrapperStyle={{ fontSize: '12px', color: COLORS.textMuted, paddingTop: '12px' }} />
                    {DIAGNOSTICS_METRICS.map((m) => (
                        <Line
                            key={m.key}
                            yAxisId={m.yAxis}
                            type="monotone"
                            dataKey={m.key}
                            stroke={m.color}
                            strokeWidth={2}
                            strokeDasharray={m.strokeDasharray}
                            dot={{ fill: m.color, r: 3, strokeWidth: 0 }}
                            activeDot={{ r: 5, fill: m.color }}
                            name={`${m.emoji} ${m.label}`}
                        />
                    ))}
                </LineChart>
            </ResponsiveContainer>
        </div>
    )
}

// Contributor Card Component
const ContributorCard = ({ contributor, rank }) => {
    const badges = calculateBadges(contributor)
    const topBadges = badges.slice(-3).reverse()
    const [hoveredBadge, setHoveredBadge] = useState(null)

    return (
        <div
            style={{
                background: COLORS.cardBg,
                borderRadius: '12px',
                padding: '16px',
                border: `1px solid ${COLORS.border}`,
                display: 'flex',
                alignItems: 'center',
                gap: '16px',
            }}
        >
            <div
                style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '50%',
                    background:
                        rank <= 3 ? `linear-gradient(135deg, ${COLORS.warning}, ${COLORS.secondary})` : COLORS.border,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: '700',
                    color: COLORS.text,
                    fontSize: '16px',
                    flexShrink: 0,
                }}
            >
                {rank}
            </div>

            <div style={{ flex: 1, minWidth: 0 }}>
                <div
                    style={{
                        color: COLORS.text,
                        fontWeight: '600',
                        fontSize: '15px',
                        marginBottom: '4px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                    }}
                >
                    {contributor.name}
                    <span style={{ display: 'flex', gap: '2px' }}>
                        {topBadges.map((b) => (
                            <span
                                key={b.id}
                                style={{ position: 'relative', cursor: 'default', fontSize: '14px' }}
                                onMouseEnter={() => setHoveredBadge(b.id)}
                                onMouseLeave={() => setHoveredBadge(null)}
                            >
                                {b.emoji}
                                {hoveredBadge === b.id && (
                                    <div
                                        style={{
                                            position: 'absolute',
                                            bottom: '130%',
                                            left: '50%',
                                            transform: 'translateX(-50%)',
                                            background: COLORS.background,
                                            border: `1px solid ${COLORS.border}`,
                                            borderRadius: '6px',
                                            padding: '5px 10px',
                                            fontSize: '12px',
                                            color: COLORS.text,
                                            whiteSpace: 'nowrap',
                                            zIndex: 100,
                                            boxShadow: '0 4px 12px rgba(0,0,0,0.5)',
                                            pointerEvents: 'none',
                                        }}
                                    >
                                        {b.emoji} {b.name}
                                    </div>
                                )}
                            </span>
                        ))}
                    </span>
                </div>
                <div style={{ color: COLORS.textMuted, fontSize: '13px' }}>
                    {contributor.currentVelocity} pts • {contributor.issueCount || 0} stories • {contributor.aiUsage}%
                    AI
                    {contributor.availability != null && (
                        <span
                            style={{
                                marginLeft: '6px',
                                color:
                                    contributor.availability >= 0.8
                                        ? COLORS.success
                                        : contributor.availability >= 0.5
                                          ? COLORS.warning
                                          : COLORS.danger,
                                fontWeight: '600',
                            }}
                        >
                            • {Math.round(contributor.availability * 100)}% avail
                        </span>
                    )}
                    {contributor.presentButLow && (
                        <span
                            title="Active but output significantly below average"
                            style={{ marginLeft: '4px', fontSize: '12px' }}
                        >
                            ⚠️
                        </span>
                    )}
                </div>
            </div>

            <div style={{ textAlign: 'right', flexShrink: 0 }}>
                <div
                    style={{
                        color:
                            contributor.trend === 'up'
                                ? COLORS.success
                                : contributor.trend === 'down'
                                  ? COLORS.danger
                                  : COLORS.textMuted,
                        fontSize: '20px',
                    }}
                >
                    {contributor.trend === 'up' ? '↑' : contributor.trend === 'down' ? '↓' : '→'}
                </div>
                <div style={{ color: COLORS.textMuted, fontSize: '12px' }}>avg: {contributor.avgVelocity}</div>
                {contributor.availability != null && (
                    <div style={{ color: COLORS.textMuted, fontSize: '11px', marginTop: '2px' }}>
                        {contributor.activityDays}/{contributor.businessDays}d
                        {contributor.velocityRatio != null && (
                            <span> • vr:{Math.round(contributor.velocityRatio * 100)}%</span>
                        )}
                    </div>
                )}
            </div>
        </div>
    )
}

// Project Breakdown Component
const ProjectBreakdown = ({ data, selectedPod }) => (
    <div
        style={{
            background: COLORS.cardBg,
            borderRadius: '16px',
            padding: '24px',
            border: `1px solid ${COLORS.border}`,
        }}
    >
        <h3 style={{ color: COLORS.text, marginBottom: '20px', fontSize: '18px', fontWeight: '600' }}>
            Project Breakdown
        </h3>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {data.map((project, index) => {
                const isHighlighted = !selectedPod || selectedPod === project.name
                return (
                    <div
                        key={project.name}
                        style={{ opacity: isHighlighted ? 1 : 0.35, transition: 'opacity 0.2s ease' }}
                    >
                        <div
                            style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                marginBottom: '8px',
                                alignItems: 'center',
                            }}
                        >
                            <span
                                style={{
                                    color: COLORS.text,
                                    fontWeight: selectedPod === project.name ? '700' : '600',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '6px',
                                }}
                            >
                                {selectedPod === project.name && (
                                    <span style={{ color: CHART_COLORS[index], fontSize: '10px' }}>▶</span>
                                )}
                                {project.name}
                            </span>
                            <span style={{ color: COLORS.textMuted }}>
                                {project.points} pts ({project.stories} stories)
                            </span>
                        </div>
                        <div
                            style={{
                                height: selectedPod === project.name ? '10px' : '8px',
                                background: COLORS.border,
                                borderRadius: '4px',
                                overflow: 'hidden',
                                transition: 'height 0.2s ease',
                            }}
                        >
                            <div
                                style={{
                                    height: '100%',
                                    width: `${Math.min(project.points / 2, 100)}%`,
                                    background: CHART_COLORS[index],
                                    borderRadius: '4px',
                                    boxShadow:
                                        selectedPod === project.name ? `0 0 8px ${CHART_COLORS[index]}88` : 'none',
                                }}
                            />
                        </div>
                        <div style={{ color: COLORS.ai, fontSize: '12px', marginTop: '4px' }}>
                            {project.aiPercent}% AI-assisted
                        </div>
                    </div>
                )
            })}
        </div>
    </div>
)

// Badge Leaderboard Component
const BadgeLeaderboard = ({
    contributors,
    title = '🎖️ Badge Champions',
    subtitle = 'Ranked by badges earned this sprint',
    emptyText = 'No badges earned yet this sprint.',
    useAllTimeBadges = false,
}) => {
    const [hoveredBadge, setHoveredBadge] = useState(null) // key string
    const [tooltipPos, setTooltipPos] = useState(null) // { top, left } for fixed positioning

    const ranked = (contributors || [])
        .map((c) => {
            if (useAllTimeBadges && c.allTimeBadges) {
                return { ...c, badges: c.allTimeBadges, badgeDisplayCount: c.totalBadgeCount }
            }
            const badges = calculateBadges(c)
            return { ...c, badges, badgeDisplayCount: badges.length }
        })
        .filter((c) => c.badges.length > 0)
        .sort((a, b) => b.badgeDisplayCount - a.badgeDisplayCount)

    const getCardBorder = (count) => {
        if (count >= 7) return '#ffd700'
        if (count >= 5) return COLORS.secondary
        if (count >= 3) return COLORS.ai
        return COLORS.border
    }

    const getCardGlow = (count) => {
        if (count >= 7) return '0 0 18px rgba(255, 215, 0, 0.35), inset 0 0 40px rgba(255, 215, 0, 0.04)'
        if (count >= 5) return `0 0 14px ${COLORS.secondary}45`
        if (count >= 3) return `0 0 10px ${COLORS.ai}35`
        return 'none'
    }

    const getRankBg = (index) => {
        if (index === 0) return 'linear-gradient(135deg, #ffd700, #f59e0b)'
        if (index === 1) return 'linear-gradient(135deg, #cbd5e1, #94a3b8)'
        if (index === 2) return 'linear-gradient(135deg, #cd7f32, #b45309)'
        return COLORS.border
    }

    const getCountPillBg = (count) => {
        if (count >= 7) return 'linear-gradient(135deg, #ffd700, #f59e0b)'
        if (count >= 5) return `linear-gradient(135deg, ${COLORS.secondary}, #7c3aed)`
        if (count >= 3) return `linear-gradient(135deg, ${COLORS.ai}, #0284c7)`
        return COLORS.border
    }

    if (ranked.length === 0) {
        return (
            <div
                style={{
                    background: COLORS.cardBg,
                    borderRadius: '16px',
                    padding: '24px',
                    border: `1px solid ${COLORS.border}`,
                }}
            >
                <h3 style={{ color: COLORS.text, marginBottom: '8px', fontSize: '18px', fontWeight: '600' }}>
                    {title}
                </h3>
                <p style={{ color: COLORS.textMuted, fontSize: '14px' }}>{emptyText}</p>
            </div>
        )
    }

    return (
        <div
            style={{
                background: COLORS.cardBg,
                borderRadius: '16px',
                padding: '24px',
                border: `1px solid ${COLORS.border}`,
            }}
        >
            <h3 style={{ color: COLORS.text, marginBottom: '4px', fontSize: '18px', fontWeight: '600' }}>{title}</h3>
            <p style={{ color: COLORS.textMuted, fontSize: '12px', marginBottom: '16px' }}>{subtitle}</p>
            <div
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '10px',
                    maxHeight: '480px',
                    overflowY: 'auto',
                    paddingRight: '4px',
                }}
            >
                {ranked.map((contributor, index) => (
                    <div
                        key={contributor.name}
                        style={{
                            background: `linear-gradient(135deg, ${COLORS.background} 0%, ${COLORS.cardBg} 100%)`,
                            borderRadius: '12px',
                            padding: '12px 14px',
                            border: `1px solid ${getCardBorder(contributor.badgeDisplayCount)}`,
                            boxShadow: getCardGlow(contributor.badgeDisplayCount),
                        }}
                    >
                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                            {/* Rank circle */}
                            <div
                                style={{
                                    width: '30px',
                                    height: '30px',
                                    borderRadius: '50%',
                                    background: getRankBg(index),
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontWeight: '700',
                                    color: index <= 2 ? '#0f172a' : COLORS.text,
                                    fontSize: '13px',
                                    flexShrink: 0,
                                }}
                            >
                                {index + 1}
                            </div>

                            <div style={{ flex: 1, minWidth: 0 }}>
                                {/* Name + badge count pill */}
                                <div
                                    style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        marginBottom: '8px',
                                    }}
                                >
                                    <span style={{ color: COLORS.text, fontWeight: '600', fontSize: '14px' }}>
                                        {contributor.name}
                                    </span>
                                    <span
                                        style={{
                                            background: getCountPillBg(contributor.badgeDisplayCount),
                                            color: contributor.badgeDisplayCount >= 7 ? '#0f172a' : COLORS.text,
                                            borderRadius: '999px',
                                            padding: '2px 10px',
                                            fontSize: '11px',
                                            fontWeight: '700',
                                            flexShrink: 0,
                                        }}
                                    >
                                        {contributor.badgeDisplayCount} badge
                                        {contributor.badgeDisplayCount !== 1 ? 's' : ''}
                                    </span>
                                </div>

                                {/* Badge chips */}
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                                    {contributor.badges.map((b) => {
                                        const isVelocity = GAMIFICATION_CONFIG.velocityBadges.some(
                                            (vb) => vb.id === b.id
                                        )
                                        const hoverKey = `${contributor.name}:${b.id}`
                                        const isHovered = hoveredBadge === hoverKey
                                        return (
                                            <span
                                                key={b.id}
                                                style={{
                                                    position: 'relative',
                                                    cursor: 'default',
                                                    background: isVelocity
                                                        ? 'linear-gradient(135deg, #78350f, #92400e)'
                                                        : 'linear-gradient(135deg, #164e63, #0e7490)',
                                                    border: `1px solid ${isVelocity ? (isHovered ? '#f59e0baa' : '#f59e0b55') : isHovered ? '#06b6d4aa' : '#06b6d455'}`,
                                                    borderRadius: '6px',
                                                    padding: '3px 8px',
                                                    fontSize: '11px',
                                                    color: isVelocity ? '#fcd34d' : '#67e8f9',
                                                    whiteSpace: 'nowrap',
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    gap: '3px',
                                                    transition: 'border-color 0.15s ease',
                                                }}
                                                onMouseEnter={(e) => {
                                                    const rect = e.currentTarget.getBoundingClientRect()
                                                    setTooltipPos({
                                                        top: rect.top - 8,
                                                        left: rect.left + rect.width / 2,
                                                    })
                                                    setHoveredBadge(hoverKey)
                                                }}
                                                onMouseLeave={() => {
                                                    setHoveredBadge(null)
                                                    setTooltipPos(null)
                                                }}
                                            >
                                                {b.emoji} {b.name}
                                                {b.earnCount ? ` ×${b.earnCount}` : ''}
                                                {isHovered && tooltipPos && (
                                                    <div
                                                        style={{
                                                            position: 'fixed',
                                                            top: tooltipPos.top,
                                                            left: tooltipPos.left,
                                                            transform: 'translate(-50%, -100%)',
                                                            background: COLORS.background,
                                                            border: `1px solid ${isVelocity ? '#f59e0b80' : '#06b6d480'}`,
                                                            borderRadius: '8px',
                                                            padding: '9px 13px',
                                                            zIndex: 9999,
                                                            boxShadow: `0 6px 20px rgba(0,0,0,0.7), 0 0 0 1px ${isVelocity ? '#f59e0b20' : '#06b6d420'}`,
                                                            pointerEvents: 'none',
                                                            minWidth: '180px',
                                                            maxWidth: '240px',
                                                        }}
                                                    >
                                                        <div
                                                            style={{
                                                                color: isVelocity ? '#fcd34d' : '#67e8f9',
                                                                fontWeight: '700',
                                                                fontSize: '12px',
                                                                marginBottom: '5px',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                gap: '5px',
                                                            }}
                                                        >
                                                            {b.emoji} {b.name}
                                                        </div>
                                                        <div
                                                            style={{
                                                                color: COLORS.textMuted,
                                                                fontSize: '11px',
                                                                lineHeight: '1.5',
                                                                whiteSpace: 'normal',
                                                            }}
                                                        >
                                                            {b.description}
                                                        </div>
                                                    </div>
                                                )}
                                            </span>
                                        )
                                    })}
                                </div>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    )
}

// Compact pill for issue type with Bug highlighted
const TypeBadge = ({ type }) => {
    const isBug = /bug/i.test(type || '')
    const color = isBug ? COLORS.danger : COLORS.textMuted
    return (
        <span
            style={{
                display: 'inline-block',
                background: `${color}22`,
                color,
                border: `1px solid ${color}55`,
                borderRadius: '999px',
                padding: '1px 8px',
                fontSize: '10px',
                fontWeight: '600',
            }}
        >
            {type || '—'}
        </span>
    )
}

// Per-sprint: points that landed in a release vs. points that didn't (orphans/unreleased)
// + bug-count line overlay
const ReleaseAttributionChart = ({ data, selectedSprintNum }) => (
    <div
        style={{
            background: COLORS.cardBg,
            borderRadius: '16px',
            padding: '24px',
            border: `1px solid ${COLORS.border}`,
        }}
    >
        <h3 style={{ color: COLORS.text, marginBottom: '4px', fontSize: '18px', fontWeight: '600' }}>
            Sprint → Release Attribution
        </h3>
        <p style={{ color: COLORS.textMuted, fontSize: '12px', marginBottom: '16px' }}>
            Completed sprint points split by release membership. Red line = bugs closed per sprint.
        </p>
        <ResponsiveContainer width="100%" height={320}>
            <ComposedChart data={data} margin={{ top: 5, right: 40, left: 0, bottom: 18 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={COLORS.border} />
                <XAxis dataKey="sprint" stroke={COLORS.textMuted} fontSize={12} />
                <YAxis
                    yAxisId="left"
                    stroke={COLORS.textMuted}
                    fontSize={11}
                    label={{
                        value: 'Story Points',
                        angle: -90,
                        position: 'insideLeft',
                        fill: COLORS.textMuted,
                        fontSize: 11,
                    }}
                />
                <YAxis
                    yAxisId="right"
                    orientation="right"
                    stroke={COLORS.textMuted}
                    fontSize={11}
                    label={{ value: 'Bugs', angle: 90, position: 'insideRight', fill: COLORS.textMuted, fontSize: 11 }}
                />
                <Tooltip
                    content={({ active, payload }) => {
                        if (!active || !payload?.length) return null
                        const d = payload[0]?.payload
                        const total = (d?.releasedPoints || 0) + (d?.unreleasedPoints || 0)
                        const pct = total > 0 ? Math.round((d.releasedPoints / total) * 1000) / 10 : 0
                        return (
                            <div
                                style={{
                                    background: COLORS.cardBg,
                                    border: `1px solid ${COLORS.border}`,
                                    borderRadius: '8px',
                                    padding: '10px 14px',
                                    color: COLORS.text,
                                    fontSize: '12px',
                                }}
                            >
                                <div style={{ fontWeight: '600', marginBottom: '4px' }}>{d?.sprint}</div>
                                <div>
                                    <span style={{ color: COLORS.success }}>Released:</span>{' '}
                                    <strong>{d?.releasedPoints}</strong> pts ({d?.releasedCount} stories)
                                </div>
                                <div>
                                    <span style={{ color: COLORS.warning }}>Unreleased:</span>{' '}
                                    <strong>{d?.unreleasedPoints}</strong> pts ({d?.unreleasedCount} stories)
                                </div>
                                <div style={{ color: COLORS.textMuted }}>Release rate: {pct}%</div>
                                <div style={{ color: COLORS.danger, marginTop: '4px' }}>
                                    🐛 {d?.bugCount} bug{d?.bugCount === 1 ? '' : 's'} closed ({d?.bugPoints} pts)
                                </div>
                            </div>
                        )
                    }}
                />
                <Legend />
                <Bar
                    yAxisId="left"
                    dataKey="releasedPoints"
                    stackId="a"
                    fill={COLORS.success}
                    name="Released"
                    radius={[0, 0, 0, 0]}
                />
                <Bar
                    yAxisId="left"
                    dataKey="unreleasedPoints"
                    stackId="a"
                    fill={COLORS.warning}
                    name="Unreleased"
                    radius={[4, 4, 0, 0]}
                />
                <Line
                    yAxisId="right"
                    type="monotone"
                    dataKey="bugCount"
                    stroke={COLORS.danger}
                    strokeWidth={2}
                    dot={{ r: 3 }}
                    name="Bugs closed"
                />
                {selectedSprintNum && (
                    <ReferenceLine
                        yAxisId="left"
                        x={`S${selectedSprintNum}`}
                        stroke={COLORS.text}
                        strokeWidth={2}
                        strokeDasharray="4 2"
                    />
                )}
            </ComposedChart>
        </ResponsiveContainer>
    </div>
)

// Lists unreleased (orphan/not-yet-shipped) issues for the selected sprint; bugs called out.
// When deployedKeys is provided, cross-references against GitLab prod deploys to distinguish
// "confirmed orphan" (shipped but not linked in Jira) from "unknown" (no code evidence).
const UnreleasedStoriesTable = ({ sprintNum, issues, releasedKeyMap, deployedKeys }) => {
    const th = {
        color: COLORS.textMuted,
        fontSize: '11px',
        fontWeight: '600',
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
        textAlign: 'left',
        padding: '10px 12px',
        borderBottom: `1px solid ${COLORS.border}`,
    }
    const td = {
        color: COLORS.text,
        fontSize: '13px',
        padding: '10px 12px',
        borderBottom: `1px solid ${COLORS.border}`,
    }
    const unreleased = (issues || []).filter((i) => !releasedKeyMap.has(i.key))
    const hasGitlab = deployedKeys && Object.keys(deployedKeys).length > 0

    // Sort: confirmed orphans first (GitLab evidence), then bugs, then by points desc
    unreleased.sort((a, b) => {
        const ad = hasGitlab && deployedKeys[a.key]?.length > 0 ? 1 : 0
        const bd = hasGitlab && deployedKeys[b.key]?.length > 0 ? 1 : 0
        if (ad !== bd) return bd - ad
        const ab = /bug/i.test(a.issueType || '') ? 1 : 0
        const bb = /bug/i.test(b.issueType || '') ? 1 : 0
        if (ab !== bb) return bb - ab
        return (b.storyPoints || 0) - (a.storyPoints || 0)
    })
    const bugs = unreleased.filter((i) => /bug/i.test(i.issueType || ''))
    const bugCount = bugs.length
    const bugPoints = bugs.reduce((s, i) => s + (i.storyPoints || 0), 0)
    const totalPoints = unreleased.reduce((s, i) => s + (i.storyPoints || 0), 0)
    const confirmedOrphans = hasGitlab ? unreleased.filter((i) => deployedKeys[i.key]?.length > 0) : []
    const confirmedOrphanPoints = confirmedOrphans.reduce((s, i) => s + (i.storyPoints || 0), 0)

    return (
        <div
            style={{
                background: COLORS.cardBg,
                borderRadius: '16px',
                padding: '24px',
                border: `1px solid ${COLORS.border}`,
                overflowX: 'auto',
            }}
        >
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '12px',
                    flexWrap: 'wrap',
                    gap: '12px',
                }}
            >
                <h3 style={{ color: COLORS.text, margin: 0, fontSize: '18px', fontWeight: '600' }}>
                    Unreleased from Sprint {sprintNum ?? '—'}
                </h3>
                <div style={{ color: COLORS.textMuted, fontSize: '12px' }}>
                    <span style={{ color: COLORS.warning, fontWeight: '600' }}>{unreleased.length}</span> stories (
                    {totalPoints} pts) ·{' '}
                    <span style={{ color: COLORS.danger, fontWeight: '600' }}>
                        🐛 {bugCount} bug{bugCount === 1 ? '' : 's'}
                    </span>{' '}
                    ({bugPoints} pts)
                    {hasGitlab && (
                        <>
                            {' '}
                            ·{' '}
                            <span style={{ color: COLORS.success, fontWeight: '600' }}>
                                🔗 {confirmedOrphans.length} confirmed orphan{confirmedOrphans.length === 1 ? '' : 's'}
                            </span>{' '}
                            ({confirmedOrphanPoints} pts)
                        </>
                    )}
                </div>
            </div>
            <p style={{ color: COLORS.textMuted, fontSize: '12px', marginTop: 0, marginBottom: '12px' }}>
                Closed in this sprint but not a child of any released GLOB epic.
                {hasGitlab
                    ? ' Highlighted rows have evidence they shipped to prod — 🔗 = matched via commit message, 🎯 = matched via Addressed-In field. Those are confirmed orphans (fix the Jira linkage).'
                    : ''}
            </p>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                    <tr>
                        <th style={th}>Key</th>
                        <th style={th}>Type</th>
                        <th style={th}>Summary</th>
                        <th style={th}>Assignee</th>
                        <th style={{ ...th, textAlign: 'right' }}>Pts</th>
                        {hasGitlab && <th style={th}>Shipped (GitLab)</th>}
                    </tr>
                </thead>
                <tbody>
                    {unreleased.slice(0, 100).map((i) => {
                        const isBug = /bug/i.test(i.issueType || '')
                        const deploys = hasGitlab ? deployedKeys[i.key] || [] : []
                        const confirmed = deploys.length > 0
                        const rowBg = confirmed ? `${COLORS.success}12` : isBug ? `${COLORS.danger}0d` : undefined
                        return (
                            <tr key={i.key} style={{ background: rowBg }}>
                                <td style={{ ...td, fontFamily: 'ui-monospace, monospace' }}>
                                    <a
                                        href={`${JIRA_BASE_URL}/browse/${i.key}`}
                                        target="_blank"
                                        rel="noreferrer"
                                        style={{
                                            color: isBug ? COLORS.danger : COLORS.primary,
                                            textDecoration: 'none',
                                        }}
                                        onMouseOver={(e) => {
                                            e.currentTarget.style.textDecoration = 'underline'
                                        }}
                                        onMouseOut={(e) => {
                                            e.currentTarget.style.textDecoration = 'none'
                                        }}
                                    >
                                        {i.key}
                                    </a>
                                </td>
                                <td style={td}>
                                    <TypeBadge type={i.issueType} />
                                </td>
                                <td
                                    style={{
                                        ...td,
                                        color: COLORS.text,
                                        maxWidth: '500px',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                    }}
                                >
                                    {i.summary || '—'}
                                </td>
                                <td style={{ ...td, color: COLORS.textMuted }}>{i.assignee || '—'}</td>
                                <td style={{ ...td, textAlign: 'right', fontWeight: '600' }}>{i.storyPoints || 0}</td>
                                {hasGitlab && (
                                    <td style={td}>
                                        {confirmed ? (
                                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                                                {deploys.slice(0, 3).map((d, idx) => {
                                                    const repoShort = d.repo
                                                        .replace('b-stock/code/three-mp/', '')
                                                        .replace('b-stock/code/', '')
                                                    const isAddressedIn = d.source === 'addressedIn'
                                                    const accent = isAddressedIn ? COLORS.secondary : COLORS.success
                                                    const icon = isAddressedIn ? '🎯' : '🔗'
                                                    const sourceLabel = isAddressedIn
                                                        ? 'via Addressed In field'
                                                        : 'via commit message'
                                                    return (
                                                        <a
                                                            key={idx}
                                                            href={d.tagUrl}
                                                            target="_blank"
                                                            rel="noreferrer"
                                                            style={{
                                                                fontSize: '10px',
                                                                color: accent,
                                                                textDecoration: 'none',
                                                                background: `${accent}1f`,
                                                                padding: '2px 6px',
                                                                borderRadius: '4px',
                                                                border: `1px solid ${accent}55`,
                                                                whiteSpace: 'nowrap',
                                                            }}
                                                            title={`Deployed ${d.deployedAt?.slice(0, 10)} · ${d.repo} · ${sourceLabel}`}
                                                        >
                                                            {icon} {repoShort}@{d.tag}
                                                        </a>
                                                    )
                                                })}
                                                {deploys.length > 3 && (
                                                    <span style={{ fontSize: '10px', color: COLORS.textMuted }}>
                                                        +{deploys.length - 3}
                                                    </span>
                                                )}
                                            </div>
                                        ) : (
                                            <span style={{ color: COLORS.textMuted, fontSize: '11px' }}>—</span>
                                        )}
                                    </td>
                                )}
                            </tr>
                        )
                    })}
                    {unreleased.length === 0 && (
                        <tr>
                            <td
                                colSpan={hasGitlab ? 6 : 5}
                                style={{ ...td, color: COLORS.success, textAlign: 'center' }}
                            >
                                ✓ Every story from this sprint is attributed to a release.
                            </td>
                        </tr>
                    )}
                </tbody>
            </table>
            {unreleased.length > 100 && (
                <div style={{ color: COLORS.textMuted, fontSize: '11px', textAlign: 'center', marginTop: '8px' }}>
                    Showing first 100 of {unreleased.length} unreleased issues.
                </div>
            )}
        </div>
    )
}

// ─────────────────────────────────────────────────────────────

// All-Time Contributor Card Component — shows cumulative stats across all sprints
const AllTimeContributorCard = ({ contributor, rank }) => {
    const badges = calculateBadges(contributor)
    const topBadges = badges.slice(-3).reverse()
    const [hoveredBadge, setHoveredBadge] = useState(null)

    return (
        <div
            style={{
                background: COLORS.cardBg,
                borderRadius: '12px',
                padding: '16px',
                border: `1px solid ${COLORS.border}`,
                display: 'flex',
                alignItems: 'center',
                gap: '16px',
            }}
        >
            <div
                style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '50%',
                    background:
                        rank <= 3 ? `linear-gradient(135deg, ${COLORS.warning}, ${COLORS.secondary})` : COLORS.border,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: '700',
                    color: COLORS.text,
                    fontSize: '16px',
                    flexShrink: 0,
                }}
            >
                {rank}
            </div>

            <div style={{ flex: 1, minWidth: 0 }}>
                <div
                    style={{
                        color: COLORS.text,
                        fontWeight: '600',
                        fontSize: '15px',
                        marginBottom: '4px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                    }}
                >
                    {contributor.name}
                    <span style={{ display: 'flex', gap: '2px' }}>
                        {topBadges.map((b) => (
                            <span
                                key={b.id}
                                style={{ position: 'relative', cursor: 'default', fontSize: '14px' }}
                                onMouseEnter={() => setHoveredBadge(b.id)}
                                onMouseLeave={() => setHoveredBadge(null)}
                            >
                                {b.emoji}
                                {hoveredBadge === b.id && (
                                    <div
                                        style={{
                                            position: 'absolute',
                                            bottom: '130%',
                                            left: '50%',
                                            transform: 'translateX(-50%)',
                                            background: COLORS.background,
                                            border: `1px solid ${COLORS.border}`,
                                            borderRadius: '6px',
                                            padding: '5px 10px',
                                            fontSize: '12px',
                                            color: COLORS.text,
                                            whiteSpace: 'nowrap',
                                            zIndex: 100,
                                            boxShadow: '0 4px 12px rgba(0,0,0,0.5)',
                                            pointerEvents: 'none',
                                        }}
                                    >
                                        {b.emoji} {b.name}
                                    </div>
                                )}
                            </span>
                        ))}
                    </span>
                </div>
                <div style={{ color: COLORS.textMuted, fontSize: '13px' }}>
                    {contributor.totalPoints} pts total • {contributor.sprintCount} sprints • avg{' '}
                    {contributor.avgVelocity}/sprint
                </div>
                <div
                    style={{
                        color: COLORS.textMuted,
                        fontSize: '12px',
                        marginTop: '2px',
                        display: 'flex',
                        gap: '12px',
                        flexWrap: 'wrap',
                    }}
                >
                    <span>
                        Peak:{' '}
                        <span style={{ color: COLORS.primary, fontWeight: '600' }}>{contributor.peakVelocity} pts</span>
                    </span>
                    <span>
                        AI: <span style={{ color: COLORS.ai, fontWeight: '600' }}>{contributor.overallAiUsage}%</span>{' '}
                        overall
                    </span>
                    <span>{contributor.totalIssues} stories</span>
                    {contributor.avgAvailability != null && (
                        <span>
                            Avail:{' '}
                            <span
                                style={{
                                    color:
                                        contributor.avgAvailability >= 0.8
                                            ? COLORS.success
                                            : contributor.avgAvailability >= 0.5
                                              ? COLORS.warning
                                              : COLORS.danger,
                                    fontWeight: '600',
                                }}
                            >
                                {Math.round(contributor.avgAvailability * 100)}%
                            </span>
                        </span>
                    )}
                </div>
            </div>

            <div style={{ textAlign: 'right', flexShrink: 0 }}>
                <div
                    style={{
                        color: COLORS.text,
                        fontSize: '22px',
                        fontWeight: '700',
                    }}
                >
                    {contributor.totalPoints}
                </div>
                <div style={{ color: COLORS.textMuted, fontSize: '11px' }}>total pts</div>
            </div>
        </div>
    )
}

// Developer Progress Chart — track individual developer velocity & AI usage over sprints
const DeveloperProgressChart = ({ sprintHistory, selectedDevelopers, onToggleDeveloper, selectedPod }) => {
    const [devSearch, setDevSearch] = useState('')
    const [chartMode, setChartMode] = useState('velocity') // 'velocity' | 'aiUsage' | 'availability'

    // Sorted sprints (chronological)
    const sortedSprints = [...(sprintHistory || [])].sort((a, b) => a.sprintNum - b.sprintNum)

    // Build a map of all developers who appear across sprints, collecting per-sprint data
    const allDevs = {}
    sortedSprints.forEach((sprint) => {
        ;(sprint.contributors || []).forEach((c) => {
            if (selectedPod && c.projects && !c.projects.includes(selectedPod)) return
            if (!allDevs[c.name]) {
                allDevs[c.name] = { name: c.name, totalPoints: 0, sprintCount: 0, sprintData: [] }
            }
            allDevs[c.name].totalPoints += c.currentVelocity || 0
            allDevs[c.name].sprintCount += 1
            allDevs[c.name].sprintData.push({
                sprintNum: sprint.sprintNum,
                velocity: c.currentVelocity || 0,
                aiUsage: c.aiUsage || 0,
                aiPoints: c.aiPoints || 0,
                issueCount: c.issueCount || 0,
                availability: c.availability != null ? Math.round(c.availability * 100) : null,
            })
        })
    })

    // Sort by total points descending for the picker
    const sortedDevs = Object.values(allDevs).sort((a, b) => b.totalPoints - a.totalPoints)

    // Build chart data: one row per sprint, with velocity and AI keys per selected developer
    const chartData = sortedSprints.map((sprint) => {
        const row = {
            sprint: `S${sprint.sprintNum}`,
            sprintNum: sprint.sprintNum,
            startDate: sprint.startDate,
            _devDetails: {},
        }
        selectedDevelopers.forEach((devName) => {
            const dev = (sprint.contributors || []).find((c) => c.name === devName)
            row[devName] =
                chartMode === 'velocity'
                    ? dev?.currentVelocity || 0
                    : chartMode === 'aiUsage'
                      ? dev?.aiUsage || 0
                      : dev?.availability != null
                        ? Math.round(dev.availability * 100)
                        : 0
            // Stash full details for tooltip
            row._devDetails[devName] = dev
                ? {
                      velocity: dev.currentVelocity || 0,
                      aiUsage: dev.aiUsage || 0,
                      aiPoints: dev.aiPoints || 0,
                      issueCount: dev.issueCount || 0,
                      availability: dev.availability != null ? Math.round(dev.availability * 100) : null,
                  }
                : null
        })
        return row
    })

    // Compute per-developer summary stats for selected developers
    const devSummaries = selectedDevelopers
        .map((devName, idx) => {
            const dev = allDevs[devName]
            if (!dev || dev.sprintData.length === 0) return null
            const sd = dev.sprintData
            const avgVelocity = Math.round((sd.reduce((s, d) => s + d.velocity, 0) / sd.length) * 10) / 10
            const avgAi = Math.round((sd.reduce((s, d) => s + d.aiUsage, 0) / sd.length) * 10) / 10
            const totalIssues = sd.reduce((s, d) => s + d.issueCount, 0)

            // AI trend: compare first half average to second half average
            const mid = Math.ceil(sd.length / 2)
            const firstHalf = sd.slice(0, mid)
            const secondHalf = sd.slice(mid)
            const firstAi = firstHalf.length > 0 ? firstHalf.reduce((s, d) => s + d.aiUsage, 0) / firstHalf.length : 0
            const secondAi =
                secondHalf.length > 0 ? secondHalf.reduce((s, d) => s + d.aiUsage, 0) / secondHalf.length : 0
            const aiDelta = Math.round(secondAi - firstAi)

            // Velocity trend: same first-half vs second-half
            const firstVel = firstHalf.length > 0 ? firstHalf.reduce((s, d) => s + d.velocity, 0) / firstHalf.length : 0
            const secondVel =
                secondHalf.length > 0 ? secondHalf.reduce((s, d) => s + d.velocity, 0) / secondHalf.length : 0
            const velDelta = Math.round((secondVel - firstVel) * 10) / 10

            // Latest sprint values
            const latest = sd[sd.length - 1]

            // Availability average
            const availSprints = sd.filter((d) => d.availability != null)
            const avgAvail =
                availSprints.length > 0
                    ? Math.round(availSprints.reduce((s, d) => s + d.availability, 0) / availSprints.length)
                    : null

            return {
                name: devName,
                color: DEV_COLORS[idx % DEV_COLORS.length],
                sprintCount: sd.length,
                avgVelocity,
                avgAi,
                avgAvail,
                totalIssues,
                aiDelta, // positive = trending up
                velDelta,
                latestVelocity: latest.velocity,
                latestAi: latest.aiUsage,
                latestAvail: latest.availability,
            }
        })
        .filter(Boolean)

    const filteredDevs = devSearch
        ? sortedDevs.filter((d) => d.name.toLowerCase().includes(devSearch.toLowerCase()))
        : sortedDevs

    const DevProgressTooltip = ({ active, payload, label }) => {
        if (!active || !payload?.length) return null
        const sprintRow = payload[0]?.payload
        const details = sprintRow?._devDetails || {}
        // Gather entries that have data
        const entries = payload
            .filter((p) => {
                const d = details[p.name]
                return d && (d.velocity > 0 || d.aiUsage > 0 || d.availability != null)
            })
            .sort((a, b) => (details[b.name]?.velocity || 0) - (details[a.name]?.velocity || 0))

        if (entries.length === 0) return null

        return (
            <div
                style={{
                    background: COLORS.cardBg,
                    border: `1px solid ${COLORS.border}`,
                    borderRadius: '8px',
                    padding: '12px 16px',
                    minWidth: '240px',
                }}
            >
                <div style={{ color: COLORS.text, fontWeight: '600', marginBottom: '10px', fontSize: '14px' }}>
                    {label}
                </div>
                {entries.map((entry, i) => {
                    const d = details[entry.name]
                    return (
                        <div
                            key={i}
                            style={{
                                marginBottom: i < entries.length - 1 ? '8px' : 0,
                                paddingBottom: i < entries.length - 1 ? '8px' : 0,
                                borderBottom: i < entries.length - 1 ? `1px solid ${COLORS.border}` : 'none',
                            }}
                        >
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '3px' }}>
                                <span style={{ color: entry.color, fontSize: '13px', fontWeight: '600' }}>
                                    {entry.name}
                                </span>
                            </div>
                            <div style={{ display: 'flex', gap: '12px', fontSize: '12px' }}>
                                <span style={{ color: COLORS.text }}>⚡ {d.velocity} pts</span>
                                <span style={{ color: COLORS.ai }}>🤖 {d.aiUsage}%</span>
                                <span style={{ color: COLORS.textMuted }}>📋 {d.issueCount}</span>
                                <span style={{ color: '#22d3ee' }}>
                                    📅 {d.availability != null ? d.availability + '%' : 'N/A'}
                                </span>
                            </div>
                        </div>
                    )
                })}
            </div>
        )
    }

    const trendArrow = (delta, suffix = '') => {
        if (delta > 0)
            return (
                <span style={{ color: COLORS.success, fontWeight: '600' }}>
                    ↑ +{delta}
                    {suffix}
                </span>
            )
        if (delta < 0)
            return (
                <span style={{ color: COLORS.danger, fontWeight: '600' }}>
                    ↓ {delta}
                    {suffix}
                </span>
            )
        return <span style={{ color: COLORS.textMuted, fontWeight: '600' }}>→ 0{suffix}</span>
    }

    return (
        <div
            style={{
                background: COLORS.cardBg,
                borderRadius: '16px',
                padding: '24px',
                border: `1px solid ${COLORS.border}`,
                marginBottom: '24px',
            }}
        >
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    flexWrap: 'wrap',
                    gap: '12px',
                    marginBottom: '16px',
                }}
            >
                <div>
                    <h3 style={{ color: COLORS.text, marginBottom: '4px', fontSize: '18px', fontWeight: '600' }}>
                        Developer Progress
                    </h3>
                    <p style={{ color: COLORS.textMuted, fontSize: '12px' }}>
                        Select developers to compare velocity &amp; AI adoption across sprints
                    </p>
                </div>
                {/* Chart mode toggle */}
                <div
                    style={{
                        display: 'flex',
                        background: COLORS.background,
                        borderRadius: '8px',
                        overflow: 'hidden',
                        border: `1px solid ${COLORS.border}`,
                    }}
                >
                    {[
                        { key: 'velocity', label: '⚡ Velocity', desc: 'Story Points' },
                        { key: 'aiUsage', label: '🤖 AI Usage', desc: 'AI %' },
                        { key: 'availability', label: '📅 Availability', desc: 'Avail %' },
                    ].map((mode) => (
                        <button
                            key={mode.key}
                            onClick={() => setChartMode(mode.key)}
                            style={{
                                background: chartMode === mode.key ? COLORS.primary + '30' : 'transparent',
                                border: 'none',
                                color: chartMode === mode.key ? COLORS.primary : COLORS.textMuted,
                                padding: '6px 14px',
                                fontSize: '12px',
                                fontWeight: chartMode === mode.key ? '600' : '400',
                                cursor: 'pointer',
                                transition: 'all 0.15s ease',
                            }}
                        >
                            {mode.label}
                        </button>
                    ))}
                </div>
            </div>

            {/* Developer picker */}
            <div style={{ marginBottom: '16px' }}>
                <input
                    type="text"
                    placeholder="Search developers..."
                    value={devSearch}
                    onChange={(e) => setDevSearch(e.target.value)}
                    style={{
                        background: COLORS.background,
                        border: `1px solid ${COLORS.border}`,
                        borderRadius: '8px',
                        padding: '8px 12px',
                        color: COLORS.text,
                        fontSize: '13px',
                        width: '260px',
                        marginBottom: '10px',
                        outline: 'none',
                    }}
                />
                <div
                    style={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: '6px',
                        maxHeight: '120px',
                        overflowY: 'auto',
                        paddingRight: '4px',
                    }}
                >
                    {filteredDevs.map((dev, idx) => {
                        const isSelected = selectedDevelopers.includes(dev.name)
                        const colorIdx = selectedDevelopers.indexOf(dev.name)
                        const chipColor = isSelected ? DEV_COLORS[colorIdx % DEV_COLORS.length] : COLORS.border
                        return (
                            <button
                                key={dev.name}
                                onClick={() => onToggleDeveloper(dev.name)}
                                style={{
                                    background: isSelected ? `${chipColor}25` : 'transparent',
                                    border: `1px solid ${chipColor}`,
                                    borderRadius: '20px',
                                    padding: '4px 12px',
                                    color: isSelected ? chipColor : COLORS.textMuted,
                                    fontSize: '12px',
                                    cursor: 'pointer',
                                    fontWeight: isSelected ? '600' : '400',
                                    transition: 'all 0.15s ease',
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                {dev.name
                                    .split(' ')
                                    .map((n) => n[0])
                                    .join('')}{' '}
                                {dev.name}
                            </button>
                        )
                    })}
                </div>
                {selectedDevelopers.length > 0 && (
                    <button
                        onClick={() => onToggleDeveloper(null)}
                        style={{
                            background: 'transparent',
                            border: 'none',
                            color: COLORS.textMuted,
                            fontSize: '12px',
                            cursor: 'pointer',
                            marginTop: '6px',
                            textDecoration: 'underline',
                        }}
                    >
                        Clear all
                    </button>
                )}
            </div>

            {/* Chart */}
            {selectedDevelopers.length === 0 ? (
                <div
                    style={{
                        height: '300px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: COLORS.textMuted,
                        fontSize: '14px',
                    }}
                >
                    Select one or more developers above to see their progress
                </div>
            ) : (
                <>
                    <ResponsiveContainer width="100%" height={370}>
                        <LineChart data={chartData} margin={{ top: 5, right: 5, left: 0, bottom: 18 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke={COLORS.border} />
                            <XAxis
                                dataKey="sprint"
                                stroke={COLORS.textMuted}
                                fontSize={12}
                                tick={<SprintTick data={chartData} />}
                            />
                            <YAxis
                                stroke={COLORS.textMuted}
                                fontSize={12}
                                domain={chartMode === 'aiUsage' ? [0, 100] : [0, 'auto']}
                                tickFormatter={chartMode === 'aiUsage' ? (v) => `${v}%` : undefined}
                                label={{
                                    value: chartMode === 'velocity' ? 'Story Points' : 'AI Usage %',
                                    angle: -90,
                                    position: 'insideLeft',
                                    fill: COLORS.textMuted,
                                    fontSize: 12,
                                }}
                            />
                            <Tooltip content={<DevProgressTooltip />} />
                            <Legend />
                            {selectedDevelopers.map((devName, i) => (
                                <Line
                                    key={devName}
                                    type="monotone"
                                    dataKey={devName}
                                    stroke={DEV_COLORS[i % DEV_COLORS.length]}
                                    strokeWidth={2}
                                    dot={{ r: 4, fill: DEV_COLORS[i % DEV_COLORS.length] }}
                                    activeDot={{ r: 6 }}
                                    name={devName}
                                    connectNulls
                                />
                            ))}
                        </LineChart>
                    </ResponsiveContainer>

                    {/* Developer stat summary cards */}
                    <div
                        style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                            gap: '12px',
                            marginTop: '20px',
                        }}
                    >
                        {devSummaries.map((dev) => (
                            <div
                                key={dev.name}
                                style={{
                                    background: COLORS.background,
                                    borderRadius: '12px',
                                    padding: '16px',
                                    border: `1px solid ${dev.color}40`,
                                    borderLeft: `3px solid ${dev.color}`,
                                }}
                            >
                                <div
                                    style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        marginBottom: '10px',
                                    }}
                                >
                                    <span style={{ color: dev.color, fontWeight: '600', fontSize: '14px' }}>
                                        {dev.name}
                                    </span>
                                    <span style={{ color: COLORS.textMuted, fontSize: '11px' }}>
                                        {dev.sprintCount} sprints
                                    </span>
                                </div>
                                {/* Metrics grid */}
                                <div
                                    style={{
                                        display: 'grid',
                                        gridTemplateColumns: '1fr 1fr',
                                        gap: '8px',
                                        fontSize: '12px',
                                    }}
                                >
                                    {/* Velocity */}
                                    <div>
                                        <div style={{ color: COLORS.textMuted, marginBottom: '2px' }}>Avg Velocity</div>
                                        <div style={{ color: COLORS.text, fontWeight: '600', fontSize: '16px' }}>
                                            {dev.avgVelocity}{' '}
                                            <span
                                                style={{ fontSize: '11px', fontWeight: '400', color: COLORS.textMuted }}
                                            >
                                                pts
                                            </span>
                                        </div>
                                        <div style={{ fontSize: '11px', marginTop: '2px' }}>
                                            Trend: {trendArrow(dev.velDelta, ' pts')}
                                        </div>
                                    </div>
                                    {/* AI Usage */}
                                    <div>
                                        <div style={{ color: COLORS.textMuted, marginBottom: '2px' }}>Avg AI Usage</div>
                                        <div style={{ color: COLORS.ai, fontWeight: '600', fontSize: '16px' }}>
                                            {dev.avgAi}%
                                        </div>
                                        <div style={{ fontSize: '11px', marginTop: '2px' }}>
                                            Trend: {trendArrow(dev.aiDelta, '%')}
                                        </div>
                                    </div>
                                    {/* Latest sprint */}
                                    <div>
                                        <div style={{ color: COLORS.textMuted, marginBottom: '2px' }}>
                                            Latest Sprint
                                        </div>
                                        <div style={{ color: COLORS.text, fontWeight: '600' }}>
                                            {dev.latestVelocity} pts
                                        </div>
                                    </div>
                                    {/* Latest AI */}
                                    <div>
                                        <div style={{ color: COLORS.textMuted, marginBottom: '2px' }}>Latest AI %</div>
                                        <div style={{ color: COLORS.ai, fontWeight: '600' }}>{dev.latestAi}%</div>
                                    </div>
                                    {/* Availability */}
                                    {dev.avgAvail != null && (
                                        <div>
                                            <div style={{ color: COLORS.textMuted, marginBottom: '2px' }}>
                                                Avg Availability
                                            </div>
                                            <div
                                                style={{
                                                    color:
                                                        dev.avgAvail >= 80
                                                            ? COLORS.success
                                                            : dev.avgAvail >= 50
                                                              ? COLORS.warning
                                                              : COLORS.danger,
                                                    fontWeight: '600',
                                                    fontSize: '16px',
                                                }}
                                            >
                                                {dev.avgAvail}%
                                            </div>
                                        </div>
                                    )}
                                    {dev.latestAvail != null && (
                                        <div>
                                            <div style={{ color: COLORS.textMuted, marginBottom: '2px' }}>
                                                Latest Avail
                                            </div>
                                            <div
                                                style={{
                                                    color:
                                                        dev.latestAvail >= 80
                                                            ? COLORS.success
                                                            : dev.latestAvail >= 50
                                                              ? COLORS.warning
                                                              : COLORS.danger,
                                                    fontWeight: '600',
                                                }}
                                            >
                                                {dev.latestAvail}%
                                            </div>
                                        </div>
                                    )}
                                </div>
                                {/* AI trend mini bar */}
                                <div style={{ marginTop: '10px' }}>
                                    <div
                                        style={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            fontSize: '11px',
                                            color: COLORS.textMuted,
                                            marginBottom: '3px',
                                        }}
                                    >
                                        <span>AI Adoption</span>
                                        <span>{dev.latestAi}%</span>
                                    </div>
                                    <div
                                        style={{
                                            height: '4px',
                                            background: `${COLORS.ai}20`,
                                            borderRadius: '2px',
                                            overflow: 'hidden',
                                        }}
                                    >
                                        <div
                                            style={{
                                                height: '100%',
                                                width: `${Math.min(dev.latestAi, 100)}%`,
                                                background: COLORS.ai,
                                                borderRadius: '2px',
                                                transition: 'width 0.3s ease',
                                            }}
                                        />
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </>
            )}
        </div>
    )
}

// Sprint History Table
const SprintHistoryTable = ({ data }) => (
    <div
        style={{
            background: COLORS.cardBg,
            borderRadius: '16px',
            padding: '24px',
            border: `1px solid ${COLORS.border}`,
            overflowX: 'auto',
        }}
    >
        <h3 style={{ color: COLORS.text, marginBottom: '20px', fontSize: '18px', fontWeight: '600' }}>
            Sprint History
        </h3>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
                <tr style={{ borderBottom: `1px solid ${COLORS.border}` }}>
                    <th
                        style={{
                            textAlign: 'left',
                            padding: '12px 8px',
                            color: COLORS.textMuted,
                            fontSize: '13px',
                            fontWeight: '600',
                        }}
                    >
                        Sprint
                    </th>
                    <th
                        style={{
                            textAlign: 'left',
                            padding: '12px 8px',
                            color: COLORS.textMuted,
                            fontSize: '13px',
                            fontWeight: '600',
                        }}
                    >
                        Dates
                    </th>
                    <th
                        style={{
                            textAlign: 'right',
                            padding: '12px 8px',
                            color: COLORS.textMuted,
                            fontSize: '13px',
                            fontWeight: '600',
                        }}
                    >
                        Total
                    </th>
                    <th
                        style={{
                            textAlign: 'right',
                            padding: '12px 8px',
                            color: COLORS.textMuted,
                            fontSize: '13px',
                            fontWeight: '600',
                        }}
                    >
                        AI
                    </th>
                    <th
                        style={{
                            textAlign: 'right',
                            padding: '12px 8px',
                            color: COLORS.textMuted,
                            fontSize: '13px',
                            fontWeight: '600',
                        }}
                    >
                        Stories
                    </th>
                    <th
                        style={{
                            textAlign: 'right',
                            padding: '12px 8px',
                            color: COLORS.textMuted,
                            fontSize: '13px',
                            fontWeight: '600',
                        }}
                    >
                        AI %
                    </th>
                    <th
                        style={{
                            textAlign: 'right',
                            padding: '12px 8px',
                            color: COLORS.textMuted,
                            fontSize: '13px',
                            fontWeight: '600',
                        }}
                    >
                        BP
                    </th>
                    <th
                        style={{
                            textAlign: 'right',
                            padding: '12px 8px',
                            color: COLORS.textMuted,
                            fontSize: '13px',
                            fontWeight: '600',
                        }}
                    >
                        SP
                    </th>
                    <th
                        style={{
                            textAlign: 'right',
                            padding: '12px 8px',
                            color: COLORS.textMuted,
                            fontSize: '13px',
                            fontWeight: '600',
                        }}
                    >
                        FP
                    </th>
                    <th
                        style={{
                            textAlign: 'right',
                            padding: '12px 8px',
                            color: COLORS.textMuted,
                            fontSize: '13px',
                            fontWeight: '600',
                        }}
                    >
                        Avail
                    </th>
                </tr>
            </thead>
            <tbody>
                {data.slice(0, 10).map((sprint, index) => {
                    const avails = (sprint.contributors || []).map((c) => c.availability).filter((a) => a != null)
                    const avgAvail =
                        avails.length > 0 ? Math.round((avails.reduce((s, v) => s + v, 0) / avails.length) * 100) : null
                    return (
                        <tr key={sprint.id || index} style={{ borderBottom: `1px solid ${COLORS.border}` }}>
                            <td style={{ padding: '12px 8px', color: COLORS.text, fontSize: '14px' }}>{sprint.name}</td>
                            <td
                                style={{
                                    padding: '12px 8px',
                                    color: COLORS.textMuted,
                                    fontSize: '13px',
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                {fmtDateRange(sprint.startDate, sprint.endDate)}
                            </td>
                            <td
                                style={{
                                    padding: '12px 8px',
                                    color: COLORS.text,
                                    fontSize: '14px',
                                    textAlign: 'right',
                                    fontWeight: '600',
                                }}
                            >
                                {sprint.totalPoints}
                            </td>
                            <td style={{ padding: '12px 8px', color: COLORS.ai, fontSize: '14px', textAlign: 'right' }}>
                                {sprint.aiPoints}
                            </td>
                            <td
                                style={{
                                    padding: '12px 8px',
                                    color: COLORS.textMuted,
                                    fontSize: '14px',
                                    textAlign: 'right',
                                }}
                            >
                                {sprint.completedStories}
                            </td>
                            <td style={{ padding: '12px 8px', color: COLORS.ai, fontSize: '14px', textAlign: 'right' }}>
                                {sprint.aiAdoption}%
                            </td>
                            <td
                                style={{
                                    padding: '12px 8px',
                                    color: COLORS.textMuted,
                                    fontSize: '14px',
                                    textAlign: 'right',
                                }}
                            >
                                {sprint.byProject?.BP || 0}
                            </td>
                            <td
                                style={{
                                    padding: '12px 8px',
                                    color: COLORS.textMuted,
                                    fontSize: '14px',
                                    textAlign: 'right',
                                }}
                            >
                                {sprint.byProject?.SP || 0}
                            </td>
                            <td
                                style={{
                                    padding: '12px 8px',
                                    color: COLORS.textMuted,
                                    fontSize: '14px',
                                    textAlign: 'right',
                                }}
                            >
                                {sprint.byProject?.FP || 0}
                            </td>
                            <td
                                style={{
                                    padding: '12px 8px',
                                    fontSize: '14px',
                                    textAlign: 'right',
                                    fontWeight: '600',
                                    color:
                                        avgAvail == null
                                            ? COLORS.textMuted
                                            : avgAvail >= 80
                                              ? COLORS.success
                                              : avgAvail >= 50
                                                ? COLORS.warning
                                                : COLORS.danger,
                                }}
                            >
                                {avgAvail != null ? `${avgAvail}%` : '—'}
                            </td>
                        </tr>
                    )
                })}
            </tbody>
        </table>
    </div>
)

// Exclude Users Panel — slide-in drawer for managing excluded contributors
const ExcludeUsersPanel = ({ allUsers, excludedUserIds, onToggle, onClose, canEdit = true, error = null }) => {
    const [search, setSearch] = useState('')

    const filteredUsers = search
        ? allUsers.filter((u) => u.name.toLowerCase().includes(search.toLowerCase()))
        : allUsers

    const excludedCount = excludedUserIds.length

    return (
        <div
            style={{
                position: 'fixed',
                top: 0,
                right: 0,
                bottom: 0,
                width: '380px',
                background: COLORS.background,
                borderLeft: `1px solid ${COLORS.border}`,
                zIndex: 1000,
                display: 'flex',
                flexDirection: 'column',
                boxShadow: '-4px 0 24px rgba(0,0,0,0.5)',
            }}
        >
            {/* Header */}
            <div
                style={{
                    padding: '20px 24px',
                    borderBottom: `1px solid ${COLORS.border}`,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                }}
            >
                <div>
                    <h3 style={{ color: COLORS.text, fontSize: '18px', fontWeight: '600', margin: 0 }}>
                        Manage Contributors
                    </h3>
                    <p style={{ color: COLORS.textMuted, fontSize: '12px', margin: '4px 0 0' }}>
                        {excludedCount > 0
                            ? `${excludedCount} user${excludedCount !== 1 ? 's' : ''} excluded from metrics`
                            : 'Toggle users to exclude from all metrics'}
                    </p>
                </div>
                <button
                    onClick={onClose}
                    style={{
                        background: 'transparent',
                        border: `1px solid ${COLORS.border}`,
                        color: COLORS.textMuted,
                        borderRadius: '8px',
                        padding: '6px 12px',
                        cursor: 'pointer',
                        fontSize: '14px',
                    }}
                >
                    ✕
                </button>
            </div>

            {/* Read-only note / error banner */}
            {(!canEdit || error) && (
                <div
                    style={{
                        margin: '12px 24px 0',
                        padding: '10px 12px',
                        borderRadius: '8px',
                        background: `${COLORS.warning}15`,
                        border: `1px solid ${COLORS.warning}40`,
                        color: COLORS.warning,
                        fontSize: '12px',
                    }}
                >
                    {error || 'Editing the excluded list requires admin or ops access.'}
                </div>
            )}

            {/* Search */}
            <div style={{ padding: '16px 24px 8px' }}>
                <input
                    type="text"
                    placeholder="Search contributors..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    style={{
                        width: '100%',
                        background: COLORS.cardBg,
                        border: `1px solid ${COLORS.border}`,
                        borderRadius: '8px',
                        padding: '10px 12px',
                        color: COLORS.text,
                        fontSize: '13px',
                        outline: 'none',
                        boxSizing: 'border-box',
                    }}
                />
            </div>

            {/* User list */}
            <div
                style={{
                    flex: 1,
                    overflowY: 'auto',
                    padding: '8px 24px 24px',
                }}
            >
                {filteredUsers.map((user) => {
                    const isExcluded = excludedUserIds.includes(user.id)
                    return (
                        <div
                            key={user.id}
                            onClick={() => canEdit && onToggle(user.id)}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                padding: '10px 14px',
                                borderRadius: '10px',
                                marginBottom: '4px',
                                cursor: canEdit ? 'pointer' : 'not-allowed',
                                opacity: canEdit ? 1 : 0.6,
                                background: isExcluded ? `${COLORS.danger}15` : COLORS.cardBg,
                                border: `1px solid ${isExcluded ? COLORS.danger + '40' : COLORS.border}`,
                                transition: 'all 0.15s ease',
                            }}
                        >
                            <span
                                style={{
                                    color: isExcluded ? COLORS.danger : COLORS.text,
                                    fontSize: '13px',
                                    fontWeight: '500',
                                    textDecoration: isExcluded ? 'line-through' : 'none',
                                    opacity: isExcluded ? 0.7 : 1,
                                }}
                            >
                                {user.name}
                            </span>
                            {/* Toggle switch */}
                            <div
                                style={{
                                    width: '36px',
                                    height: '20px',
                                    borderRadius: '10px',
                                    background: isExcluded ? COLORS.danger : COLORS.success,
                                    position: 'relative',
                                    transition: 'background 0.2s ease',
                                    flexShrink: 0,
                                }}
                            >
                                <div
                                    style={{
                                        width: '16px',
                                        height: '16px',
                                        borderRadius: '50%',
                                        background: '#fff',
                                        position: 'absolute',
                                        top: '2px',
                                        left: isExcluded ? '2px' : '18px',
                                        transition: 'left 0.2s ease',
                                    }}
                                />
                            </div>
                        </div>
                    )
                })}
            </div>
        </div>
    )
}

// ── People Tab ───────────────────────────────────────────────
const ROLE_CYCLE = [null, 'dev', 'ux', 'tech_lead', 'manager']
const ROLE_META = {
    dev: { label: 'Dev', color: '#3b82f6' },
    ux: { label: 'UX', color: '#a855f7' },
    tech_lead: { label: 'TL', color: '#f59e0b' },
    manager: { label: 'Mgr', color: '#ef4444' },
}

function RoleBadge({ role, onClick, saving }) {
    const meta = role ? ROLE_META[role] : null
    return (
        <button
            onClick={onClick}
            title={saving ? 'Saving…' : 'Click to change role'}
            style={{
                background: meta ? `${meta.color}22` : 'transparent',
                border: `1px solid ${meta ? meta.color : COLORS.border}`,
                color: meta ? meta.color : COLORS.textMuted,
                borderRadius: '4px',
                padding: '2px 8px',
                fontSize: '11px',
                fontWeight: '600',
                cursor: 'pointer',
                minWidth: '36px',
                opacity: saving ? 0.5 : 1,
                transition: 'all 0.15s',
            }}
        >
            {saving ? '…' : meta ? meta.label : '—'}
        </button>
    )
}

function WorkBreakdownCell({ bucket, color }) {
    if (!bucket || bucket.count === 0) return <span style={{ color: COLORS.textMuted }}>—</span>
    const epicEntries = Object.entries(bucket.epics || {}).sort((a, b) => b[1] - a[1])
    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
            <div style={{ fontSize: '12px', fontWeight: '600', color }}>
                {bucket.pts}pt · {bucket.count} {bucket.count === 1 ? 'story' : 'stories'}
            </div>
            {epicEntries.map(([name, pts]) => (
                <span
                    key={name}
                    style={{
                        display: 'inline-block',
                        background: `${color}18`,
                        border: `1px solid ${color}40`,
                        borderRadius: '3px',
                        padding: '1px 6px',
                        fontSize: '10px',
                        color: COLORS.textMuted,
                        maxWidth: '180px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                    }}
                    title={`${name} — ${pts}pt`}
                >
                    {name} · {pts}pt
                </span>
            ))}
        </div>
    )
}

function PeopleTab({ data, roles, onRoleChange, roleSaving, displaySprint, selectedPod }) {
    const [roleFilter, setRoleFilter] = useState('all')

    const contributors = (displaySprint?.contributors || []).filter(
        (c) => !selectedPod || c.projects?.includes(selectedPod)
    )

    // Apply role filter
    const filtered =
        roleFilter === 'all'
            ? contributors
            : roleFilter === 'unassigned'
              ? contributors.filter((c) => !roles[c.id])
              : contributors.filter((c) => roles[c.id] === roleFilter)

    // Count roles across all contributors in this sprint (pod-filtered)
    const roleCounts = {}
    contributors.forEach((c) => {
        const r = roles[c.id] || 'unassigned'
        roleCounts[r] = (roleCounts[r] || 0) + 1
    })

    const cycleRole = (accountId) => {
        const current = roles[accountId] || null
        const idx = ROLE_CYCLE.indexOf(current)
        const next = ROLE_CYCLE[(idx + 1) % ROLE_CYCLE.length]
        onRoleChange(accountId, next)
    }

    const thStyle = {
        textAlign: 'left',
        padding: '8px 12px',
        fontSize: '11px',
        fontWeight: '600',
        color: COLORS.textMuted,
        borderBottom: `1px solid ${COLORS.border}`,
        whiteSpace: 'nowrap',
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
    }
    const tdStyle = {
        padding: '10px 12px',
        borderBottom: `1px solid ${COLORS.border}18`,
        verticalAlign: 'top',
    }

    return (
        <div>
            {/* Controls row */}
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '16px',
                    marginBottom: '20px',
                    flexWrap: 'wrap',
                }}
            >
                {/* Role filter chips */}
                <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                    {[
                        { key: 'all', label: `All (${contributors.length})` },
                        ...ROLE_CYCLE.slice(1).map((r) => ({
                            key: r,
                            label: `${ROLE_META[r].label} (${roleCounts[r] || 0})`,
                            color: ROLE_META[r].color,
                        })),
                        { key: 'unassigned', label: `Unassigned (${roleCounts.unassigned || 0})` },
                    ].map(({ key, label, color }) => {
                        const active = roleFilter === key
                        const activeColor = color || COLORS.primary
                        return (
                            <button
                                key={key}
                                onClick={() => setRoleFilter(key)}
                                style={{
                                    background: active ? `${activeColor}22` : 'transparent',
                                    border: `1px solid ${active ? activeColor : COLORS.border}`,
                                    color: active ? activeColor : COLORS.textMuted,
                                    borderRadius: '6px',
                                    padding: '4px 12px',
                                    fontSize: '12px',
                                    fontWeight: active ? '600' : '400',
                                    cursor: 'pointer',
                                    transition: 'all 0.15s',
                                }}
                            >
                                {label}
                            </button>
                        )
                    })}
                </div>

                <div style={{ marginLeft: 'auto', fontSize: '11px', color: COLORS.textMuted }}>
                    Click a role badge to cycle: — → Dev → UX → TL → Mgr → —
                </div>
            </div>

            {/* Table */}
            <div
                style={{
                    background: COLORS.cardBg,
                    borderRadius: '12px',
                    border: `1px solid ${COLORS.border}`,
                    overflow: 'hidden',
                }}
            >
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                        <tr style={{ background: `${COLORS.background}80` }}>
                            <th style={thStyle}>Developer</th>
                            <th style={thStyle}>Role</th>
                            <th style={thStyle}>Pod</th>
                            <th style={{ ...thStyle, textAlign: 'right' }}>Pts</th>
                            <th style={thStyle}>Feature</th>
                            <th style={thStyle}>Support</th>
                            <th style={thStyle}>Bug</th>
                            <th style={thStyle}>Tech Debt</th>
                            <th style={thStyle}>Other</th>
                            <th style={{ ...thStyle, textAlign: 'right' }}>AI%</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filtered.length === 0 && (
                            <tr>
                                <td
                                    colSpan={10}
                                    style={{
                                        ...tdStyle,
                                        color: COLORS.textMuted,
                                        textAlign: 'center',
                                        padding: '24px',
                                    }}
                                >
                                    No contributors match this filter
                                </td>
                            </tr>
                        )}
                        {filtered.map((c) => {
                            const wb = c.workBreakdown || {}
                            return (
                                <tr
                                    key={c.id}
                                    style={{ transition: 'background 0.1s' }}
                                    onMouseEnter={(e) => (e.currentTarget.style.background = `${COLORS.border}18`)}
                                    onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                                >
                                    <td style={tdStyle}>
                                        <span style={{ fontWeight: '600', fontSize: '13px', color: COLORS.text }}>
                                            {c.name}
                                        </span>
                                    </td>
                                    <td style={tdStyle}>
                                        <RoleBadge
                                            role={roles[c.id] || null}
                                            onClick={() => cycleRole(c.id)}
                                            saving={roleSaving === c.id}
                                        />
                                    </td>
                                    <td style={tdStyle}>
                                        <span style={{ fontSize: '12px', color: COLORS.textMuted }}>
                                            {c.projects?.join(', ') || '—'}
                                        </span>
                                    </td>
                                    <td
                                        style={{
                                            ...tdStyle,
                                            textAlign: 'right',
                                            fontWeight: '700',
                                            fontSize: '14px',
                                            color: COLORS.primary,
                                        }}
                                    >
                                        {c.currentVelocity}
                                    </td>
                                    <td style={tdStyle}>
                                        <WorkBreakdownCell bucket={wb.feature} color={COLORS.primary} />
                                    </td>
                                    <td style={tdStyle}>
                                        <WorkBreakdownCell bucket={wb.support} color={COLORS.warning} />
                                    </td>
                                    <td style={tdStyle}>
                                        <WorkBreakdownCell bucket={wb.bug} color={COLORS.danger} />
                                    </td>
                                    <td style={tdStyle}>
                                        <WorkBreakdownCell bucket={wb.tech_debt} color="#a78bfa" />
                                    </td>
                                    <td style={tdStyle}>
                                        <WorkBreakdownCell bucket={wb.other} color={COLORS.textMuted} />
                                    </td>
                                    <td style={{ ...tdStyle, textAlign: 'right', fontSize: '12px', color: COLORS.ai }}>
                                        {c.aiUsage}%
                                    </td>
                                </tr>
                            )
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    )
}

// ── Categories Tab ───────────────────────────────────────────────
const CATEGORY_META = {
    feature: { label: 'Feature', color: COLORS.primary, icon: '🔷' },
    bug: { label: 'Bug', color: COLORS.danger, icon: '🐛' },
    support: { label: 'Support', color: COLORS.warning, icon: '🛠️' },
    tech_debt: { label: 'Tech Debt', color: '#a78bfa', icon: '🔧' },
    other: { label: 'Other', color: COLORS.textMuted, icon: '📦' },
}
const CATS = ['feature', 'bug', 'support', 'tech_debt', 'other']

function CategoriesTab({ roles, displaySprint, selectedPod }) {
    const contributors = (displaySprint?.contributors || []).filter(
        (c) => !selectedPod || c.projects?.includes(selectedPod)
    )

    // Invert contributor → category view
    const categoryData = Object.fromEntries(
        CATS.map((cat) => [cat, { totalPts: 0, totalCount: 0, epics: {}, devs: [] }])
    )

    contributors.forEach((c) => {
        const wb = c.workBreakdown || {}
        CATS.forEach((cat) => {
            const bucket = wb[cat]
            if (!bucket || bucket.count === 0) return
            categoryData[cat].totalPts += bucket.pts
            categoryData[cat].totalCount += bucket.count
            Object.entries(bucket.epics || {}).forEach(([name, pts]) => {
                categoryData[cat].epics[name] = (categoryData[cat].epics[name] || 0) + pts
            })
            categoryData[cat].devs.push({
                name: c.name,
                id: c.id,
                pts: bucket.pts,
                count: bucket.count,
                projects: c.projects,
                role: roles?.[c.id] || null,
            })
        })
    })

    CATS.forEach((cat) => categoryData[cat].devs.sort((a, b) => b.pts - a.pts))

    const totalPts = CATS.reduce((s, cat) => s + categoryData[cat].totalPts, 0)
    const totalCount = CATS.reduce((s, cat) => s + categoryData[cat].totalCount, 0)
    const totalDevs = contributors.length

    // Collapsed state per category — default all collapsed
    const [collapsed, setCollapsed] = useState(() => Object.fromEntries(CATS.map((cat) => [cat, true])))
    const toggleCollapsed = (cat) => setCollapsed((prev) => ({ ...prev, [cat]: !prev[cat] }))

    // AI epic-classification review panel — collapsed by default.
    const [showEpicAI, setShowEpicAI] = useState(false)
    // Count of AI-suggested epics not yet approved (across all sprints) — the daily
    // job keeps this queue current; the badge nudges the reviewer to clear it.
    const [pendingEpics, setPendingEpics] = useState(null)
    const refreshPendingEpics = useCallback(async () => {
        try {
            const r = await fetch('/api/pulse/classify-epic/summary')
            if (!r.ok) return
            const j = await r.json()
            const pending = (j.rows || [])
                .filter((row) => row.status === 'suggested')
                .reduce((s, row) => s + Number(row.n || 0), 0)
            setPendingEpics(pending)
        } catch (_) {
            /* leave as-is on error */
        }
    }, [])
    useEffect(() => {
        refreshPendingEpics()
    }, [refreshPendingEpics])

    const thStyle = {
        padding: '8px 12px',
        fontSize: '12px',
        fontWeight: '600',
        color: COLORS.textMuted,
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
        whiteSpace: 'nowrap',
    }

    return (
        <div>
            {/* AI epic classification review — improves Work Mix accuracy by reading epic descriptions */}
            <div style={{ marginBottom: '20px', border: `1px solid ${COLORS.border}`, borderRadius: '10px', overflow: 'hidden' }}>
                <button
                    onClick={() => setShowEpicAI((v) => !v)}
                    style={{
                        width: '100%', display: 'flex', alignItems: 'center', gap: '8px',
                        background: showEpicAI ? `${COLORS.primary}18` : 'transparent',
                        border: 'none', color: COLORS.text, padding: '12px 16px',
                        fontSize: '14px', fontWeight: 600, cursor: 'pointer', textAlign: 'left',
                    }}
                >
                    <span>{showEpicAI ? '▾' : '▸'}</span>
                    <span>✨ Classify epics with AI</span>
                    {pendingEpics > 0 && (
                        <span
                            title={`${pendingEpics} AI-suggested epic${pendingEpics === 1 ? '' : 's'} awaiting your approval`}
                            style={{
                                background: COLORS.warning, color: '#0f172a', fontWeight: 700,
                                fontSize: '11px', borderRadius: '999px', padding: '2px 9px',
                            }}
                        >
                            {pendingEpics} awaiting approval
                        </span>
                    )}
                    <span style={{ color: COLORS.textMuted, fontSize: '12px', fontWeight: 400 }}>
                        — review per-sprint suggestions to refine these categories
                        {displaySprint?.sprintNum ? ` (Sprint ${displaySprint.sprintNum})` : ''}
                    </span>
                </button>
                {showEpicAI && (
                    <div style={{ padding: '0 16px 16px' }}>
                        <EpicClassification
                            defaultSprintNum={displaySprint?.sprintNum}
                            onApproved={refreshPendingEpics}
                        />
                    </div>
                )}
            </div>

            {/* Summary cards */}
            <div
                style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(5, 1fr)',
                    gap: '12px',
                    marginBottom: '24px',
                }}
            >
                {CATS.map((cat) => {
                    const meta = CATEGORY_META[cat]
                    const d = categoryData[cat]
                    const ptsPct = totalPts > 0 ? Math.round((d.totalPts / totalPts) * 100) : 0
                    const storiesPct = totalCount > 0 ? Math.round((d.totalCount / totalCount) * 100) : 0
                    const devsPct = totalDevs > 0 ? Math.round((d.devs.length / totalDevs) * 100) : 0
                    return (
                        <div
                            key={cat}
                            style={{
                                background: COLORS.cardBg,
                                border: `1px solid ${COLORS.border}`,
                                borderTop: `3px solid ${meta.color}`,
                                borderRadius: '10px',
                                padding: '16px',
                            }}
                        >
                            <div
                                style={{
                                    fontSize: '11px',
                                    color: COLORS.textMuted,
                                    fontWeight: '600',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.05em',
                                    marginBottom: '8px',
                                }}
                            >
                                {meta.icon} {meta.label}
                            </div>
                            <div style={{ fontSize: '26px', fontWeight: '700', color: meta.color, lineHeight: 1 }}>
                                {d.totalPts}
                                <span style={{ fontSize: '13px', fontWeight: '400', color: COLORS.textMuted, marginLeft: '4px' }}>
                                    pts
                                </span>
                                <span style={{ fontSize: '13px', fontWeight: '500', color: meta.color, marginLeft: '8px' }}>
                                    {ptsPct}%
                                </span>
                            </div>
                            <div style={{ marginTop: '10px', display: 'flex', flexDirection: 'column', gap: '5px' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px' }}>
                                    <span style={{ color: COLORS.textMuted }}>
                                        {d.totalCount} {d.totalCount === 1 ? 'story' : 'stories'}
                                    </span>
                                    <span style={{ color: COLORS.textMuted, fontWeight: '500' }}>{storiesPct}%</span>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px' }}>
                                    <span style={{ color: meta.color, fontWeight: '600' }}>
                                        {d.devs.length} {d.devs.length === 1 ? 'dev' : 'devs'}
                                    </span>
                                    <span style={{ color: meta.color, fontWeight: '600' }}>{devsPct}%</span>
                                </div>
                            </div>
                            <div
                                style={{ marginTop: '10px', height: '4px', background: COLORS.border, borderRadius: '2px' }}
                            >
                                <div
                                    style={{ height: '100%', width: `${ptsPct}%`, background: meta.color, borderRadius: '2px' }}
                                />
                            </div>
                        </div>
                    )
                })}
            </div>

            {/* Category panels — 2-column grid */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                {CATS.map((cat) => {
                    const meta = CATEGORY_META[cat]
                    const d = categoryData[cat]
                    const isCollapsed = collapsed[cat]
                    const topEpics = Object.entries(d.epics)
                        .sort((a, b) => b[1] - a[1])
                        .slice(0, 6)

                    return (
                        <div
                            key={cat}
                            style={{
                                background: COLORS.cardBg,
                                border: `1px solid ${COLORS.border}`,
                                borderRadius: '12px',
                                overflow: 'hidden',
                            }}
                        >
                            {/* Panel header — clickable to toggle */}
                            <div
                                onClick={() => toggleCollapsed(cat)}
                                style={{
                                    background: `${meta.color}12`,
                                    borderBottom: isCollapsed ? 'none' : `1px solid ${meta.color}30`,
                                    padding: '12px 16px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    cursor: 'pointer',
                                    userSelect: 'none',
                                }}
                            >
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <span style={{ fontSize: '16px' }}>{meta.icon}</span>
                                    <span style={{ fontWeight: '700', color: meta.color, fontSize: '14px' }}>
                                        {meta.label}
                                    </span>
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                                    <div style={{ display: 'flex', gap: '16px', fontSize: '12px', color: COLORS.textMuted }}>
                                        <span>
                                            <span style={{ color: meta.color, fontWeight: '700' }}>{d.devs.length}</span> devs
                                        </span>
                                        <span>
                                            <span style={{ color: COLORS.text, fontWeight: '600' }}>{d.totalPts}</span> pts
                                        </span>
                                        <span>
                                            <span style={{ color: COLORS.text, fontWeight: '600' }}>{d.totalCount}</span>{' '}
                                            stories
                                        </span>
                                    </div>
                                    <span style={{ color: COLORS.textMuted, fontSize: '12px', lineHeight: 1 }}>
                                        {isCollapsed ? '▸' : '▾'}
                                    </span>
                                </div>
                            </div>

                            {/* Epics + dev list — hidden when collapsed */}
                            {!isCollapsed && topEpics.length > 0 && (
                                <div
                                    style={{
                                        borderBottom: `1px solid ${COLORS.border}`,
                                        padding: '12px 16px',
                                        background: `${COLORS.background}60`,
                                    }}
                                >
                                    <div
                                        style={{
                                            fontSize: '12px',
                                            color: COLORS.textMuted,
                                            fontWeight: '600',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.05em',
                                            marginBottom: '8px',
                                        }}
                                    >
                                        Epics
                                    </div>
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                                        {topEpics.map(([name, pts]) => (
                                            <span
                                                key={name}
                                                title={`${name} — ${pts}pt`}
                                                style={{
                                                    background: `${meta.color}15`,
                                                    border: `1px solid ${meta.color}40`,
                                                    borderRadius: '4px',
                                                    padding: '4px 10px',
                                                    fontSize: '12px',
                                                    color: COLORS.text,
                                                    maxWidth: '260px',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap',
                                                }}
                                            >
                                                {name}
                                                <span style={{ color: COLORS.textMuted, marginLeft: '6px' }}>
                                                    {pts}pt
                                                </span>
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Dev table */}
                            {!isCollapsed && (d.devs.length === 0 ? (
                                <div
                                    style={{
                                        padding: '24px 16px',
                                        color: COLORS.textMuted,
                                        fontSize: '13px',
                                        textAlign: 'center',
                                    }}
                                >
                                    No work in this category this sprint
                                </div>
                            ) : (
                                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                    <thead>
                                        <tr style={{ background: `${COLORS.background}60` }}>
                                            <th style={{ ...thStyle, textAlign: 'left', paddingLeft: '16px' }}>
                                                Developer
                                            </th>
                                            <th style={{ ...thStyle, textAlign: 'right' }}>Pts</th>
                                            <th style={{ ...thStyle, textAlign: 'right' }}>Stories</th>
                                            <th style={{ ...thStyle, textAlign: 'right', paddingRight: '16px' }}>
                                                Share
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {d.devs.map((dev) => {
                                            const share =
                                                d.totalPts > 0 ? Math.round((dev.pts / d.totalPts) * 100) : 0
                                            const roleMeta = dev.role ? ROLE_META[dev.role] : null
                                            return (
                                                <tr
                                                    key={dev.id}
                                                    style={{ borderTop: `1px solid ${COLORS.border}18` }}
                                                    onMouseEnter={(e) =>
                                                        (e.currentTarget.style.background = `${COLORS.border}18`)
                                                    }
                                                    onMouseLeave={(e) =>
                                                        (e.currentTarget.style.background = 'transparent')
                                                    }
                                                >
                                                    <td style={{ padding: '9px 12px 9px 16px' }}>
                                                        <div
                                                            style={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                gap: '7px',
                                                            }}
                                                        >
                                                            <span
                                                                style={{
                                                                    fontWeight: '600',
                                                                    fontSize: '14px',
                                                                    color: COLORS.text,
                                                                }}
                                                            >
                                                                {dev.name}
                                                            </span>
                                                            {roleMeta && (
                                                                <span
                                                                    style={{
                                                                        fontSize: '11px',
                                                                        fontWeight: '600',
                                                                        color: roleMeta.color,
                                                                        background: `${roleMeta.color}22`,
                                                                        border: `1px solid ${roleMeta.color}40`,
                                                                        borderRadius: '3px',
                                                                        padding: '1px 6px',
                                                                    }}
                                                                >
                                                                    {roleMeta.label}
                                                                </span>
                                                            )}
                                                        </div>
                                                        <div style={{ fontSize: '12px', color: COLORS.textMuted }}>
                                                            {dev.projects?.join(', ')}
                                                        </div>
                                                    </td>
                                                    <td
                                                        style={{
                                                            padding: '9px 12px',
                                                            textAlign: 'right',
                                                            fontWeight: '700',
                                                            fontSize: '14px',
                                                            color: meta.color,
                                                        }}
                                                    >
                                                        {dev.pts}
                                                    </td>
                                                    <td
                                                        style={{
                                                            padding: '9px 12px',
                                                            textAlign: 'right',
                                                            fontSize: '13px',
                                                            color: COLORS.textMuted,
                                                        }}
                                                    >
                                                        {dev.count}
                                                    </td>
                                                    <td style={{ padding: '9px 16px 9px 12px', textAlign: 'right' }}>
                                                        <div
                                                            style={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                gap: '6px',
                                                                justifyContent: 'flex-end',
                                                            }}
                                                        >
                                                            <div
                                                                style={{
                                                                    width: '52px',
                                                                    height: '5px',
                                                                    background: COLORS.border,
                                                                    borderRadius: '3px',
                                                                    overflow: 'hidden',
                                                                }}
                                                            >
                                                                <div
                                                                    style={{
                                                                        height: '100%',
                                                                        width: `${share}%`,
                                                                        background: meta.color,
                                                                        borderRadius: '3px',
                                                                    }}
                                                                />
                                                            </div>
                                                            <span
                                                                style={{
                                                                    fontSize: '12px',
                                                                    color: COLORS.textMuted,
                                                                    minWidth: '32px',
                                                                }}
                                                            >
                                                                {share}%
                                                            </span>
                                                        </div>
                                                    </td>
                                                </tr>
                                            )
                                        })}
                                    </tbody>
                                </table>
                            ))}
                        </div>
                    )
                })}
            </div>
        </div>
    )
}

// ── Pillars Tab ───────────────────────────────────────────────
const PILLARS = ['Liquidity', 'Seller Automation', 'GTM', 'Other']
const PILLAR_META = {
    Liquidity: { label: 'Liquidity', color: '#38bdf8', icon: '💧' },
    'Seller Automation': { label: 'Seller Automation', color: '#34d399', icon: '⚙️' },
    GTM: { label: 'GTM', color: '#f59e0b', icon: '🚀' },
    Other: { label: 'Other', color: COLORS.textMuted, icon: '📦' },
}

function PillarsTab({ roles, displaySprint, selectedPod }) {
    const contributors = (displaySprint?.contributors || []).filter(
        (c) => !selectedPod || c.projects?.includes(selectedPod)
    )

    const pillarData = Object.fromEntries(
        PILLARS.map((p) => [p, { totalPts: 0, totalCount: 0, epics: {}, devs: [] }])
    )

    contributors.forEach((c) => {
        const pb = c.pillarBreakdown || {}
        PILLARS.forEach((p) => {
            const bucket = pb[p]
            if (!bucket || bucket.count === 0) return
            pillarData[p].totalPts += bucket.pts
            pillarData[p].totalCount += bucket.count
            Object.entries(bucket.epics || {}).forEach(([name, pts]) => {
                pillarData[p].epics[name] = (pillarData[p].epics[name] || 0) + pts
            })
            pillarData[p].devs.push({
                name: c.name,
                id: c.id,
                pts: bucket.pts,
                count: bucket.count,
                projects: c.projects,
                role: roles?.[c.id] || null,
            })
        })
    })

    PILLARS.forEach((p) => pillarData[p].devs.sort((a, b) => b.pts - a.pts))

    // Use sprint total for percentages (not sum of pillars, which inflates for multi-pillar stories)
    const totalPts = contributors.reduce((s, c) => s + (c.currentVelocity || 0), 0)
    const totalCount = contributors.reduce((s, c) => s + (c.issueCount || 0), 0)
    const totalDevs = contributors.length

    const [collapsed, setCollapsed] = useState(() => Object.fromEntries(PILLARS.map((p) => [p, true])))
    const toggleCollapsed = (p) => setCollapsed((prev) => ({ ...prev, [p]: !prev[p] }))

    const thStyle = {
        padding: '8px 12px',
        fontSize: '12px',
        fontWeight: '600',
        color: COLORS.textMuted,
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
        whiteSpace: 'nowrap',
    }

    const hasAnyData = PILLARS.some((p) => pillarData[p].totalPts > 0)

    return (
        <div>
            {!hasAnyData && (
                <div
                    style={{
                        background: COLORS.cardBg,
                        border: `1px solid ${COLORS.border}`,
                        borderRadius: '10px',
                        padding: '32px',
                        textAlign: 'center',
                        color: COLORS.textMuted,
                        marginBottom: '24px',
                    }}
                >
                    <div style={{ fontSize: '32px', marginBottom: '12px' }}>🏷️</div>
                    <div style={{ fontSize: '15px', fontWeight: '600', marginBottom: '6px' }}>No pillar tags yet</div>
                    <div style={{ fontSize: '13px' }}>
                        Tag epics in Jira with components: <strong>Liquidity</strong>, <strong>Seller Automation</strong>, or <strong>GTM</strong>
                    </div>
                </div>
            )}

            {/* Summary cards */}
            <div
                style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(4, 1fr)',
                    gap: '12px',
                    marginBottom: '24px',
                }}
            >
                {PILLARS.map((p) => {
                    const meta = PILLAR_META[p]
                    const d = pillarData[p]
                    const ptsPct = totalPts > 0 ? Math.round((d.totalPts / totalPts) * 100) : 0
                    const storiesPct = totalCount > 0 ? Math.round((d.totalCount / totalCount) * 100) : 0
                    const devsPct = totalDevs > 0 ? Math.round((d.devs.length / totalDevs) * 100) : 0
                    return (
                        <div
                            key={p}
                            style={{
                                background: COLORS.cardBg,
                                border: `1px solid ${COLORS.border}`,
                                borderTop: `3px solid ${meta.color}`,
                                borderRadius: '10px',
                                padding: '16px',
                            }}
                        >
                            <div
                                style={{
                                    fontSize: '11px',
                                    color: COLORS.textMuted,
                                    fontWeight: '600',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.05em',
                                    marginBottom: '8px',
                                }}
                            >
                                {meta.icon} {meta.label}
                            </div>
                            <div style={{ fontSize: '26px', fontWeight: '700', color: meta.color, lineHeight: 1 }}>
                                {d.totalPts}
                                <span style={{ fontSize: '13px', fontWeight: '400', color: COLORS.textMuted, marginLeft: '4px' }}>
                                    pts
                                </span>
                                <span style={{ fontSize: '13px', fontWeight: '500', color: meta.color, marginLeft: '8px' }}>
                                    {ptsPct}%
                                </span>
                            </div>
                            <div style={{ marginTop: '10px', display: 'flex', flexDirection: 'column', gap: '5px' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px' }}>
                                    <span style={{ color: COLORS.textMuted }}>
                                        {d.totalCount} {d.totalCount === 1 ? 'story' : 'stories'}
                                    </span>
                                    <span style={{ color: COLORS.textMuted, fontWeight: '500' }}>{storiesPct}%</span>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px' }}>
                                    <span style={{ color: meta.color, fontWeight: '600' }}>
                                        {d.devs.length} {d.devs.length === 1 ? 'dev' : 'devs'}
                                    </span>
                                    <span style={{ color: meta.color, fontWeight: '600' }}>{devsPct}%</span>
                                </div>
                            </div>
                            <div
                                style={{ marginTop: '10px', height: '4px', background: COLORS.border, borderRadius: '2px' }}
                            >
                                <div
                                    style={{ height: '100%', width: `${ptsPct}%`, background: meta.color, borderRadius: '2px' }}
                                />
                            </div>
                        </div>
                    )
                })}
            </div>

            {/* Pillar panels — 2-column grid */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                {PILLARS.map((p) => {
                    const meta = PILLAR_META[p]
                    const d = pillarData[p]
                    const isCollapsed = collapsed[p]
                    const topEpics = Object.entries(d.epics)
                        .sort((a, b) => b[1] - a[1])
                        .slice(0, 6)

                    return (
                        <div
                            key={p}
                            style={{
                                background: COLORS.cardBg,
                                border: `1px solid ${COLORS.border}`,
                                borderRadius: '12px',
                                overflow: 'hidden',
                            }}
                        >
                            {/* Panel header — clickable to toggle */}
                            <div
                                onClick={() => toggleCollapsed(p)}
                                style={{
                                    background: `${meta.color}12`,
                                    borderBottom: isCollapsed ? 'none' : `1px solid ${meta.color}30`,
                                    padding: '12px 16px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    cursor: 'pointer',
                                    userSelect: 'none',
                                }}
                            >
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <span style={{ fontSize: '16px' }}>{meta.icon}</span>
                                    <span style={{ fontWeight: '700', color: meta.color, fontSize: '14px' }}>
                                        {meta.label}
                                    </span>
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                                    <div style={{ display: 'flex', gap: '16px', fontSize: '12px', color: COLORS.textMuted }}>
                                        <span>
                                            <span style={{ color: meta.color, fontWeight: '700' }}>{d.devs.length}</span> devs
                                        </span>
                                        <span>
                                            <span style={{ color: COLORS.text, fontWeight: '600' }}>{d.totalPts}</span> pts
                                        </span>
                                        <span>
                                            <span style={{ color: COLORS.text, fontWeight: '600' }}>{d.totalCount}</span>{' '}
                                            stories
                                        </span>
                                    </div>
                                    <span style={{ color: COLORS.textMuted, fontSize: '12px', lineHeight: 1 }}>
                                        {isCollapsed ? '▸' : '▾'}
                                    </span>
                                </div>
                            </div>

                            {/* Epics */}
                            {!isCollapsed && topEpics.length > 0 && (
                                <div
                                    style={{
                                        borderBottom: `1px solid ${COLORS.border}`,
                                        padding: '12px 16px',
                                        background: `${COLORS.background}60`,
                                    }}
                                >
                                    <div
                                        style={{
                                            fontSize: '12px',
                                            color: COLORS.textMuted,
                                            fontWeight: '600',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.05em',
                                            marginBottom: '8px',
                                        }}
                                    >
                                        Epics
                                    </div>
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                                        {topEpics.map(([name, pts]) => (
                                            <span
                                                key={name}
                                                title={`${name} — ${pts}pt`}
                                                style={{
                                                    background: `${meta.color}15`,
                                                    border: `1px solid ${meta.color}40`,
                                                    borderRadius: '4px',
                                                    padding: '4px 10px',
                                                    fontSize: '12px',
                                                    color: COLORS.text,
                                                    maxWidth: '260px',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap',
                                                }}
                                            >
                                                {name}
                                                <span style={{ color: COLORS.textMuted, marginLeft: '6px' }}>
                                                    {pts}pt
                                                </span>
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Dev table */}
                            {!isCollapsed && (d.devs.length === 0 ? (
                                <div
                                    style={{
                                        padding: '24px 16px',
                                        color: COLORS.textMuted,
                                        fontSize: '13px',
                                        textAlign: 'center',
                                    }}
                                >
                                    No work tagged to this pillar this sprint
                                </div>
                            ) : (
                                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                    <thead>
                                        <tr style={{ background: `${COLORS.background}60` }}>
                                            <th style={{ ...thStyle, textAlign: 'left', paddingLeft: '16px' }}>
                                                Developer
                                            </th>
                                            <th style={{ ...thStyle, textAlign: 'right' }}>Pts</th>
                                            <th style={{ ...thStyle, textAlign: 'right' }}>Stories</th>
                                            <th style={{ ...thStyle, textAlign: 'right', paddingRight: '16px' }}>
                                                Share
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {d.devs.map((dev) => {
                                            const share =
                                                d.totalPts > 0 ? Math.round((dev.pts / d.totalPts) * 100) : 0
                                            const roleMeta = dev.role ? ROLE_META[dev.role] : null
                                            return (
                                                <tr
                                                    key={dev.id}
                                                    style={{ borderTop: `1px solid ${COLORS.border}18` }}
                                                    onMouseEnter={(e) =>
                                                        (e.currentTarget.style.background = `${COLORS.border}18`)
                                                    }
                                                    onMouseLeave={(e) =>
                                                        (e.currentTarget.style.background = 'transparent')
                                                    }
                                                >
                                                    <td style={{ padding: '9px 12px 9px 16px' }}>
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '7px' }}>
                                                            <span style={{ fontWeight: '600', fontSize: '14px', color: COLORS.text }}>
                                                                {dev.name}
                                                            </span>
                                                            {roleMeta && (
                                                                <span
                                                                    style={{
                                                                        fontSize: '11px',
                                                                        fontWeight: '600',
                                                                        color: roleMeta.color,
                                                                        background: `${roleMeta.color}22`,
                                                                        border: `1px solid ${roleMeta.color}40`,
                                                                        borderRadius: '3px',
                                                                        padding: '1px 6px',
                                                                    }}
                                                                >
                                                                    {roleMeta.label}
                                                                </span>
                                                            )}
                                                        </div>
                                                        <div style={{ fontSize: '12px', color: COLORS.textMuted }}>
                                                            {dev.projects?.join(', ')}
                                                        </div>
                                                    </td>
                                                    <td style={{ padding: '9px 12px', textAlign: 'right', fontWeight: '700', fontSize: '14px', color: meta.color }}>
                                                        {dev.pts}
                                                    </td>
                                                    <td style={{ padding: '9px 12px', textAlign: 'right', fontSize: '13px', color: COLORS.textMuted }}>
                                                        {dev.count}
                                                    </td>
                                                    <td style={{ padding: '9px 16px 9px 12px', textAlign: 'right' }}>
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', justifyContent: 'flex-end' }}>
                                                            <div style={{ width: '52px', height: '5px', background: COLORS.border, borderRadius: '3px', overflow: 'hidden' }}>
                                                                <div style={{ height: '100%', width: `${share}%`, background: meta.color, borderRadius: '3px' }} />
                                                            </div>
                                                            <span style={{ fontSize: '12px', color: COLORS.textMuted, minWidth: '32px' }}>
                                                                {share}%
                                                            </span>
                                                        </div>
                                                    </td>
                                                </tr>
                                            )
                                        })}
                                    </tbody>
                                </table>
                            ))}
                        </div>
                    )
                })}
            </div>
        </div>
    )
}

// Main Dashboard Component
export default function JiraDashboard() {
    const {
        user: authUser,
        loading: authChecking,
        error: authError,
        login: authLogin,
        logout: handleLogout,
    } = useAuth()
    const [loading, setLoading] = useState(true)
    const [syncing, setSyncing] = useState(false)
    const [error, setError] = useState(null)
    const [data, setData] = useState(null)
    const [lastSync, setLastSync] = useState(null)
    const [lastFullSync, setLastFullSync] = useState(() => {
        try {
            return localStorage.getItem('jiraDashboard_lastFullSync') || null
        } catch {
            return null
        }
    })
    const [selectedSprintNum, setSelectedSprintNum] = useState(null)
    const [selectedPod, setSelectedPod] = useState(null) // null = All Pods
    const [selectedDevelopers, setSelectedDevelopers] = useState([])
    const [workspace, setWorkspace] = useState(() => {
        try {
            const v = localStorage.getItem('jiraDashboard_workspace')
            // Known workspace ids only — legacy "data" value preserved (was DAT+DET combined;
            // now means DAT-only per server config)
            return v && ['engineering', 'data', 'data-engineering'].includes(v) ? v : 'engineering'
        } catch {
            return 'engineering'
        }
    })
    const isDataWorkspace = workspace === 'data' || workspace === 'data-engineering'
    const isSinglePodWorkspace = isDataWorkspace // data workspaces each have one team, so pod filter is redundant
    // Excluded-user list. Source of truth is the server (/api/exclusions) so
    // every viewer sees identical sprint totals. This initial localStorage read
    // is only a first-paint seed / offline fallback — the server list overrides
    // it on load (see the /api/exclusions effect below).
    const [excludedUserIds, setExcludedUserIds] = useState(() => {
        try {
            const stored = localStorage.getItem('jiraDashboard_excludedUserIds')
            return stored ? JSON.parse(stored) : []
        } catch {
            return []
        }
    })
    // True only when the server is unreachable (offline / no-database dev) —
    // in that case editing persists to this browser's localStorage instead.
    const [exclusionsOffline, setExclusionsOffline] = useState(false)
    // Set true when a save is rejected with 403 (caller lacks admin/ops).
    const [exclusionsForbidden, setExclusionsForbidden] = useState(false)
    const [exclusionsError, setExclusionsError] = useState(null)
    const [showExcludePanel, setShowExcludePanel] = useState(false)
    const [activeDashboard, setActiveDashboard] = useState('summary') // summary | velocity | quality | workmix | delivery | people | team | scrub
    const [workMixView, setWorkMixView] = useState('categories') // categories | pillars (toggle inside Work Mix)
    const [toolsOpen, setToolsOpen] = useState(false)
    const [pulseRefreshSignal, setPulseRefreshSignal] = useState(0)
    const [roles, setRoles] = useState({})
    const [roleSaving, setRoleSaving] = useState(null) // accountId currently being saved
    const [isExporting, setIsExporting] = useState(false)
    const dashboardRef = useRef(null)

    // Export interactive HTML with pod filtering and hover effects
    const exportDashboardHTML = useCallback(async () => {
        const container = dashboardRef.current
        if (!container || isExporting) return
        setIsExporting(true)

        const originalPod = selectedPod
        const podConfigs = [
            { key: 'all', stateValue: null, label: '🌐 All Pods' },
            { key: 'bp', stateValue: 'BP', label: '🛒 Buyer' },
            { key: 'sp', stateValue: 'SP', label: '🏪 Seller' },
            { key: 'fp', stateValue: 'FP', label: '🏗️ Foundations' },
        ]

        const waitForRender = () =>
            new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(() => setTimeout(resolve, 120))))

        const cleanClone = (clone, podKey) => {
            // Remove all buttons and replace selects with static text
            clone.querySelectorAll('button').forEach((el) => el.remove())
            clone.querySelectorAll('select').forEach((el) => {
                const span = document.createElement('span')
                span.textContent = el.options[el.selectedIndex]?.text || ''
                span.style.cssText = 'color: #f1f5f9; font-size: 14px; font-weight: 600;'
                el.replaceWith(span)
            })
            // Remove fixed-position overlays (exclude panel, export overlay)
            clone.querySelectorAll('[style*="position: fixed"]').forEach((el) => el.remove())
            // Make sticky nav non-sticky
            clone.querySelectorAll('[style*="position: sticky"]').forEach((el) => {
                el.style.position = 'relative'
            })
            // Fix Recharts responsive containers
            clone.querySelectorAll('.recharts-responsive-container').forEach((rc) => {
                rc.style.width = '100%'
                rc.style.height = rc.style.height || '320px'
            })
            // Tag stat cards for CSS hover (border-radius: 16px + padding: 24px + cardBg)
            clone.querySelectorAll('div').forEach((div) => {
                const s = div.getAttribute('style') || ''
                if (s.includes('border-radius: 16px') && s.includes('padding: 24px') && s.includes('#1e293b')) {
                    div.setAttribute('data-card', 'stat')
                }
                if (s.includes('border-radius: 12px') && s.includes('padding: 16px') && s.includes('display: flex')) {
                    div.setAttribute('data-card', 'contributor')
                }
            })
            // Namespace SVG gradient/clipPath IDs to avoid collisions across pod views
            clone.querySelectorAll('linearGradient, radialGradient, clipPath, mask, pattern, filter').forEach((el) => {
                const oldId = el.getAttribute('id')
                if (oldId) {
                    const newId = `${podKey}-${oldId}`
                    el.setAttribute('id', newId)
                    // Update fill/stroke/clip-path url(#...) references
                    clone
                        .querySelectorAll(
                            `[fill="url(#${oldId})"], [stroke="url(#${oldId})"], [clip-path="url(#${oldId})"]`
                        )
                        .forEach((ref) => {
                            ;['fill', 'stroke', 'clip-path'].forEach((attr) => {
                                if (ref.getAttribute(attr) === `url(#${oldId})`) {
                                    ref.setAttribute(attr, `url(#${newId})`)
                                }
                            })
                        })
                    // Also fix style attribute references
                    clone.querySelectorAll(`[style*="url(#${oldId})"]`).forEach((ref) => {
                        ref.style.cssText = ref.style.cssText.replace(
                            new RegExp(`url\\(#${oldId}\\)`, 'g'),
                            `url(#${newId})`
                        )
                    })
                }
            })
        }

        // Capture a snapshot for each pod
        const snapshots = {}
        try {
            for (const pod of podConfigs) {
                setSelectedPod(pod.stateValue)
                await waitForRender()
                const clone = container.cloneNode(true)
                cleanClone(clone, pod.key)
                snapshots[pod.key] = clone.outerHTML
            }
        } finally {
            // Always restore original state
            setSelectedPod(originalPod)
            await waitForRender()
            setIsExporting(false)
        }

        const dashboardTitle = activeDashboard === 'velocity' ? 'Velocity Dashboard' : 'Delivery Dashboard'
        const timestamp = new Date().toLocaleString()

        const podButtonsHTML = podConfigs
            .map((p, i) => `<button class="pod-btn${i === 0 ? ' active' : ''}" data-pod="${p.key}">${p.label}</button>`)
            .join('\n        ')

        const podViewsHTML = podConfigs
            .map(
                (p, i) =>
                    `<div id="pod-${p.key}" class="pod-view" style="display:${i === 0 ? 'block' : 'none'}">${snapshots[p.key]}</div>`
            )
            .join('\n')

        const html = `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="UTF-8">
<meta name="viewport" content="width=device-width, initial-scale=1.0">
<title>${dashboardTitle} - Exported ${timestamp}</title>
<style>
  * { margin: 0; padding: 0; box-sizing: border-box; }
  body {
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
    color: #f1f5f9;
    min-height: 100vh;
  }
  svg { max-width: 100%; }
  .export-banner {
    background: #334155;
    color: #94a3b8;
    text-align: center;
    padding: 8px 16px;
    font-size: 12px;
    border-bottom: 1px solid #475569;
  }
  .export-nav {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 12px 24px;
    background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
    border-bottom: 1px solid #334155;
    position: sticky;
    top: 0;
    z-index: 100;
  }
  .export-nav::before {
    content: 'Pod';
    color: #94a3b8;
    font-size: 12px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    margin-right: 4px;
  }
  .pod-btn {
    background: transparent;
    border: 1px solid #334155;
    border-radius: 999px;
    padding: 6px 18px;
    font-size: 13px;
    font-weight: 400;
    color: #94a3b8;
    cursor: pointer;
    transition: all 0.15s ease;
    font-family: inherit;
  }
  .pod-btn:hover {
    border-color: #3b82f6;
    color: #e2e8f0;
    background: rgba(59, 130, 246, 0.08);
  }
  .pod-btn.active {
    background: rgba(59, 130, 246, 0.15);
    border-color: #3b82f6;
    color: #3b82f6;
    font-weight: 700;
    box-shadow: 0 0 12px rgba(59, 130, 246, 0.2);
  }
  [data-card="stat"] {
    transition: transform 0.2s ease, box-shadow 0.2s ease;
    cursor: default;
  }
  [data-card="stat"]:hover {
    transform: scale(1.03);
    box-shadow: 0 0 24px rgba(59, 130, 246, 0.18) !important;
  }
  [data-card="contributor"] {
    transition: background 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease;
    cursor: default;
  }
  [data-card="contributor"]:hover {
    background: #253348 !important;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.3);
    transform: translateY(-1px);
  }
  .recharts-bar-rectangle rect {
    transition: opacity 0.15s ease;
    cursor: pointer;
  }
  .recharts-bar-rectangle rect:hover { opacity: 0.75; }
  .recharts-dot { cursor: pointer; }
  .recharts-dot:hover { opacity: 0.7; }
  .recharts-area-area:hover { opacity: 0.85; }
</style>
</head>
<body>
<div class="export-banner">
  ${dashboardTitle} &middot; Exported on ${timestamp} &middot; Select a pod to filter
</div>
<div class="export-nav">
  ${podButtonsHTML}
</div>
${podViewsHTML}
<script>
(function() {
  var btns = document.querySelectorAll('.pod-btn');
  var views = document.querySelectorAll('.pod-view');
  btns.forEach(function(btn) {
    btn.addEventListener('click', function() {
      var pod = btn.getAttribute('data-pod');
      views.forEach(function(v) { v.style.display = 'none'; });
      var target = document.getElementById('pod-' + pod);
      if (target) target.style.display = 'block';
      btns.forEach(function(b) { b.classList.remove('active'); });
      btn.classList.add('active');
    });
  });
})();
</script>
</body>
</html>`

        const blob = new Blob([html], { type: 'text/html' })
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = `${dashboardTitle.toLowerCase().replace(/\s+/g, '-')}-${new Date().toISOString().slice(0, 10)}.html`
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        URL.revokeObjectURL(url)
    }, [activeDashboard, isExporting, selectedPod])

    // Only admin/ops may edit the shared list. In the offline dev fallback we
    // still allow local-only edits (they persist to this browser's localStorage).
    const canWriteExclusions = authUser?.role === 'admin' || authUser?.role === 'ops'
    const canEditExclusions = exclusionsOffline || (canWriteExclusions && !exclusionsForbidden)

    // Persist the excluded list to the server (source of truth). Optimistic:
    // apply locally, then POST; on failure revert and surface the error. On 403
    // mark the list read-only. When the server is unreachable (offline / no-db
    // dev) fall back to this browser's localStorage.
    const saveExclusions = useCallback(
        async (nextIds) => {
            const prevIds = excludedUserIds
            setExcludedUserIds(nextIds)
            setExclusionsError(null)
            if (exclusionsOffline) {
                try {
                    localStorage.setItem('jiraDashboard_excludedUserIds', JSON.stringify(nextIds))
                } catch {
                    /* ignore quota errors */
                }
                return
            }
            try {
                const res = await fetch('/api/exclusions', {
                    method: 'POST',
                    credentials: 'include',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ excluded: nextIds }),
                })
                if (res.status === 403) {
                    setExcludedUserIds(prevIds)
                    setExclusionsForbidden(true)
                    setExclusionsError('Editing the excluded list requires admin or ops access.')
                    return
                }
                if (res.status === 503) {
                    // Server has no persistence available — degrade to local-only.
                    setExclusionsOffline(true)
                    try {
                        localStorage.setItem('jiraDashboard_excludedUserIds', JSON.stringify(nextIds))
                    } catch {
                        /* ignore quota errors */
                    }
                    return
                }
                if (!res.ok) {
                    setExcludedUserIds(prevIds)
                    setExclusionsError('Failed to save exclusions. Please try again.')
                    return
                }
                const blob = await res.json()
                if (Array.isArray(blob.excluded)) setExcludedUserIds(blob.excluded)
            } catch {
                // Network failure — revert and surface.
                setExcludedUserIds(prevIds)
                setExclusionsError('Could not reach the server to save exclusions.')
            }
        },
        [excludedUserIds, exclusionsOffline]
    )

    const toggleExcludeUser = useCallback(
        (userId) => {
            if (!canEditExclusions) return
            const next = excludedUserIds.includes(userId)
                ? excludedUserIds.filter((id) => id !== userId)
                : [...excludedUserIds, userId]
            saveExclusions(next)
        },
        [excludedUserIds, canEditExclusions, saveExclusions]
    )

    // Fetch data from API
    // Merge fresh (partial) data with cached data — replace matching sprints, keep older ones from cache
    const mergeDashboardData = useCallback((cached, fresh) => {
        // Merge sprint-level arrays by sprintNum (fresh wins for matching entries)
        const mergeBySprintNum = (cachedArr, freshArr, sortDescending = false) => {
            const map = new Map()
            ;(cachedArr || []).forEach((entry) => map.set(entry.sprintNum, entry))
            ;(freshArr || []).forEach((entry) => map.set(entry.sprintNum, entry))
            const merged = Array.from(map.values())
            return sortDescending
                ? merged.sort((a, b) => b.sprintNum - a.sprintNum)
                : merged.sort((a, b) => a.sprintNum - b.sprintNum)
        }

        const mergedVelocityTrend = mergeBySprintNum(cached.velocityTrend, fresh.velocityTrend)
        const mergedSprintHistory = mergeBySprintNum(cached.sprintHistory, fresh.sprintHistory, true)

        // Recalculate top-level aggregates from merged data
        const currentEntry = mergedVelocityTrend[mergedVelocityTrend.length - 1] || {}
        const previousEntry = mergedVelocityTrend[mergedVelocityTrend.length - 2] || {}
        const last3 = mergedVelocityTrend.slice(-3)
        const avgVelocity =
            last3.length > 0 ? Math.round(last3.reduce((sum, e) => sum + (e.total || 0), 0) / last3.length) : 0

        // Rebuild contributors from merged sprint history
        const contribMap = {}
        mergedSprintHistory.forEach((sprint) => {
            ;(sprint.contributors || []).forEach((c) => {
                const key = c.id || c.name
                if (!contribMap[key]) {
                    contribMap[key] = {
                        name: c.name,
                        id: c.id,
                        totalPoints: 0,
                        aiPoints: 0,
                        issueCount: 0,
                        sprintNums: new Map(),
                    }
                }
                const d = contribMap[key]
                d.totalPoints += c.currentVelocity || 0
                d.aiPoints += c.aiPoints || 0
                d.issueCount += c.issueCount || 0
                d.sprintNums.set(sprint.sprintNum, { total: c.currentVelocity || 0, ai: c.aiPoints || 0 })
            })
        })

        const currentSprintNum = currentEntry.sprintNum
        const contributors = Object.values(contribMap)
            .map((c) => {
                const sprintCount = c.sprintNums.size
                const avgContribVelocity = sprintCount > 0 ? c.totalPoints / sprintCount : 0
                const currentSprintData = c.sprintNums.get(currentSprintNum) || { total: 0 }
                return {
                    name: c.name,
                    id: c.id,
                    totalPoints: c.totalPoints,
                    aiPoints: c.aiPoints,
                    issueCount: c.issueCount,
                    sprintCount,
                    avgVelocity: Math.round(avgContribVelocity * 10) / 10,
                    currentVelocity: currentSprintData.total,
                    aiUsage: c.totalPoints > 0 ? Math.round((c.aiPoints / c.totalPoints) * 100) : 0,
                    trend:
                        currentSprintData.total > avgContribVelocity
                            ? 'up'
                            : currentSprintData.total < avgContribVelocity
                              ? 'down'
                              : 'stable',
                }
            })
            .sort((a, b) => b.currentVelocity - a.currentVelocity)

        // Rebuild summary
        const allSprints = mergedSprintHistory
        const summary = {
            totalSprints: allSprints.length,
            totalIssues: allSprints.reduce((s, sp) => s + (sp.completedStories || 0), 0),
            totalPoints: allSprints.reduce((s, sp) => s + (sp.totalPoints || 0), 0),
            totalAiPoints: allSprints.reduce((s, sp) => s + (sp.aiPoints || 0), 0),
            uniqueContributors: new Set(allSprints.flatMap((sp) => (sp.contributors || []).map((c) => c.id || c.name)))
                .size,
        }

        return {
            ...fresh,
            velocityTrend: mergedVelocityTrend,
            sprintHistory: mergedSprintHistory,
            currentVelocity: currentEntry.total || 0,
            previousVelocity: previousEntry.total || 0,
            aiVelocity: currentEntry.aiVelocity || 0,
            nonAiVelocity: currentEntry.nonAiVelocity || 0,
            aiAdoption:
                (currentEntry.total || 0) > 0
                    ? Math.round(((currentEntry.aiVelocity || 0) / currentEntry.total) * 100)
                    : 0,
            avgVelocity,
            contributors,
            summary,
        }
    }, [])

    // mode: false = incremental refresh, true = full fetch from /api/latest then /api/dashboard-data, 'all' = force full Jira fetch
    const fetchData = useCallback(
        async (mode = false) => {
            setSyncing(true)
            setError(null)

            // Workspace-scoped localStorage keys so engineering and data don't trample each other
            const cacheKey = `jiraDashboard_sprintCache_${workspace}`
            const lastFullSyncKey = `jiraDashboard_lastFullSync_${workspace}`
            const qsWs = `workspace=${workspace}`

            try {
                // Load cached data from localStorage
                let cached = null
                try {
                    const stored = localStorage.getItem(cacheKey)
                    if (stored) cached = JSON.parse(stored)
                } catch {
                    /* ignore parse errors */
                }

                // Determine fetch strategy
                if (mode === 'all') {
                    // Paint something BEFORE the crawl. A forced full sync is a
                    // 60-160s round trip (wide crawl + ~1,000 changelog backfill calls,
                    // see backfillTruncatedStarts), and blocking first paint on it
                    // strands the user on "Loading Dashboard..." long enough to read as
                    // hung — worse, reloading restarts the crawl and never persists
                    // lastFullSync, so the forced path repeats forever. Seed from the
                    // browser cache, else from the server's last snapshot (a fast blob
                    // read); the crawl below still replaces it, which is what heals the
                    // stale sprints this mode exists for.
                    if (!data || data.workspace !== workspace) {
                        if (cached && cached.sprintHistory?.length > 0) {
                            setData(cached)
                            setLastSync(cached.timestamp)
                            setLoading(false)
                        } else {
                            try {
                                const seed = await fetch(`/api/latest?${qsWs}`, { credentials: 'include' })
                                if (seed.ok) {
                                    const snapshot = await seed.json()
                                    setData(snapshot)
                                    setLastSync(snapshot.timestamp)
                                    setLoading(false)
                                }
                            } catch {
                                /* no snapshot to seed from — fall through to the crawl */
                            }
                        }
                    }

                    // Force full refresh — fetch everything from Jira
                    const response = await fetch(`/api/dashboard-data?${qsWs}`, { credentials: 'include' })
                    if (!response.ok) throw new Error(`API error: ${response.status}`)
                    const result = await response.json()
                    setData(result)
                    setLastSync(result.timestamp)
                    setLastFullSync(result.timestamp)
                    localStorage.setItem(cacheKey, JSON.stringify(result))
                    localStorage.setItem(lastFullSyncKey, result.timestamp)
                } else if (cached && cached.sprintHistory?.length > 0 && mode === false) {
                    // Incremental refresh — use cache for older sprints, fetch only recent 3 from Jira
                    // Show cached data immediately
                    if (!data || data.workspace !== workspace) {
                        setData(cached)
                        setLastSync(cached.timestamp)
                        setLoading(false)
                    }

                    // Compute sinceDate: start of the 3rd-from-latest sprint minus 1 week buffer
                    const sortedSprints = [...cached.velocityTrend].sort((a, b) => a.sprintNum - b.sprintNum)
                    const targetIdx = Math.max(0, sortedSprints.length - 3)
                    const targetStartDate = sortedSprints[targetIdx]?.startDate
                    let sinceDate = ''
                    if (targetStartDate) {
                        const d = new Date(targetStartDate)
                        d.setDate(d.getDate() - 7) // 1-week buffer
                        sinceDate = d.toISOString().split('T')[0]
                    }

                    const endpoint = sinceDate
                        ? `/api/dashboard-data?${qsWs}&sinceDate=${sinceDate}`
                        : `/api/dashboard-data?${qsWs}`
                    const response = await fetch(endpoint, { credentials: 'include' })
                    if (!response.ok) throw new Error(`API error: ${response.status}`)
                    const freshResult = await response.json()

                    // Merge fresh data with cache
                    const merged = sinceDate ? mergeDashboardData(cached, freshResult) : freshResult
                    setData(merged)
                    setLastSync(merged.timestamp)
                    localStorage.setItem(cacheKey, JSON.stringify(merged))
                    // If this was a full fetch (no sinceDate), update full sync timestamp
                    if (!sinceDate) {
                        setLastFullSync(merged.timestamp)
                        localStorage.setItem(lastFullSyncKey, merged.timestamp)
                    }
                } else {
                    // No cache — try /api/latest first, then fall back to full fetch
                    const latestResponse = await fetch(`/api/latest?${qsWs}`, { credentials: 'include' })
                    if (latestResponse.ok) {
                        const result = await latestResponse.json()
                        setData(result)
                        setLastSync(result.timestamp)
                        setLastFullSync(result.timestamp)
                        localStorage.setItem(cacheKey, JSON.stringify(result))
                        localStorage.setItem(lastFullSyncKey, result.timestamp)
                    } else {
                        // No server cache either — full Jira fetch
                        const response = await fetch(`/api/dashboard-data?${qsWs}`, { credentials: 'include' })
                        if (!response.ok) throw new Error(`API error: ${response.status}`)
                        const result = await response.json()
                        setData(result)
                        setLastSync(result.timestamp)
                        setLastFullSync(result.timestamp)
                        localStorage.setItem(cacheKey, JSON.stringify(result))
                        localStorage.setItem(lastFullSyncKey, result.timestamp)
                    }
                }
            } catch (err) {
                console.error('Error fetching data:', err)
                if (err.message?.includes('401')) {
                    // Session expired — bounce to LoginScreen instead of stranding the user on stale cache
                    handleLogout()
                    return
                }
                setError(err.message)
            } finally {
                setSyncing(false)
                setLoading(false)
            }
        },
        [data, mergeDashboardData, workspace, handleLogout]
    )

    // Initial load — show cached data fast, then incremental refresh.
    // Also one-shot: clear stale "data"/"data-engineering" localStorage caches if
    // they're missing the wider sprint window (server now returns 18 sprints for
    // these workspaces; prior caches held only 12, leaving sprints outside both
    // windows — like DAT S39/S40 — stranded as gaps in the trend chart).
    useEffect(() => {
        try {
            // Pre-2026-04-21 combined DAT+DET cache: clear if DET sprints leaked in.
            const stale = localStorage.getItem('jiraDashboard_sprintCache_data')
            if (stale) {
                const parsed = JSON.parse(stale)
                const looksCombined = parsed?.sprintHistory?.some((s) => /^DET\s/i.test(s.name || ''))
                if (looksCombined) {
                    localStorage.removeItem('jiraDashboard_sprintCache_data')
                    localStorage.removeItem('jiraDashboard_lastFullSync_data')
                    console.log('Cleared pre-split data-workspace cache')
                }
            }
            // Sprint-window expansion: drop caches whose history has gaps in sprint
            // numbers, so the next fetch repopulates a continuous run from the server's
            // wider window. Only applies to workspaces that just had their window grown.
            for (const ws of ['data', 'data-engineering']) {
                const raw = localStorage.getItem(`jiraDashboard_sprintCache_${ws}`)
                if (!raw) continue
                const parsed = JSON.parse(raw)
                const nums = (parsed?.velocityTrend || [])
                    .map((e) => e.sprintNum)
                    .filter((n) => Number.isFinite(n))
                    .sort((a, b) => a - b)
                if (nums.length < 2) continue
                const hasGap = nums[nums.length - 1] - nums[0] + 1 !== nums.length
                if (hasGap) {
                    localStorage.removeItem(`jiraDashboard_sprintCache_${ws}`)
                    localStorage.removeItem(`jiraDashboard_lastFullSync_${ws}`)
                    console.log(`Cleared gapped ${ws}-workspace cache (sprint numbers had gaps)`)
                }
            }
        } catch {
            /* ignore */
        }
        if (authUser) {
            // NOTE: no fetchData here. This effect and the workspace effect below both
            // depend on authUser, so calling it here fired TWO concurrent crawls on
            // every login (observed as duplicate 80s+ /api/dashboard-data requests).
            // The workspace effect covers login and workspace switches alike, and it
            // runs after this one, so the cache cleanup above still lands first.
            fetch('/api/roles', { credentials: 'include' })
                .then((r) => (r.ok ? r.json() : {}))
                .then((r) => setRoles(r))
                .catch(() => {})
        }
    }, [authUser])

    // Load the shared excluded-user list from the server (source of truth), and
    // run a one-time migration of any legacy per-browser list. If the server
    // list is empty, this browser has a non-empty legacy localStorage list, and
    // the user can write, push the local list up once then retire the local key
    // so it never re-pushes (on 403 just fall back to the server list). If the
    // server is unreachable (offline / no-database dev) keep the localStorage
    // seed and switch to local-only editing.
    useEffect(() => {
        if (!authUser) return
        let cancelled = false
        ;(async () => {
            try {
                const res = await fetch('/api/exclusions', { credentials: 'include' })
                if (!res.ok) throw new Error(`status ${res.status}`)
                const blob = await res.json()
                if (cancelled) return
                const serverList = Array.isArray(blob.excluded) ? blob.excluded : []
                let legacy = []
                try {
                    const raw = localStorage.getItem('jiraDashboard_excludedUserIds')
                    legacy = raw ? JSON.parse(raw) : []
                } catch {
                    legacy = []
                }
                const canWrite = authUser?.role === 'admin' || authUser?.role === 'ops'
                if (serverList.length === 0 && Array.isArray(legacy) && legacy.length > 0 && canWrite) {
                    try {
                        const up = await fetch('/api/exclusions', {
                            method: 'POST',
                            credentials: 'include',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({ excluded: legacy }),
                        })
                        if (up.ok) {
                            const saved = await up.json()
                            if (!cancelled) {
                                setExcludedUserIds(Array.isArray(saved.excluded) ? saved.excluded : legacy)
                            }
                            // Retire the local key so migration never repeats.
                            try {
                                localStorage.removeItem('jiraDashboard_excludedUserIds')
                                localStorage.setItem('jiraDashboard_excludedUserIds_migrated', '1')
                            } catch {
                                /* ignore */
                            }
                        } else if (!cancelled) {
                            // 403 or error: fall back to the (empty) server list.
                            setExcludedUserIds(serverList)
                        }
                    } catch {
                        if (!cancelled) setExcludedUserIds(serverList)
                    }
                } else if (!cancelled) {
                    setExcludedUserIds(serverList)
                }
            } catch {
                // Server unreachable (offline / no-database dev): keep the
                // localStorage-seeded state and allow local-only editing.
                if (!cancelled) setExclusionsOffline(true)
            }
        })()
        return () => {
            cancelled = true
        }
    }, [authUser])

    // When the workspace toggle changes, reset filters + lastFullSync context
    // + refetch from the new workspace's server cache.
    useEffect(() => {
        if (!authUser) return
        setSelectedPod(null)
        setSelectedSprintNum(null)
        setSelectedDevelopers([])
        let lastFull = null
        try {
            lastFull = localStorage.getItem(`jiraDashboard_lastFullSync_${workspace}`) || null
            setLastFullSync(lastFull)
        } catch {
            /* ignore */
        }
        // Data workspaces only expose summary / velocity / workmix / people — bounce off hidden tabs.
        if (isDataWorkspace && ['delivery', 'quality', 'scrub', 'agents'].includes(activeDashboard)) {
            setActiveDashboard('summary')
        }
        try {
            localStorage.setItem('jiraDashboard_workspace', workspace)
        } catch {
            /* ignore */
        }
        // Heal stale caches on workspace switch too (see cacheFreshness.js).
        fetchData(shouldForceFullSync(lastFull) ? 'all' : false)
    }, [workspace, authUser])

    // Default sprint selector to latest sprint once data arrives (or after workspace reset)
    useEffect(() => {
        const latest = data?.sprintHistory?.[0]?.sprintNum
        if (latest != null && selectedSprintNum == null) {
            setSelectedSprintNum(latest)
        }
    }, [data, selectedSprintNum])

    const handleRoleChange = useCallback(async (accountId, newRole) => {
        setRoleSaving(accountId)
        try {
            const res = await fetch('/api/roles', {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ accountId, role: newRole }),
            })
            if (res.ok) {
                const { roles: updated } = await res.json()
                setRoles(updated)
            }
        } catch {
            /* ignore */
        } finally {
            setRoleSaving(null)
        }
    }, [])

    // Auth checking state
    if (authChecking) {
        return (
            <div
                style={{
                    minHeight: '100vh',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: `linear-gradient(135deg, ${COLORS.background} 0%, ${COLORS.cardBg} 100%)`,
                }}
            >
                <p style={{ color: COLORS.textMuted, fontSize: '16px' }}>Checking authentication...</p>
            </div>
        )
    }

    // Not authenticated — show login
    if (!authUser) {
        return (
            <LoginScreen
                googleClientId={GOOGLE_CLIENT_ID}
                onCredential={authLogin}
                appName="Jira Velocity Dashboard"
                error={authError}
                colors={{
                    bg: COLORS.background,
                    card: COLORS.cardBg,
                    border: COLORS.border,
                    text: COLORS.text,
                    textMuted: COLORS.textMuted,
                    danger: COLORS.danger,
                }}
            />
        )
    }

    // Loading state
    if (loading) {
        return (
            <div
                style={{
                    minHeight: '100vh',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: `linear-gradient(135deg, ${COLORS.background} 0%, ${COLORS.cardBg} 100%)`,
                    color: COLORS.text,
                }}
            >
                <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '48px', marginBottom: '16px' }}>📊</div>
                    <div style={{ fontSize: '20px', fontWeight: '600' }}>Loading Dashboard...</div>
                    <div style={{ color: COLORS.textMuted, marginTop: '8px' }}>Fetching Jira data</div>
                </div>
            </div>
        )
    }

    // Error state
    if (error && !data) {
        return (
            <div
                style={{
                    minHeight: '100vh',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: `linear-gradient(135deg, ${COLORS.background} 0%, ${COLORS.cardBg} 100%)`,
                    color: COLORS.text,
                    padding: '20px',
                }}
            >
                <div
                    style={{
                        textAlign: 'center',
                        background: COLORS.cardBg,
                        padding: '40px',
                        borderRadius: '16px',
                        maxWidth: '500px',
                    }}
                >
                    <div style={{ fontSize: '48px', marginBottom: '16px' }}>⚠️</div>
                    <div style={{ fontSize: '20px', fontWeight: '600', marginBottom: '12px' }}>Connection Error</div>
                    <div style={{ color: COLORS.textMuted, marginBottom: '24px' }}>{error}</div>
                    <div style={{ color: COLORS.textMuted, fontSize: '14px', marginBottom: '24px' }}>
                        Make sure the server is running and your .env file has valid Jira credentials.
                    </div>
                    <button
                        onClick={() => fetchData(true)}
                        style={{
                            background: COLORS.primary,
                            color: 'white',
                            border: 'none',
                            padding: '12px 24px',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            fontWeight: '600',
                        }}
                    >
                        Retry
                    </button>
                </div>
            </div>
        )
    }

    // === Build master user list for the exclusion panel ===
    const allKnownUsers = (() => {
        if (!data) return []
        const userMap = new Map()
        ;(data.contributors || []).forEach((c) => {
            if (c.id && !userMap.has(c.id)) userMap.set(c.id, { id: c.id, name: c.name })
        })
        ;(data.sprintHistory || []).forEach((sprint) => {
            ;(sprint.contributors || []).forEach((c) => {
                if (c.id && !userMap.has(c.id)) userMap.set(c.id, { id: c.id, name: c.name })
            })
        })
        return Array.from(userMap.values()).sort((a, b) => a.name.localeCompare(b.name))
    })()

    // Helper: recompute sprint aggregates from a filtered contributor list
    const recomputeSprintFromContributors = (sprint, keptContributors) => {
        const totalPoints = keptContributors.reduce((s, c) => s + (c.currentVelocity || 0), 0)
        const aiPoints = keptContributors.reduce((s, c) => s + (c.aiPoints || 0), 0)
        const completedStories = keptContributors.reduce((s, c) => s + (c.issueCount || 0), 0)
        const projectBreakdown = (sprint.projectBreakdown || []).map((pb) => {
            const podContribs = keptContributors.filter((c) => !c.projects || c.projects.includes(pb.name))
            const podPts = podContribs.reduce((s, c) => s + (c.currentVelocity || 0), 0)
            const podAi = podContribs.reduce((s, c) => s + (c.aiPoints || 0), 0)
            const podStories = podContribs.reduce((s, c) => s + (c.issueCount || 0), 0)
            return {
                ...pb,
                points: podPts,
                stories: podStories,
                aiPercent: podPts > 0 ? Math.round((podAi / podPts) * 100) : 0,
            }
        })
        const byProject = {}
        projectBreakdown.forEach((pb) => {
            byProject[pb.name] = pb.points
        })
        return {
            ...sprint,
            totalPoints,
            aiPoints,
            nonAiPoints: totalPoints - aiPoints,
            aiAdoption: totalPoints > 0 ? Math.round((aiPoints / totalPoints) * 100) : 0,
            completedStories,
            contributors: keptContributors,
            projectBreakdown,
            byProject,
        }
    }

    // === EXCLUSION LAYER: filter excluded users from sprint data ===
    const exclusionFilteredHistory = (() => {
        if (!data?.sprintHistory || excludedUserIds.length === 0) return data?.sprintHistory
        return data.sprintHistory.map((sprint) => {
            const kept = (sprint.contributors || []).filter((c) => !excludedUserIds.includes(c.id))
            return recomputeSprintFromContributors(sprint, kept)
        })
    })()

    // Apply exclusions to the active sprint card and recompute aggregates from kept contributors.
    // Same shape contract as data.activeSprint so the card consumer is unchanged.
    const exclusionFilteredActiveSprint = (() => {
        const a = data?.activeSprint
        if (!a) return null
        if (excludedUserIds.length === 0) return a
        const kept = (a.contributors || []).filter((c) => !excludedUserIds.includes(c.id))
        const sum = (arr, k) => arr.reduce((s, c) => s + (c[k] || 0), 0)
        const pointsCommitted = sum(kept, 'pointsCommitted')
        const pointsCompleted = sum(kept, 'pointsCompleted')
        const pointsInProgress = sum(kept, 'pointsInProgress')
        const aiPointsCompleted = sum(kept, 'aiPointsCompleted')
        const storiesCommitted = sum(kept, 'storiesCommitted')
        const storiesCompleted = sum(kept, 'storiesCompleted')
        const projectBreakdown = (a.projectBreakdown || []).map((p) => {
            const projKept = kept.filter((c) => c.projects?.includes(p.name))
            const ptsCommitted = sum(projKept, 'pointsCommitted')
            const ptsCompleted = sum(projKept, 'pointsCompleted')
            const ai = sum(projKept, 'aiPointsCompleted')
            return {
                ...p,
                pointsCommitted: ptsCommitted,
                pointsCompleted: ptsCompleted,
                pointsInProgress: sum(projKept, 'pointsInProgress'),
                storiesCommitted: sum(projKept, 'storiesCommitted'),
                storiesCompleted: sum(projKept, 'storiesCompleted'),
                aiPointsCompleted: ai,
                percentComplete: ptsCommitted > 0 ? Math.round((ptsCompleted / ptsCommitted) * 100) : 0,
                aiAdoption: ptsCompleted > 0 ? Math.round((ai / ptsCompleted) * 100) : 0,
            }
        })
        return {
            ...a,
            pointsCommitted,
            pointsCompleted,
            pointsInProgress,
            pointsTodo: Math.max(0, pointsCommitted - pointsCompleted - pointsInProgress),
            pointsRemaining: pointsCommitted - pointsCompleted,
            aiPointsCompleted,
            aiAdoption: pointsCompleted > 0 ? Math.round((aiPointsCompleted / pointsCompleted) * 100) : 0,
            storiesCommitted,
            storiesCompleted,
            storiesInProgress: sum(kept, 'storiesInProgress'),
            percentComplete: pointsCommitted > 0 ? Math.round((pointsCompleted / pointsCommitted) * 100) : 0,
            projectBreakdown,
            contributors: kept,
        }
    })()

    const selectedSprint =
        selectedSprintNum != null ? exclusionFilteredHistory?.find((s) => s.sprintNum === selectedSprintNum) : null
    const selectedSprintIdx = exclusionFilteredHistory?.findIndex((s) => s.sprintNum === selectedSprintNum) ?? -1
    const prevSprint =
        selectedSprintIdx > -1 && selectedSprintIdx < (exclusionFilteredHistory?.length ?? 0) - 1
            ? exclusionFilteredHistory[selectedSprintIdx + 1]
            : null
    const displaySprint = selectedSprint || exclusionFilteredHistory?.[0] || null

    const exclusionFilteredVelocityTrend = (() => {
        if (!data?.velocityTrend || excludedUserIds.length === 0) return data?.velocityTrend
        return data.velocityTrend.map((entry) => {
            const histSprint = exclusionFilteredHistory?.find((s) => s.sprintNum === entry.sprintNum)
            if (!histSprint) return entry
            const activeDevs = histSprint.contributors.length
            const total = histSprint.totalPoints
            const stories = histSprint.completedStories
            const keptAvails = histSprint.contributors.map((c) => c.availability).filter((a) => a != null)
            return {
                ...entry,
                total,
                aiVelocity: histSprint.aiPoints,
                nonAiVelocity: histSprint.nonAiPoints,
                activeDevs,
                pointsPerDev: activeDevs > 0 ? Math.round((total / activeDevs) * 10) / 10 : 0,
                storiesCompleted: stories,
                avgStorySize: stories > 0 ? Math.round((total / stories) * 10) / 10 : 0,
                avgAvailability:
                    keptAvails.length > 0
                        ? Math.round((keptAvails.reduce((s, v) => s + v, 0) / keptAvails.length) * 100) / 100
                        : null,
                lowAvailCount: histSprint.contributors.filter((c) => c.availability != null && c.availability < 0.5)
                    .length,
                fullyActiveDevs: histSprint.contributors.filter((c) => c.availability != null && c.availability >= 0.8)
                    .length,
                partialDevs: histSprint.contributors.filter((c) => c.availability != null && c.availability < 0.8)
                    .length,
            }
        })
    })()

    const exclusionFilteredSummary = (() => {
        if (!data?.summary || excludedUserIds.length === 0) return data?.summary
        const allIds = new Set()
        let totalIssues = 0,
            totalPoints = 0,
            totalAiPoints = 0
        ;(exclusionFilteredHistory || []).forEach((sprint) => {
            ;(sprint.contributors || []).forEach((c) => {
                allIds.add(c.id)
                totalIssues += c.issueCount || 0
            })
            totalPoints += sprint.totalPoints
            totalAiPoints += sprint.aiPoints
        })
        return { ...data.summary, uniqueContributors: allIds.size, totalIssues, totalPoints, totalAiPoints }
    })()

    // === POD FILTER: chains on top of exclusion-filtered data ===
    const filteredDisplaySprint = (() => {
        if (!selectedPod || !displaySprint) return displaySprint
        const podBreakdown = displaySprint.projectBreakdown?.find((p) => p.name === selectedPod)
        if (!podBreakdown) return displaySprint
        const podPoints = podBreakdown.points
        const podAi = Math.round((podPoints * podBreakdown.aiPercent) / 100)
        const podContributors = (displaySprint.contributors || []).filter(
            (c) => !c.projects || c.projects.includes(selectedPod)
        )
        return {
            ...displaySprint,
            totalPoints: podPoints,
            aiPoints: podAi,
            nonAiPoints: podPoints - podAi,
            aiAdoption: podBreakdown.aiPercent,
            completedStories: podBreakdown.stories,
            contributors: podContributors,
        }
    })()

    const filteredVelocityTrend = (() => {
        if (!selectedPod || !exclusionFilteredVelocityTrend) return exclusionFilteredVelocityTrend
        return exclusionFilteredVelocityTrend.map((entry) => {
            const histSprint = exclusionFilteredHistory?.find((s) => s.sprintNum === entry.sprintNum)
            const podBreakdown = histSprint?.projectBreakdown?.find((p) => p.name === selectedPod)
            if (!podBreakdown)
                return {
                    ...entry,
                    total: 0,
                    aiVelocity: 0,
                    nonAiVelocity: 0,
                    storiesCompleted: 0,
                    avgStorySize: 0,
                    activeDevs: 0,
                    pointsPerDev: 0,
                    rollovers: 0,
                }
            const podTotal = podBreakdown.points
            const podAi = Math.round((podTotal * podBreakdown.aiPercent) / 100)
            const podStories = podBreakdown.stories
            const podContributors = (histSprint?.contributors || []).filter(
                (c) => !c.projects || c.projects.includes(selectedPod)
            )
            const podActiveDevs = podContributors.length
            const podRollovers = histSprint?.rolloversByProject?.[selectedPod] ?? 0
            const podRolloverPoints = histSprint?.rolloverPointsByProject?.[selectedPod] ?? 0
            const podNotDone = histSprint?.notDoneByProject?.[selectedPod] ?? { count: 0, points: 0 }
            const podStoriesCommitted = podStories + podNotDone.count
            const podPointsCommitted = podTotal + podNotDone.points
            const podAvails = podContributors.map((c) => c.availability).filter((a) => a != null)
            return {
                ...entry,
                total: podTotal,
                aiVelocity: podAi,
                nonAiVelocity: podTotal - podAi,
                storiesCompleted: podStories,
                storiesCommitted: podStoriesCommitted,
                pointsCommitted: podPointsCommitted,
                avgStorySize: podStories > 0 ? Math.round((podTotal / podStories) * 10) / 10 : 0,
                activeDevs: podActiveDevs,
                pointsPerDev: podActiveDevs > 0 ? Math.round((podTotal / podActiveDevs) * 10) / 10 : 0,
                rollovers: podRollovers,
                rolloverPoints: podRolloverPoints,
                avgAvailability:
                    podAvails.length > 0
                        ? Math.round((podAvails.reduce((s, v) => s + v, 0) / podAvails.length) * 100) / 100
                        : null,
                lowAvailCount: podContributors.filter((c) => c.availability != null && c.availability < 0.5).length,
                fullyActiveDevs: podContributors.filter((c) => c.availability != null && c.availability >= 0.8).length,
                partialDevs: podContributors.filter((c) => c.availability != null && c.availability < 0.8).length,
            }
        })
    })()

    // === ALL-TIME STATS: aggregate contributor data across all sprints ===
    const allTimeContributors = (() => {
        const source = exclusionFilteredHistory
        if (!source || source.length === 0) return []
        const devMap = {}
        source.forEach((sprint) => {
            ;(sprint.contributors || []).forEach((c) => {
                if (selectedPod && c.projects && !c.projects.includes(selectedPod)) return
                const key = c.id || c.name
                if (!devMap[key]) {
                    devMap[key] = {
                        name: c.name,
                        id: c.id,
                        totalPoints: 0,
                        totalAiPoints: 0,
                        totalIssues: 0,
                        sprintCount: 0,
                        peakVelocity: 0,
                        peakAiUsage: 0,
                    }
                }
                const d = devMap[key]
                d.totalPoints += c.currentVelocity || 0
                d.totalAiPoints += c.aiPoints || 0
                d.totalIssues += c.issueCount || 0
                d.sprintCount += 1
                d.peakVelocity = Math.max(d.peakVelocity, c.currentVelocity || 0)
                d.peakAiUsage = Math.max(d.peakAiUsage, c.aiUsage || 0)
                // Calculate badges earned this sprint and accumulate
                const sprintBadges = calculateBadges({
                    currentVelocity: c.currentVelocity || 0,
                    aiUsage: c.aiUsage || 0,
                })
                d.totalBadgeCount = (d.totalBadgeCount || 0) + sprintBadges.length
                if (!d.badgeCounts) d.badgeCounts = {}
                sprintBadges.forEach((b) => {
                    d.badgeCounts[b.id] = (d.badgeCounts[b.id] || 0) + 1
                })
                if (c.availability != null) {
                    d.availabilitySum = (d.availabilitySum || 0) + c.availability
                    d.availabilityCount = (d.availabilityCount || 0) + 1
                }
            })
        })
        return Object.values(devMap)
            .map((d) => {
                // Build allTimeBadges: distinct badges with earn counts
                const allBadges = [...GAMIFICATION_CONFIG.velocityBadges, ...GAMIFICATION_CONFIG.aiAdoptionBadges]
                const allTimeBadges = allBadges
                    .filter((b) => d.badgeCounts && d.badgeCounts[b.id])
                    .map((b) => ({ ...b, earnCount: d.badgeCounts[b.id] }))
                return {
                    ...d,
                    avgVelocity: d.sprintCount > 0 ? Math.round((d.totalPoints / d.sprintCount) * 10) / 10 : 0,
                    overallAiUsage: d.totalPoints > 0 ? Math.round((d.totalAiPoints / d.totalPoints) * 100) : 0,
                    // For badge calculation: use peak single-sprint values
                    currentVelocity: d.peakVelocity,
                    aiUsage: d.peakAiUsage,
                    allTimeBadges,
                    totalBadgeCount: d.totalBadgeCount || 0,
                    avgAvailability:
                        d.availabilityCount > 0
                            ? Math.round((d.availabilitySum / d.availabilityCount) * 100) / 100
                            : null,
                }
            })
            .sort((a, b) => b.totalPoints - a.totalPoints)
    })()

    const velocityProgress = filteredDisplaySprint ? (filteredDisplaySprint.totalPoints / 1600) * 100 : 0
    const velocityChange = (() => {
        if (!filteredDisplaySprint || !prevSprint) return 0
        const prevPoints = selectedPod
            ? (prevSprint.projectBreakdown?.find((p) => p.name === selectedPod)?.points ?? prevSprint.totalPoints)
            : prevSprint.totalPoints
        return prevPoints > 0 ? Math.round(((filteredDisplaySprint.totalPoints - prevPoints) / prevPoints) * 100) : 0
    })()

    return (
        <div
            ref={dashboardRef}
            style={{
                minHeight: '100vh',
                background: `linear-gradient(135deg, ${COLORS.background} 0%, ${COLORS.cardBg} 100%)`,
                padding: '24px',
            }}
        >
            {isExporting && (
                <div
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: 'rgba(15, 23, 42, 0.92)',
                        zIndex: 10000,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexDirection: 'column',
                        gap: '16px',
                    }}
                >
                    <div style={{ fontSize: '48px' }}>📥</div>
                    <div style={{ color: COLORS.text, fontSize: '18px', fontWeight: '600' }}>
                        Exporting Dashboard...
                    </div>
                    <div style={{ color: COLORS.textMuted, fontSize: '14px' }}>Rendering all pod views</div>
                </div>
            )}
            {/* Header */}
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    marginBottom: '16px',
                    flexWrap: 'wrap',
                    gap: '16px',
                }}
            >
                <div>
                    <h1 style={{ color: COLORS.text, fontSize: '28px', fontWeight: '700', marginBottom: '4px' }}>
                        {{
                            summary: '🧭 Sprint Summary',
                            velocity: '📈 Velocity & Efficiency',
                            quality: '🩺 Quality',
                            workmix: '📊 Work Mix',
                            delivery: '🚀 Delivery',
                            agents: '🤖 AI Agents',
                            people: '👥 People',
                            scrub: '🧪 Scrub',
                        }[activeDashboard] || '📈 Dashboard'}
                        <span
                            style={{ color: COLORS.textMuted, fontSize: '16px', fontWeight: '500', marginLeft: '12px' }}
                        >
                            ·{' '}
                            {workspace === 'data'
                                ? 'Data'
                                : workspace === 'data-engineering'
                                  ? 'Data Engineering'
                                  : 'Engineering'}
                        </span>
                    </h1>
                    <p style={{ color: COLORS.textMuted, fontSize: '14px' }}>
                        {selectedSprintNum != null ? `Sprint ${selectedSprintNum}` : 'Latest Sprint'}
                        {selectedPod
                            ? ` · ${
                                  {
                                      BP: 'Buyer',
                                      SP: 'Seller',
                                      FP: 'Foundations',
                                      DAT: 'Analytics',
                                      DET: 'Data Engineering',
                                  }[selectedPod] || selectedPod
                              }`
                            : ''}
                        {activeDashboard === 'velocity'
                            ? workspace === 'data'
                                ? ' • Data pods velocity'
                                : ' • Target: 1600 pts by EOY 2026'
                            : activeDashboard === 'summary'
                              ? ' • Standardized scorecard + AI interpretation'
                              : activeDashboard === 'quality'
                                ? ' • Defects, escapes, CFR & MTTR'
                                : ''}
                    </p>
                    {/* Workspace + Dashboard toggles */}
                    <div style={{ display: 'flex', gap: '10px', marginTop: '10px', flexWrap: 'wrap' }}>
                        <div
                            style={{
                                display: 'inline-flex',
                                background: COLORS.background,
                                borderRadius: '10px',
                                border: `1px solid ${COLORS.border}`,
                                overflow: 'hidden',
                            }}
                        >
                            {[
                                { key: 'engineering', label: 'Engineering', icon: '⚙️' },
                                { key: 'data', label: 'Data', icon: '📊' },
                                { key: 'data-engineering', label: 'Data Engineering', icon: '🛠️' },
                            ].map((ws) => (
                                <button
                                    key={ws.key}
                                    onClick={() => setWorkspace(ws.key)}
                                    style={{
                                        background: workspace === ws.key ? `${COLORS.secondary}30` : 'transparent',
                                        border: 'none',
                                        borderBottom:
                                            workspace === ws.key
                                                ? `2px solid ${COLORS.secondary}`
                                                : '2px solid transparent',
                                        color: workspace === ws.key ? COLORS.secondary : COLORS.textMuted,
                                        padding: '8px 16px',
                                        fontSize: '13px',
                                        fontWeight: workspace === ws.key ? '700' : '400',
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '6px',
                                    }}
                                >
                                    {ws.icon} {ws.label}
                                </button>
                            ))}
                        </div>
                        <div
                            style={{
                                display: 'inline-flex',
                                background: COLORS.background,
                                borderRadius: '10px',
                                border: `1px solid ${COLORS.border}`,
                                overflow: 'hidden',
                            }}
                        >
                            {[
                                { key: 'summary', label: 'Summary', icon: '🧭' },
                                { key: 'velocity', label: 'Velocity & Efficiency', icon: '📈' },
                                ...(isDataWorkspace ? [] : [{ key: 'quality', label: 'Quality', icon: '🩺' }]),
                                { key: 'workmix', label: 'Work Mix', icon: '📊' },
                                ...(isDataWorkspace ? [] : [{ key: 'delivery', label: 'Delivery', icon: '🚀' }]),
                                ...(isDataWorkspace ? [] : [{ key: 'agents', label: 'AI Agents', icon: '🤖' }]),
                                { key: 'people', label: 'People', icon: '👥' },
                                { key: 'team', label: 'Team', icon: '🧩' },
                                { key: 'frameworks', label: 'Frameworks', icon: '⚖️' },
                            ].map((tab) => (
                                <button
                                    key={tab.key}
                                    onClick={() => setActiveDashboard(tab.key)}
                                    style={{
                                        background: activeDashboard === tab.key ? `${COLORS.primary}25` : 'transparent',
                                        border: 'none',
                                        borderBottom:
                                            activeDashboard === tab.key
                                                ? `2px solid ${COLORS.primary}`
                                                : '2px solid transparent',
                                        color: activeDashboard === tab.key ? COLORS.primary : COLORS.textMuted,
                                        padding: '8px 20px',
                                        fontSize: '13px',
                                        fontWeight: activeDashboard === tab.key ? '700' : '400',
                                        cursor: 'pointer',
                                        transition: 'all 0.15s ease',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '6px',
                                    }}
                                >
                                    {tab.icon} {tab.label}
                                </button>
                            ))}
                            {/* Tools menu — internal/data-quality tooling kept out of the main flow */}
                            {!isDataWorkspace && (
                                <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                                    <button
                                        onClick={() => setToolsOpen((v) => !v)}
                                        style={{
                                            background: activeDashboard === 'scrub' ? `${COLORS.primary}25` : 'transparent',
                                            border: 'none',
                                            borderBottom:
                                                activeDashboard === 'scrub'
                                                    ? `2px solid ${COLORS.primary}`
                                                    : '2px solid transparent',
                                            color: activeDashboard === 'scrub' ? COLORS.primary : COLORS.textMuted,
                                            padding: '8px 16px',
                                            fontSize: '13px',
                                            cursor: 'pointer',
                                        }}
                                    >
                                        ⚙ Tools ▾
                                    </button>
                                    {toolsOpen && (
                                        <div
                                            style={{
                                                position: 'absolute',
                                                top: '100%',
                                                right: 0,
                                                background: COLORS.cardBackground,
                                                border: `1px solid ${COLORS.border}`,
                                                borderRadius: '8px',
                                                padding: '6px',
                                                zIndex: 20,
                                                minWidth: '160px',
                                            }}
                                        >
                                            <button
                                                onClick={() => {
                                                    setActiveDashboard('scrub')
                                                    setToolsOpen(false)
                                                }}
                                                style={{
                                                    display: 'block',
                                                    width: '100%',
                                                    textAlign: 'left',
                                                    background: 'transparent',
                                                    border: 'none',
                                                    color: COLORS.text,
                                                    padding: '8px 12px',
                                                    fontSize: '13px',
                                                    cursor: 'pointer',
                                                    borderRadius: '6px',
                                                }}
                                            >
                                                🧪 Scrub (ticket classifier)
                                            </button>
                                            <div style={{ color: COLORS.textMuted, fontSize: '11px', padding: '6px 12px' }}>
                                                Epic approval lives in Work Mix → Categories.
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    {authUser && (
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                padding: '6px 12px',
                                background: COLORS.background,
                                borderRadius: '8px',
                                border: `1px solid ${COLORS.border}`,
                            }}
                        >
                            {authUser.picture && (
                                <img
                                    src={authUser.picture}
                                    alt=""
                                    style={{ width: 24, height: 24, borderRadius: '50%' }}
                                    referrerPolicy="no-referrer"
                                />
                            )}
                            <span style={{ color: COLORS.textMuted, fontSize: '13px' }}>
                                {authUser.name || authUser.email}
                            </span>
                            <button
                                onClick={handleLogout}
                                style={{
                                    background: 'none',
                                    border: 'none',
                                    color: COLORS.textMuted,
                                    cursor: 'pointer',
                                    padding: '2px 6px',
                                    fontSize: '12px',
                                }}
                            >
                                Sign out
                            </button>
                        </div>
                    )}
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '2px' }}>
                        {lastSync && (
                            <span style={{ color: COLORS.textMuted, fontSize: '13px' }}>
                                Last sync: {new Date(lastSync).toLocaleString()}
                            </span>
                        )}
                        {lastFullSync && lastFullSync !== lastSync && (
                            <span style={{ color: COLORS.textMuted, fontSize: '11px', opacity: 0.7 }}>
                                Full refresh: {new Date(lastFullSync).toLocaleString()}
                            </span>
                        )}
                    </div>
                    <button
                        onClick={() => setShowExcludePanel((prev) => !prev)}
                        style={{
                            background: excludedUserIds.length > 0 ? `${COLORS.warning}20` : 'transparent',
                            border: `1px solid ${excludedUserIds.length > 0 ? COLORS.warning : COLORS.border}`,
                            color: excludedUserIds.length > 0 ? COLORS.warning : COLORS.textMuted,
                            padding: '10px 16px',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            fontWeight: '500',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            fontSize: '13px',
                        }}
                    >
                        👥 {excludedUserIds.length > 0 ? `${excludedUserIds.length} Excluded` : 'Manage Users'}
                    </button>
                    <button
                        onClick={exportDashboardHTML}
                        disabled={isExporting}
                        style={{
                            background: 'transparent',
                            border: `1px solid ${COLORS.border}`,
                            color: COLORS.textMuted,
                            padding: '10px 16px',
                            borderRadius: '8px',
                            cursor: isExporting ? 'not-allowed' : 'pointer',
                            fontWeight: '500',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            fontSize: '13px',
                            opacity: isExporting ? 0.5 : 1,
                        }}
                    >
                        {isExporting ? '⏳ Exporting...' : '📥 Export HTML'}
                    </button>
                    <button
                        onClick={() =>
                            ['quality', 'summary', 'agents'].includes(activeDashboard)
                                ? setPulseRefreshSignal((value) => value + 1)
                                : fetchData(false)
                        }
                        disabled={!['quality', 'summary', 'agents'].includes(activeDashboard) && syncing}
                        style={{
                            background: !['quality', 'summary', 'agents'].includes(activeDashboard) && syncing ? COLORS.border : COLORS.primary,
                            color: 'white',
                            border: 'none',
                            padding: '10px 20px',
                            borderRadius: '8px',
                            cursor: !['quality', 'summary', 'agents'].includes(activeDashboard) && syncing ? 'not-allowed' : 'pointer',
                            fontWeight: '600',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                        }}
                    >
                        {!['quality', 'summary', 'agents'].includes(activeDashboard) && syncing ? '⏳ Syncing...' : '🔄 Refresh'}
                    </button>
                    <button
                        onClick={() => fetchData('all')}
                        disabled={syncing}
                        style={{
                            background: 'transparent',
                            color: syncing ? COLORS.textMuted : COLORS.text,
                            border: `1px solid ${COLORS.border}`,
                            padding: '10px 16px',
                            borderRadius: '8px',
                            cursor: syncing ? 'not-allowed' : 'pointer',
                            fontWeight: '500',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            fontSize: '13px',
                        }}
                    >
                        🔃 Refresh All Sprints
                    </button>
                </div>
            </div>

            {/* Filters — sticky nav with Pod and Sprint selectors */}
            <div
                style={{
                    position: 'sticky',
                    top: 0,
                    zIndex: 50,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    marginBottom: '24px',
                    flexWrap: 'wrap',
                    background: `linear-gradient(135deg, ${COLORS.background} 0%, ${COLORS.cardBg} 100%)`,
                    paddingTop: '12px',
                    paddingBottom: '12px',
                    marginTop: '-12px',
                    borderBottom: `1px solid ${COLORS.border}`,
                }}
            >
                {/* Pod filter — only meaningful for multi-project workspaces (engineering) */}
                {!isSinglePodWorkspace && (
                    <>
                        <span
                            style={{
                                color: COLORS.textMuted,
                                fontSize: '12px',
                                fontWeight: '600',
                                textTransform: 'uppercase',
                                letterSpacing: '0.05em',
                                marginRight: '4px',
                            }}
                        >
                            Pod
                        </span>
                        {[
                            { key: null, label: '🌐 All Pods', color: COLORS.primary },
                            { key: 'BP', label: '🛒 Buyer', color: CHART_COLORS[0] },
                            { key: 'SP', label: '🏪 Seller', color: CHART_COLORS[1] },
                            { key: 'FP', label: '🏗️ Foundations', color: CHART_COLORS[2] },
                        ].map(({ key, label, color }) => {
                            const isActive = selectedPod === key
                            return (
                                <button
                                    key={key ?? 'all'}
                                    onClick={() => setSelectedPod(key)}
                                    style={{
                                        background: isActive ? color : 'transparent',
                                        border: `1px solid ${isActive ? color : COLORS.border}`,
                                        borderRadius: '999px',
                                        padding: '6px 18px',
                                        fontSize: '13px',
                                        fontWeight: isActive ? '700' : '400',
                                        color: isActive ? '#fff' : COLORS.textMuted,
                                        cursor: 'pointer',
                                        transition: 'all 0.15s ease',
                                        boxShadow: isActive ? `0 0 12px ${color}55` : 'none',
                                    }}
                                >
                                    {label}
                                </button>
                            )
                        })}

                        {/* Divider */}
                        <div style={{ width: '1px', height: '24px', background: COLORS.border, margin: '0 8px' }} />
                    </>
                )}

                {/* Sprint filter — dropdown */}
                <span
                    style={{
                        color: COLORS.textMuted,
                        fontSize: '12px',
                        fontWeight: '600',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        marginRight: '4px',
                    }}
                >
                    Sprint
                </span>
                <select
                    value={selectedSprintNum ?? ''}
                    onChange={(e) => setSelectedSprintNum(e.target.value === '' ? null : Number(e.target.value))}
                    style={{
                        background: COLORS.background,
                        border: `1px solid ${selectedSprintNum != null ? COLORS.secondary : COLORS.border}`,
                        borderRadius: '999px',
                        padding: '6px 32px 6px 16px',
                        fontSize: '13px',
                        fontWeight: selectedSprintNum != null ? '600' : '400',
                        color: selectedSprintNum != null ? COLORS.secondary : COLORS.textMuted,
                        cursor: 'pointer',
                        outline: 'none',
                        appearance: 'none',
                        WebkitAppearance: 'none',
                        backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%2394a3b8' d='M3 5l3 3 3-3'/%3E%3C/svg%3E")`,
                        backgroundRepeat: 'no-repeat',
                        backgroundPosition: 'right 12px center',
                        transition: 'all 0.15s ease',
                    }}
                >
                    <option value="">All Sprints</option>
                    {[...(exclusionFilteredHistory || [])]
                        .sort((a, b) => b.sprintNum - a.sprintNum)
                        .map((sprint) => (
                            <option key={sprint.sprintNum} value={sprint.sprintNum}>
                                S{sprint.sprintNum} · {fmtDateRange(sprint.startDate, sprint.endDate)}
                            </option>
                        ))}
                </select>
            </div>

            {error && (
                <div
                    style={{
                        background: `${COLORS.danger}20`,
                        border: `1px solid ${COLORS.danger}`,
                        borderRadius: '8px',
                        padding: '12px 16px',
                        marginBottom: '24px',
                        color: COLORS.danger,
                    }}
                >
                    ⚠️ {error} (showing cached data)
                </div>
            )}

            <DataStalenessBanner lastSync={lastSync} onRefresh={() => fetchData('all')} syncing={syncing} />

            {activeDashboard === 'velocity' && (
                <>
                    {/* Active Sprint Progress — engineering only (server gates on enableActiveSprint) */}
                    {exclusionFilteredActiveSprint && (
                        <ActiveSprintCard
                            activeSprint={exclusionFilteredActiveSprint}
                            selectedPod={selectedPod}
                            podColor={
                                selectedPod === 'BP'
                                    ? CHART_COLORS[0]
                                    : selectedPod === 'SP'
                                      ? CHART_COLORS[1]
                                      : selectedPod === 'FP'
                                        ? CHART_COLORS[2]
                                        : undefined
                            }
                        />
                    )}

                    {/* Stats Grid */}
                    <div
                        style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
                            gap: '20px',
                            marginBottom: '24px',
                        }}
                    >
                        <StatCard
                            title="Sprint Velocity"
                            value={filteredDisplaySprint?.totalPoints || 0}
                            subtitle="story points"
                            trend={velocityChange}
                            icon="📈"
                            color={COLORS.primary}
                        />
                        <StatCard
                            title="AI-Assisted"
                            value={filteredDisplaySprint?.aiPoints || 0}
                            subtitle={`${filteredDisplaySprint?.aiAdoption || 0}% of total`}
                            icon="🤖"
                            color={COLORS.ai}
                        />
                        <StatCard
                            title="Traditional"
                            value={filteredDisplaySprint?.nonAiPoints || 0}
                            subtitle="without AI"
                            icon="👨‍💻"
                            color={COLORS.nonAi}
                        />
                        <StatCard
                            title="Goal Progress"
                            value={`${Math.round(velocityProgress)}%`}
                            subtitle="to 1600 target"
                            icon="🎯"
                            color={COLORS.success}
                        />
                    </div>

                    {/* Main Content Grid */}
                    <div
                        style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
                            gap: '24px',
                            marginBottom: '24px',
                        }}
                    >
                        {/* Velocity Chart */}
                        {filteredVelocityTrend && (
                            <VelocityChart data={filteredVelocityTrend} selectedSprintNum={selectedSprintNum} />
                        )}

                        {/* AI Adoption Gauge */}
                        <div
                            style={{
                                background: COLORS.cardBg,
                                borderRadius: '16px',
                                padding: '24px',
                                border: `1px solid ${COLORS.border}`,
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                            }}
                        >
                            <h3
                                style={{
                                    color: COLORS.text,
                                    marginBottom: '20px',
                                    fontSize: '18px',
                                    fontWeight: '600',
                                }}
                            >
                                AI Adoption Rate
                            </h3>
                            <div
                                style={{
                                    position: 'relative',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                }}
                            >
                                <ProgressRing
                                    progress={filteredDisplaySprint?.aiAdoption || 0}
                                    size={160}
                                    strokeWidth={14}
                                />
                                <div
                                    style={{
                                        position: 'absolute',
                                        textAlign: 'center',
                                    }}
                                >
                                    <div style={{ fontSize: '32px', fontWeight: '700', color: COLORS.text }}>
                                        {filteredDisplaySprint?.aiAdoption || 0}%
                                    </div>
                                    <div style={{ color: COLORS.textMuted, fontSize: '14px' }}>AI Usage</div>
                                </div>
                            </div>
                            <div style={{ marginTop: '24px', textAlign: 'center' }}>
                                <div style={{ color: COLORS.text, fontSize: '24px', fontWeight: '600' }}>
                                    {filteredDisplaySprint?.aiPoints || 0} pts
                                </div>
                                <div style={{ color: COLORS.textMuted, fontSize: '14px' }}>AI-assisted this sprint</div>
                            </div>
                        </div>
                    </div>

                    {/* What moved velocity — sprint-over-sprint pod & factor attribution */}
                    {exclusionFilteredHistory && <SprintDriversCard history={exclusionFilteredHistory} />}

                    {/* Leaderboards and Project Breakdown */}
                    <div
                        style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                            gap: '24px',
                            marginBottom: '24px',
                        }}
                    >
                        {/* Leaderboard */}
                        <div
                            style={{
                                background: COLORS.cardBg,
                                borderRadius: '16px',
                                padding: '24px',
                                border: `1px solid ${COLORS.border}`,
                            }}
                        >
                            <h3
                                style={{
                                    color: COLORS.text,
                                    marginBottom: '20px',
                                    fontSize: '18px',
                                    fontWeight: '600',
                                }}
                            >
                                🏆 Leaderboard
                            </h3>
                            <div
                                style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: '12px',
                                    maxHeight: '480px',
                                    overflowY: 'auto',
                                    paddingRight: '4px',
                                }}
                            >
                                {(filteredDisplaySprint?.contributors || []).map((contributor, index) => (
                                    <ContributorCard
                                        key={contributor.name}
                                        contributor={contributor}
                                        rank={index + 1}
                                    />
                                ))}
                            </div>
                        </div>

                        {/* Badge Champions */}
                        <BadgeLeaderboard contributors={filteredDisplaySprint?.contributors} />

                        {/* Project Breakdown */}
                        {displaySprint?.projectBreakdown && (
                            <ProjectBreakdown data={displaySprint.projectBreakdown} selectedPod={selectedPod} />
                        )}
                    </div>

                    {/* All-Time Leaderboard and Badge Champions */}
                    {allTimeContributors.length > 0 && (
                        <div
                            style={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                                gap: '24px',
                                marginBottom: '24px',
                            }}
                        >
                            {/* All-Time Leaderboard */}
                            <div
                                style={{
                                    background: COLORS.cardBg,
                                    borderRadius: '16px',
                                    padding: '24px',
                                    border: `1px solid ${COLORS.border}`,
                                }}
                            >
                                <div
                                    style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'flex-start',
                                        marginBottom: '4px',
                                    }}
                                >
                                    <h3 style={{ color: COLORS.text, margin: 0, fontSize: '18px', fontWeight: '600' }}>
                                        🏅 All-Time Leaderboard
                                    </h3>
                                    <button
                                        onClick={() => {
                                            const headers = [
                                                'Rank',
                                                'Name',
                                                'Total Points',
                                                'Sprints',
                                                'Avg Points/Sprint',
                                                'Peak Velocity',
                                                'Total Stories',
                                                'AI Adoption %',
                                                'Avg Availability %',
                                            ]
                                            const rows = allTimeContributors.map((c, i) =>
                                                [
                                                    i + 1,
                                                    `"${c.name}"`,
                                                    c.totalPoints,
                                                    c.sprintCount,
                                                    c.avgVelocity,
                                                    c.peakVelocity,
                                                    c.totalIssues,
                                                    c.overallAiUsage,
                                                    c.avgAvailability != null
                                                        ? Math.round(c.avgAvailability * 100)
                                                        : '',
                                                ].join(',')
                                            )
                                            const csv = [headers.join(','), ...rows].join('\n')
                                            const blob = new Blob([csv], { type: 'text/csv' })
                                            const url = URL.createObjectURL(blob)
                                            const a = document.createElement('a')
                                            a.href = url
                                            a.download = 'all-time-leaderboard.csv'
                                            a.click()
                                            URL.revokeObjectURL(url)
                                        }}
                                        style={{
                                            background: 'transparent',
                                            border: `1px solid ${COLORS.border}`,
                                            borderRadius: '8px',
                                            color: COLORS.textMuted,
                                            fontSize: '12px',
                                            padding: '4px 10px',
                                            cursor: 'pointer',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '4px',
                                            flexShrink: 0,
                                        }}
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.borderColor = COLORS.primary
                                            e.currentTarget.style.color = COLORS.text
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.borderColor = COLORS.border
                                            e.currentTarget.style.color = COLORS.textMuted
                                        }}
                                    >
                                        Export CSV
                                    </button>
                                </div>
                                <p style={{ color: COLORS.textMuted, fontSize: '12px', marginBottom: '16px' }}>
                                    Cumulative points across all {exclusionFilteredHistory?.length || 0} sprints
                                </p>
                                <div
                                    style={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: '12px',
                                        maxHeight: '480px',
                                        overflowY: 'auto',
                                        paddingRight: '4px',
                                    }}
                                >
                                    {allTimeContributors.map((contributor, index) => (
                                        <AllTimeContributorCard
                                            key={contributor.id || contributor.name}
                                            contributor={contributor}
                                            rank={index + 1}
                                        />
                                    ))}
                                </div>
                            </div>

                            {/* All-Time Badge Champions */}
                            <BadgeLeaderboard
                                contributors={allTimeContributors}
                                title="🏅 All-Time Badge Champions"
                                subtitle="Total badges earned across all tracked sprints"
                                emptyText="No badges earned yet across any sprint."
                                useAllTimeBadges
                            />
                        </div>
                    )}

                    {/* Sprint History */}
                    {exclusionFilteredHistory && <SprintHistoryTable data={exclusionFilteredHistory} />}

                    {/* Sprint Diagnostics */}
                    {filteredVelocityTrend && (
                        <div style={{ marginBottom: '24px', marginTop: '24px' }}>
                            <SprintDiagnosticsChart data={filteredVelocityTrend} />
                        </div>
                    )}

                    {/* Developer Progress */}
                    {exclusionFilteredHistory && (
                        <DeveloperProgressChart
                            sprintHistory={exclusionFilteredHistory}
                            selectedDevelopers={selectedDevelopers}
                            onToggleDeveloper={(name) => {
                                if (name === null) {
                                    setSelectedDevelopers([])
                                } else {
                                    setSelectedDevelopers((prev) =>
                                        prev.includes(name) ? prev.filter((n) => n !== name) : [...prev, name]
                                    )
                                }
                            }}
                            selectedPod={selectedPod}
                        />
                    )}

                    {/* Summary Footer */}
                    {exclusionFilteredSummary && (
                        <div
                            style={{
                                marginTop: '24px',
                                padding: '16px 24px',
                                background: COLORS.cardBg,
                                borderRadius: '12px',
                                border: `1px solid ${COLORS.border}`,
                                display: 'flex',
                                justifyContent: 'space-around',
                                flexWrap: 'wrap',
                                gap: '16px',
                            }}
                        >
                            <div style={{ textAlign: 'center' }}>
                                <div style={{ color: COLORS.text, fontSize: '24px', fontWeight: '700' }}>
                                    {exclusionFilteredSummary.totalSprints}
                                </div>
                                <div style={{ color: COLORS.textMuted, fontSize: '13px' }}>Sprints Tracked</div>
                            </div>
                            <div style={{ textAlign: 'center' }}>
                                <div style={{ color: COLORS.text, fontSize: '24px', fontWeight: '700' }}>
                                    {exclusionFilteredSummary.totalIssues}
                                </div>
                                <div style={{ color: COLORS.textMuted, fontSize: '13px' }}>Stories Completed</div>
                            </div>
                            <div style={{ textAlign: 'center' }}>
                                <div style={{ color: COLORS.text, fontSize: '24px', fontWeight: '700' }}>
                                    {exclusionFilteredSummary.totalPoints}
                                </div>
                                <div style={{ color: COLORS.textMuted, fontSize: '13px' }}>Total Points</div>
                            </div>
                            <div style={{ textAlign: 'center' }}>
                                <div style={{ color: COLORS.text, fontSize: '24px', fontWeight: '700' }}>
                                    {exclusionFilteredSummary.uniqueContributors}
                                </div>
                                <div style={{ color: COLORS.textMuted, fontSize: '13px' }}>Contributors</div>
                            </div>
                        </div>
                    )}

                    {/* AI efficiency (#1) — AI vs non-AI speed & quality */}
                    {!isDataWorkspace && <AiEfficiency refreshSignal={pulseRefreshSignal} />}
                </>
            )}

            {activeDashboard === 'summary' && (
                <SprintSummary sprintNum={displaySprint?.sprintNum} refreshSignal={pulseRefreshSignal} />
            )}

            {activeDashboard === 'quality' && (
                <QualityHub selectedPod={selectedPod} refreshSignal={pulseRefreshSignal} />
            )}

            {activeDashboard === 'agents' && <AgentInsights refreshSignal={pulseRefreshSignal} />}

            {activeDashboard === 'delivery' && (
                <>
                    {/* === DELIVERY DASHBOARD === */}
                    {/* Release Tracking — ground-truth from GitLab prod_deploy events.
                        Legacy cycle-time and release-charts panels were removed in favour of
                        the MR-based By Sprint view that lives inside ReleaseTracking. */}
                    <ReleaseTracking excludedUserIds={excludedUserIds} />
                </>
            )}

            {activeDashboard === 'scrub' && <TicketScrubbing />}

            {activeDashboard === 'people' && (
                <PeopleTab
                    data={data}
                    roles={roles}
                    onRoleChange={handleRoleChange}
                    roleSaving={roleSaving}
                    displaySprint={displaySprint}
                    selectedPod={selectedPod}
                />
            )}

            {activeDashboard === 'team' && <TeamAdmin authUser={authUser} />}

            {activeDashboard === 'frameworks' && <Frameworks />}

            {activeDashboard === 'workmix' && (
                <>
                    {/* Work Mix — Categories ⇄ Pillars, two lenses on the same contributor data */}
                    <div
                        style={{
                            display: 'inline-flex',
                            background: COLORS.background,
                            borderRadius: '10px',
                            border: `1px solid ${COLORS.border}`,
                            overflow: 'hidden',
                            marginBottom: '20px',
                        }}
                    >
                        {[
                            { key: 'categories', label: '📊 By Category', },
                            { key: 'pillars', label: '🏛️ By Pillar' },
                        ].map((v) => (
                            <button
                                key={v.key}
                                onClick={() => setWorkMixView(v.key)}
                                style={{
                                    background: workMixView === v.key ? `${COLORS.primary}25` : 'transparent',
                                    border: 'none',
                                    borderBottom:
                                        workMixView === v.key ? `2px solid ${COLORS.primary}` : '2px solid transparent',
                                    color: workMixView === v.key ? COLORS.primary : COLORS.textMuted,
                                    padding: '8px 18px',
                                    fontSize: '13px',
                                    fontWeight: workMixView === v.key ? '700' : '400',
                                    cursor: 'pointer',
                                }}
                            >
                                {v.label}
                            </button>
                        ))}
                    </div>
                    {workMixView === 'categories' ? (
                        <CategoriesTab roles={roles} displaySprint={displaySprint} selectedPod={selectedPod} />
                    ) : (
                        <PillarsTab roles={roles} displaySprint={displaySprint} selectedPod={selectedPod} />
                    )}
                </>
            )}

            {/* Exclude Users Panel */}
            {showExcludePanel && (
                <>
                    <div
                        onClick={() => setShowExcludePanel(false)}
                        style={{
                            position: 'fixed',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            background: 'rgba(0,0,0,0.4)',
                            zIndex: 999,
                        }}
                    />
                    <ExcludeUsersPanel
                        allUsers={allKnownUsers}
                        excludedUserIds={excludedUserIds}
                        onToggle={toggleExcludeUser}
                        onClose={() => setShowExcludePanel(false)}
                        canEdit={canEditExclusions}
                        error={exclusionsError}
                    />
                </>
            )}
        </div>
    )
}
